import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, doc, setDoc, getDoc, deleteDoc, serverTimestamp, updateDoc, collection, query, where, orderBy, getDocs, deleteField } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, signInWithEmailAndPassword, signOut as firebaseSignOut, onAuthStateChanged, setPersistence, browserSessionPersistence } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject, connectStorageEmulator } from 'firebase/storage';
import './style.css';
import { CONFIG, shouldUseEmulator, getEmulatorConfig, connectToEmulators, APP, AUTH, LANGUAGES, LANGUAGE_NAMES } from './config.js';
import { toast } from './toast.js';
import { GmImagesController } from './controllers/gmImagesController.js';
import { gmImagesState, LANGUAGES as LANGUAGES_GM } from './state/gmImagesState.js';
import { OverlayManager } from './utils/overlayManager.js';
import { SessionManager } from './utils/sessionManager.js';
import { ModalManager } from './utils/modalManager.js';
import { ViewManager } from './utils/viewManager.js';
import { registerGuard, requestLeave } from './ui/navGuard.js';
import { mask, unmask, listSkeleton } from './ui/skeleton.js';
import {
  TASKS, getVisibleTasks,
  loadTaskPreferences, saveTaskPreferences, applyTaskVisibility,
} from './features/taskPreferences.js';
import { resultCardHtml, fixtureCardHtml } from './features/iplCardTemplates.js';
import { initShell, syncShell, setShellUser, setStepAvailability, setNavigationServices } from './ui/shell.js';
import { CloudFunctionsClient } from './utils/cloudFunctions.js';
import {
  getISTDateString,
  formatDateForDisplay,
  updateDateBadges,
  getEarliestSelectableDate,
  clampToSelectableDate,
  shiftDateString,
  applyDateInputLimits,
  MAX_PAST_REFERENCE_DAYS
} from './utils/dateHelpers.js';

// Initialize config values in HTML
document.addEventListener('DOMContentLoaded', () => {
  document.getElementById('maxArticles').textContent = APP.MAX_ARTICLES;
  document.getElementById('minArticles').textContent = APP.MIN_ARTICLES;
  
  // Show login view initially, will be updated after Firebase init
  showView('loginView');

  // Deliberately here and not at module scope: the clock's Intl formatters are
  // declared further down the file, so calling this during evaluation would hit
  // their temporal dead zone and abort the rest of the module.
  startClock();
});

// Quieten routine logging in production, but keep the channels that report
// something going wrong. Suppressing console.error meant a translation
// outage produced no signal anywhere — not in the UI, not in the console.
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  // console.warn and console.error deliberately left intact.
}

// Firebase configuration - For Firebase Auth, we need complete config
// Get this from Firebase Console: Project Settings > General > Your apps > Web app > Config
const firebaseConfig = {
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'ghoshna-in',
  // These are required for Firebase Auth - get from Firebase Console
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || null,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || null,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || null,
  // Optional but recommended
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || null,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || null,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || null
};

// Cloud Functions Manual Trigger Configuration
const CLOUD_FUNCTIONS_URL = import.meta.env.VITE_CLOUD_FUNCTIONS_URL || `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net`;
const ADMIN_API_KEY = import.meta.env.VITE_ADMIN_API_KEY || null;

// Initialize utility managers
const overlayManager = new OverlayManager();
const cloudFunctionsClient = new CloudFunctionsClient(CLOUD_FUNCTIONS_URL, ADMIN_API_KEY);

// Legacy function wrappers for backward compatibility
function showFetchOverlay(message, opts) {
  overlayManager.show(message, opts);
}

function hideFetchOverlay() {
  overlayManager.hide();
}

function updateFetchOverlayMessage(message, subtext) {
  overlayManager.updateMessage(message, subtext);
}

/* --- Loading that does not blink ---------------------------------------------
   Most reads here finish faster than a spinner can be read, so showing one the
   instant the work starts produces a flash, not progress — most visibly when
   stepping the publication date a day at a time.

   So loading UI waits: nothing appears unless the work is still running after
   the grace period, and anything that did appear stays up long enough to be
   legible instead of blinking away. Both numbers are shared, so every screen
   settles at the same rhythm. */
const LOADING_GRACE_MS = 250;
const LOADING_MIN_MS = 450;

/**
 * Defer a loading state behind the grace period.
 *
 * Returns a handle whose `done(after)` tears the state down: at once if it
 * never appeared, otherwise when it has had its minimum time on screen. The
 * optional `after` always runs, at that same moment — it is for work that must
 * happen whether or not anything was ever shown (clearing masks the page was
 * already wearing, say), so a fast answer cannot strand the page mid-load.
 */
function deferredLoading(show, hide) {
  let shownAt = 0;
  let settled = false;

  const timer = setTimeout(() => {
    if (settled) return;
    shownAt = Date.now();
    show();
  }, LOADING_GRACE_MS);

  return {
    done(after) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);

      const finish = () => { if (shownAt) hide(); if (after) after(); };
      const remaining = shownAt ? LOADING_MIN_MS - (Date.now() - shownAt) : 0;
      if (remaining <= 0) finish();
      else setTimeout(finish, remaining);
    }
  };
}

function callCloudFunction(functionName, queryParams = {}, signal = null) {
  return cloudFunctionsClient.call(functionName, queryParams, signal);
}

let _fetchAbortController = { get signal() { return overlayManager.getAbortSignal(); } };

// Initialize Firebase
let db, auth, functions, storage;
let gmImagesController;
try {
  console.log('Initializing Firebase with config:', {
    projectId: firebaseConfig.projectId,
    hasApiKey: !!firebaseConfig.apiKey,
    hasAuthDomain: !!firebaseConfig.authDomain,
    hasAppId: !!firebaseConfig.appId
  });
  
  // Check if we have required config for Firebase Auth
  if (!firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.appId) {
    throw new Error('Firebase Auth requires complete configuration. Please get config from Firebase Console: Project Settings > General > Your apps > Web app > Config');
  }
  
  const app = initializeApp(firebaseConfig);
  db = getFirestore(app);
  
  // Initialize Auth - required for this app
  auth = getAuth(app);
  
  // Initialize Storage
  storage = getStorage(app);
  
  // IMPORTANT: Force storage to use emulator URL
  if (shouldUseEmulator()) {
    connectStorageEmulator(storage, CONFIG.FIREBASE_EMULATOR.HOST, CONFIG.FIREBASE_EMULATOR.STORAGE_PORT);
    console.log(`Storage emulator connected on http://${CONFIG.FIREBASE_EMULATOR.HOST}:${CONFIG.FIREBASE_EMULATOR.STORAGE_PORT}`);
  }
  
  // Set persistence to SESSION to prevent auto-login after browser close
  setPersistence(auth, browserSessionPersistence)
    .then(() => {
      console.log('Firebase Auth persistence set to SESSION');
      
      // Set up authentication state listener
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          console.log('User is signed in:', user.email);
          appState.isAuthenticated = true;
          setShellUser(user.email);
          initializeSession();
          // A restored session is the other way into the console, so the
          // board's task preferences have to load here too — otherwise a
          // returning operator gets every card back.
          await loadTaskPreferences(db, user.uid);
          applyTaskVisibility();
          initDateSelection();
          showView('dateSelectionView');
          toast.success(`Welcome back, ${user.email}!`);
        } else {
          console.log('No user signed in');
          appState.isAuthenticated = false;
          cleanupSession();
          showView('loginView');
        }
      });
    })
    .catch((error) => {
      console.error('Error setting auth persistence:', error);
      toast.error('Authentication setup failed');
    });
  
  functions = getFunctions(app);
  console.log('Firebase Auth and Functions initialized successfully');
  
  console.log('Firebase initialized successfully');
  
  // Initialize GM Images Controller
  gmImagesController = new GmImagesController(db, storage, overlayManager);
  gmImagesController.setNavigateHomeHandler(() => {
    showView('dateSelectionView');
  });

  // LANGUAGE_NAMES lists translation targets, where English is the source and
  // so absent. The image slots are all four languages.
  const GM_LANGUAGE_NAMES = { en: 'English', ...LANGUAGE_NAMES };

  gmImagesController.setSubmittedHandler((dateString, slots) => {
    const stored = slots.filter(s => s.url);
    showSubmissionSummary({
      task: 'gm',
      date: dateString,
      summary: `${stored.length} of 4 languages stored.`,
      rows: slots.map(({ lang, url }) => ({
        primary: GM_LANGUAGE_NAMES[lang] || lang.toUpperCase(),
        secondary: url ? 'Uploaded' : 'Missing',
        tone: url ? 'positive' : 'negative',
      })),
    });
  });

  // Global Clear All hook (reliable even if controller bind missed)
  window.clearAllGmImages = async () => {
    if (gmImagesController) {
      await gmImagesController.clearAllImages();
    } else {
      toast.error('GM images not ready yet');
    }
  };

  // Connect to emulators if enabled in config (moved outside try-catch)
  if (shouldUseEmulator() && db && auth && functions) {
    console.log('Connecting to Firebase emulators...');
    console.log('Emulator config:', {
      host: CONFIG.FIREBASE_EMULATOR.HOST,
      firestore: CONFIG.FIREBASE_EMULATOR.FIRESTORE_PORT,
      auth: CONFIG.FIREBASE_EMULATOR.AUTH_PORT,
      functions: CONFIG.FIREBASE_EMULATOR.FUNCTIONS_PORT
    });
    
    const connected = connectToEmulators(db, auth, functions);
    if (!connected) {
      toast.error('Failed to connect to Firebase emulators. Please check if they are running.');
    } else {
      console.log('Successfully connected to Firebase emulators');
      console.log('Storage emulator connected separately during initialization');
    }
  }

} catch (error) {
  console.error("Firebase initialization error:", error);
  console.log("Firebase not configured yet - app will not work without proper setup");
  db = null;
  auth = null;
  functions = null;
  storage = null;
  showView('loginView');
  
  // Show detailed error message to user
  setTimeout(() => {
    toast.error('Firebase not properly configured. Please check console for details and get config from Firebase Console.');
  }, 2000);
}

// Initialize Session Manager
const sessionManager = new SessionManager((reason) => {
  forceSignOut(reason);
});

// App State
let appState = {
  isAuthenticated: false,
  selectedDate: '',
  hasExistingData: false,
  newsArticles: [],
  currentArticle: {
    title: '',
    description: '',
    source: '',
    link: ''
  },
  translations: {},
  // Set once a submit succeeds; closes the pipeline for that date so a
  // finished day is not re-entered by accident.
  submittedForDate: null,
  // Which language translations fell back to English, per article index.
  // Never written to Firestore; drives the warning shown to the operator.
  translationFailures: {},
  selectedLanguages: [LANGUAGES.HINDI, LANGUAGES.MARATHI, LANGUAGES.GUJARATI],
  skippedTranslationStep: false,
  iplData: {
    yesterdayResults: [],
    todayFixtures: [],
    status: 'Fetched'
  }
};

// Session State
let sessionState = {
  startTime: null,
  lastActivity: null,
  warningTimer: null,
  timeoutTimer: null,
  warningShown: false,
  isWarningModalOpen: false,
  countdownInterval: null,
  countdownTime: 0
};

const IPL_TEAMS = [
  { code: 'CSK', name: 'Chennai Super Kings' },
  { code: 'DC', name: 'Delhi Capitals' },
  { code: 'GT', name: 'Gujarat Titans' },
  { code: 'KKR', name: 'Kolkata Knight Riders' },
  { code: 'LSG', name: 'Lucknow Super Giants' },
  { code: 'MI', name: 'Mumbai Indians' },
  { code: 'PBKS', name: 'Punjab Kings' },
  { code: 'RR', name: 'Rajasthan Royals' },
  { code: 'RCB', name: 'Royal Challengers Bengaluru' },
  { code: 'SRH', name: 'Sunrisers Hyderabad' }
];

// Legacy function wrappers for backward compatibility
function showConfirmModal(options) {
  return ModalManager.showConfirm(options);
}

// Views that can change what is stored for a date. Returning from one of
// these is exactly when the workspace's numbers may be out of date.
const TASK_VIEWS = [
  'dataEntryView', 'translationOptionsView', 'articleTranslateView',
  'reviewEditView', 'giftNiftyView', 'iplView', 'gmImagesView',
  // The confirmation page counts: arriving at the workspace from it must
  // re-read the date, or the cards would still show the pre-submission state.
  'submittedView',
];

function showView(viewId) {
  // Scoped to returning from a task: on login initDateSelection() has already
  // fetched, and refreshing again there would just double the request.
  const returningFromTask =
    viewId === 'dateSelectionView' && TASK_VIEWS.includes(ViewManager.getCurrentView());
  // Whatever was loading belonged to the page being left. Carrying its blocking
  // spinner onto the new page leaves the operator staring at a modal that no
  // longer has anything behind it.
  hideFetchOverlay();

  ViewManager.show(viewId);
  syncShell(viewId);
  updateTopbarStat(viewId);

  // The article list is rebuilt on the way in rather than by each of the five
  // callers that can land here — only some of them did, so arriving on a date
  // with nothing stored left the container with the empty markup from
  // index.html and no placeholder slots. After syncShell, because the step
  // gating it triggers is computed against the active view. All of this is
  // synchronous, so the browser still paints the finished list in one go.
  if (viewId === 'dataEntryView') updateArticlesList();

  // The workspace reports what is stored for the date, so it has to re-read it
  // on the way back — an upload or a save that just happened elsewhere would
  // otherwise leave it showing pre-edit values. Doing it here rather than in
  // each back handler means it cannot drift: only the IPL handler used to
  // refetch, so returning from Images, Gift Nifty or Articles showed stale
  // counts. Quiet refresh — no blocking overlay for something automatic.
  if (returningFromTask && appState.selectedDate && appState.isAuthenticated) {
    refreshWorkspaceData({ silent: true });
  }
}

/**
 * Re-read every task's stored state for the selected date.
 * @param {{silent?: boolean}} opts silent skips the blocking overlay and the toast.
 */
async function refreshWorkspaceData({ silent = false } = {}) {
  if (!appState.selectedDate) return;
  const btn = document.getElementById('workspaceRefreshBtn');
  if (btn) btn.setAttribute('data-loading', 'true');
  try {
    await checkExistingData(appState.selectedDate, { showOverlay: !silent });
    if (!silent) toast.success(`Reloaded ${formatDateForDisplay(appState.selectedDate)} from the database.`);
  } catch (err) {
    console.error('Workspace refresh failed:', err);
    // A skeleton that outlives its request reads as a hung page.
    clearRouteCardSkeletons();
    if (!silent) toast.error('Could not reload from the database. Please try again.');
  } finally {
    if (btn) btn.removeAttribute('data-loading');
  }
}

// Firebase Authentication Only
// Boot the navigation chrome. It performs no actions of its own —
// every control proxies to the handler that already owns the behaviour.
initShell();
registerNavGuards();
setNavigationServices({
  showView,
  canLeave: (viewId) => requestLeave(viewId, showConfirmModal),
});

document.getElementById('loginForm').addEventListener('submit', async (e) => {
  e.preventDefault();
  const email = document.getElementById('username').value;
  const password = document.getElementById('password').value;
  const errorDiv = document.getElementById('loginError');
  // Banner markup: the message goes into the title node. Writing textContent
  // on the banner itself would delete its icon and structure permanently.
  const errorText = errorDiv.querySelector('.banner__title') || errorDiv;
  const submitBtn = e.target.querySelector('button[type="submit"]');

  // Disable submit button and show loading
  submitBtn.disabled = true;
  submitBtn.innerHTML = '<div class="spinner inline-block mr-2"></div>Signing in...';

  try {
    if (!auth) {
      throw new Error('Firebase Auth not initialized. Please enable Authentication in Firebase Console.');
    }

    // Sign in with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    appState.isAuthenticated = true;
    setShellUser(email);
    // Load which tasks belong on this operator's board before it paints.
    await loadTaskPreferences(db, auth?.currentUser?.uid);
    applyTaskVisibility();
    toast.success('Welcome! Login successful');
    
    // Initialize date selection and show main view
    initDateSelection();
    showView('dateSelectionView');
    errorDiv.classList.add('hidden');
    
  } catch (error) {
    console.error('Login error:', error);
    let errorMessage = 'Login failed. Please try again.';
    
    // Handle specific Firebase Auth errors
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No account found with this email address.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address format.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection.';
        break;
      case 'auth/invalid-api-key':
        errorMessage = 'Firebase configuration error. Please check your project setup.';
        break;
      default:
        errorMessage = `${error.message || 'Login failed. Please try again.'}`;
    }
    
    errorText.textContent = errorMessage;
    errorDiv.classList.remove('hidden');
    toast.error('Login failed. Please check your credentials.');
    document.getElementById('password').value = '';
    
  } finally {
    // Re-enable submit button
    submitBtn.disabled = false;
    submitBtn.innerHTML = 'Login';
  }
});

// Session Management Functions (using SessionManager)

function initializeSession() {
  sessionManager.initialize();
}

function updateSessionStatusIndicator(status) {
  const indicator = document.getElementById('sessionStatusIndicator');
  if (!indicator) return;
  
  // Remove all status classes
  indicator.className = 'session-indicator';
  
  switch (status) {
    case 'active':
      indicator.classList.add('bg-success/10', 'text-success', 'border', 'border-success/20');
      indicator.innerHTML = `
        <div class="w-2 h-2 bg-success rounded-full animate-pulse"></div>
        <span>Session Active</span>
      `;
      break;
    case 'warning':
      indicator.classList.add('bg-warning/10', 'text-warning', 'border', 'border-warning/20');
      indicator.innerHTML = `
        <div class="w-2 h-2 bg-warning rounded-full animate-pulse"></div>
        <span>Session Warning</span>
      `;
      break;
    case 'expired':
      indicator.classList.add('bg-error/10', 'text-error', 'border', 'border-error/20');
      indicator.innerHTML = `
        <div class="w-2 h-2 bg-error rounded-full"></div>
        <span>Session Expired</span>
      `;
      break;
    default:
      indicator.classList.add('hidden');
  }
}

// The inactivity timer, activity listeners and warning countdown all live in
// SessionManager (src/utils/sessionManager.js), which initializeSession()
// delegates to. A second, parallel implementation used to sit here: it was
// unreachable (nothing called startSessionMonitoring or setupActivityListeners)
// and referenced a SESSION_CONFIG that was never in scope, so it would have
// thrown if it ever had been. Removed rather than left as a decoy.


function clearSessionTimers() {
  if (sessionState.timeoutTimer) {
    clearTimeout(sessionState.timeoutTimer);
    sessionState.timeoutTimer = null;
  }
  
  if (sessionState.warningTimer) {
    clearInterval(sessionState.warningTimer);
    sessionState.warningTimer = null;
  }
  
  if (sessionState.countdownInterval) {
    clearInterval(sessionState.countdownInterval);
    sessionState.countdownInterval = null;
  }
}

function cleanupSession() {
  console.log('Cleaning up session');
  clearSessionTimers();
  sessionState = {
    startTime: null,
    lastActivity: null,
    warningTimer: null,
    timeoutTimer: null,
    warningShown: false,
    isWarningModalOpen: false,
    countdownInterval: null,
    countdownTime: 0
  };
  
  // Hide session status indicator
  updateSessionStatusIndicator('hidden');
  sessionManager.cleanup();
}


function forceSignOut(reason) {
  console.log('Force sign out:', reason);
  
  toast.warning(reason || 'Session expired');
  
  resetApp();
  
  if (auth) {
    firebaseSignOut(auth).catch(error => {
      console.error('Error during force sign out:', error);
    });
  }
  
  showView('loginView');
}

// --- Date Selection Logic ---

// Initialize date picker with defaults (or restore a preferred date)
function initDateSelection(preferredDate) {
  const dateInput = document.getElementById('publicationDate');
  const tomorrowIST = getISTDateString(1);
  applyDateInputLimits(dateInput);

  const { date: dateToUse, wasClamped } = clampToSelectableDate(preferredDate || tomorrowIST);
  dateInput.value = dateToUse;
  appState.selectedDate = dateToUse;

  if (wasClamped && preferredDate) {
    toast.info(`Dates older than ${MAX_PAST_REFERENCE_DAYS} days are locked. Showing ${formatDateForDisplay(dateToUse)}.`);
  }

  // Reset banners
  document.getElementById('existingDataBanner')?.classList.add('hidden');
  document.getElementById('giftNiftyStatusBanner')?.classList.add('hidden');

  syncDateStepButtons();

  // Load DB status for this date (full-page overlay)
  checkExistingData(dateToUse, { showOverlay: true });
}

/** Open the native date picker (used by Pick Date button) */
function openPublicationDatePicker() {
  const dateInput = document.getElementById('publicationDate');
  if (!dateInput) return;

  applyDateInputLimits(dateInput);
  dateInput.focus();
  try {
    if (typeof dateInput.showPicker === 'function') {
      dateInput.showPicker();
    }
  } catch (err) {
    console.warn('showPicker unavailable:', err);
  }
}

document.getElementById('openPublicationDateBtn')?.addEventListener('click', (e) => {
  e.preventDefault();
  openPublicationDatePicker();
});

/* --- Day stepping ------------------------------------------------------------
   Moving one day is the commonest date change on every screen, and it used to
   cost a trip through the calendar widget. Each date control now carries a
   back/next pair around the date it shows.

   The buttons write the input and dispatch `change`, so none of what a date
   change *means* is duplicated here: the per-view handler still runs, keeping
   its confirm dialogs and its reloads. One delegated listener covers the
   workspace bar and both topbar chips. */

/** Grey out "previous day" once an input sits on the earliest allowed date. */
function syncDateStepButtons() {
  const earliest = getEarliestSelectableDate();
  document.querySelectorAll('[data-date-step]').forEach(btn => {
    if (Number(btn.dataset.dateStep) > 0) return; // future dates are unbounded
    const input = document.getElementById(btn.dataset.dateTarget);
    btn.disabled = !!input?.value && input.value <= earliest;
  });
}

/* Stepping four days should cost one read, not four. The date on screen moves
   on every click — that part must feel instant — but the database work waits
   for the clicking to stop. */
const DATE_STEP_SETTLE_MS = 260;
let _dateStepTimer = null;
let _dateStepInput = null;

/** Run a pending step now. For anything that must not act on a stale date. */
function flushPendingDateStep() {
  if (!_dateStepTimer) return;
  clearTimeout(_dateStepTimer);
  _dateStepTimer = null;
  const input = _dateStepInput;
  _dateStepInput = null;
  input?.dispatchEvent(new Event('change', { bubbles: true }));
}

function stepDateInput(input, deltaDays) {
  if (!input) return;
  // Read from the input, not from appState: mid-burst the state still holds the
  // date we started on, so stepping would stall on the second click.
  const from = input.value || appState.selectedDate || getISTDateString(1);
  const { date: target, wasClamped } = clampToSelectableDate(shiftDateString(from, deltaDays));

  if (wasClamped) {
    toast.error(`You can only go back ${MAX_PAST_REFERENCE_DAYS} days (from ${formatDateForDisplay(getEarliestSelectableDate())}).`);
    syncDateStepButtons();
    return;
  }

  input.value = target;
  syncDateStepButtons();

  // The workspace headline restates the date in words, and it used to wait on
  // the fetch — so the field said one day and the heading another until the
  // read came back. It leads now; only the task statuses wait.
  if (input.id === 'publicationDate') updateWorkspaceDate(target);

  // A programmatic write fires no `change`, so say it on the input's behalf —
  // once the burst has settled, so every handler downstream (confirm dialogs
  // included) sees one date change rather than one per click.
  clearTimeout(_dateStepTimer);
  _dateStepInput = input;
  _dateStepTimer = setTimeout(() => {
    _dateStepTimer = null;
    _dateStepInput = null;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }, DATE_STEP_SETTLE_MS);
}

document.addEventListener('click', (e) => {
  const btn = e.target.closest?.('[data-date-step]');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  stepDateInput(document.getElementById(btn.dataset.dateTarget), Number(btn.dataset.dateStep));
});

// Which date check currently owns the visible overlay (see its hide callback).
let _dateCheckOverlaySeq = 0;

// Cached results from the last checkExistingData call
let _dateCheckCache = { date: null, articleCount: 0, giftNifty: null, ipl: null, gm: null };
// giftNifty shape: null (not checked/none) | { lastPrice, dayChange, dayChangePerc, source }
// gm shape: null (not checked/none) | { total: number, languages: string[], data: object }

// Show the correct status banner based on currently selected route
/**
 * Paint the status banner inside every task card.
 *
 * This used to hide all four and reveal only the selected one, which meant the
 * operator had to click each task in turn to learn its state — the one question
 * they have each morning. Each banner now lives in its own card and all four are
 * written on every refresh, so the workspace answers it at a glance.
 */
function updateDateStatusBanners() {
  const ICON = {
    check:  '<circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>',
    warn:   '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/>',
    news:   '<path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9h4"/>',
    trend:  '<path d="M22 7l-8.5 8.5-4-4L2 19"/><path d="M16 7h6v6"/>',
    trophy: '<path d="M6 4h12v5a6 6 0 01-12 0V4z"/><path d="M9 20h6M12 15v5"/>',
    image:  '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>',
  };

  const paint = (bannerId, titleId, detailId, iconId, tone, glyph, title, detail) => {
    const banner = document.getElementById(bannerId);
    if (banner) {
      banner.setAttribute('data-tone', tone);
      banner.classList.remove('hidden');   // every card keeps its footer
    }
    const iconEl = document.getElementById(iconId);
    if (iconEl) iconEl.innerHTML = glyph;
    const titleEl = document.getElementById(titleId);
    if (titleEl) titleEl.textContent = title;
    const detailEl = document.getElementById(detailId);
    if (detailEl) detailEl.textContent = detail || '';
  };

  // ── News articles ──────────────────────────────────────────────────────
  const count = _dateCheckCache.articleCount || 0;
  if (count >= APP.MIN_ARTICLES) {
    paint('existingDataBanner', 'existingDataTitle', 'existingDataDetail', 'existingDataIcon',
      'positive', ICON.check,
      `${count} articles saved`,
      'You can add more, or go on to translations.');
  } else if (count > 0) {
    const need = APP.MIN_ARTICLES - count;
    paint('existingDataBanner', 'existingDataTitle', 'existingDataDetail', 'existingDataIcon',
      'warning', ICON.warn,
      `Needs ${need} more`,
      `At least ${APP.MIN_ARTICLES} articles before you can translate.`);
  } else {
    paint('existingDataBanner', 'existingDataTitle', 'existingDataDetail', 'existingDataIcon',
      'neutral', ICON.news,
      'No articles yet',
      `You need at least ${APP.MIN_ARTICLES} to continue.`);
  }

  // ── Gift Nifty ─────────────────────────────────────────────────────────
  const gn = _dateCheckCache.giftNifty;
  if (gn && gn.lastPrice != null) {
    const price = Number(gn.lastPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    if (gn.source === 'manual') {
      const sign = gn.dayChange >= 0 ? '+' : '';
      const psign = gn.dayChangePerc >= 0 ? '+' : '';
      paint('giftNiftyStatusBanner', 'giftNiftyStatusTitle', 'giftNiftyStatusDetail', 'giftNiftyStatusIcon',
        'positive', ICON.check,
        'Manual override saved',
        `${sign}${Number(gn.dayChange).toFixed(2)} (${psign}${Number(gn.dayChangePerc).toFixed(2)}%)`);
    } else {
      paint('giftNiftyStatusBanner', 'giftNiftyStatusTitle', 'giftNiftyStatusDetail', 'giftNiftyStatusIcon',
        'warning', ICON.warn,
        'Fetched, not overridden',
        `From ${gn.source || 'the API'}: ${price}. Open to set your own values.`);
    }
  } else {
    paint('giftNiftyStatusBanner', 'giftNiftyStatusTitle', 'giftNiftyStatusDetail', 'giftNiftyStatusIcon',
      'neutral', ICON.trend,
      'No values yet',
      'Nothing stored for this date.');
  }

  // ── IPL ────────────────────────────────────────────────────────────────
  const ipl = _dateCheckCache.ipl;
  if (ipl) {
    const match = (ipl.yesterdayResults && ipl.yesterdayResults[0] && ipl.yesterdayResults[0].matchLegend)
      || (ipl.yesterdayResult && ipl.yesterdayResult.matchLegend) || '';
    paint('iplStatusBanner', 'iplStatusTitle', 'iplStatusDetail', 'iplStatusIcon',
      'positive', ICON.check,
      'Snapshot saved',
      match ? `Latest: ${match}` : 'Loaded when you open it.');
  } else {
    paint('iplStatusBanner', 'iplStatusTitle', 'iplStatusDetail', 'iplStatusIcon',
      'neutral', ICON.trophy,
      'Nothing prepared',
      'No results or fixtures for this date.');
  }

  // ── Good morning images ────────────────────────────────────────────────
  const gm = _dateCheckCache.gm;
  if (gm && gm.total === 4) {
    paint('gmStatusBanner', 'gmStatusTitle', 'gmStatusDetail', 'gmStatusIcon',
      'positive', ICON.check,
      'All four languages uploaded',
      gm.languages.map(l => l.toUpperCase()).join(', '));
  } else if (gm && gm.total > 0) {
    const missing = ['en', 'hi', 'mr', 'gu'].filter(l => !gm.languages.includes(l));
    paint('gmStatusBanner', 'gmStatusTitle', 'gmStatusDetail', 'gmStatusIcon',
      'warning', ICON.warn,
      `Missing ${missing.map(l => l.toUpperCase()).join(' and ')}`,
      `${gm.total} of 4 uploaded.`);
  } else {
    paint('gmStatusBanner', 'gmStatusTitle', 'gmStatusDetail', 'gmStatusIcon',
      'neutral', ICON.image,
      'No images yet',
      'One portrait image per language.');
  }
}

// Update per-route status chips on the task cards from _dateCheckCache
// Icon geometry for the per-task status dot.
const STATUS_GLYPH = {
  positive: '<path d="M20 6L9 17l-5-5"/>',
  warning:  '<path d="M12 8v5M12 17h.01"/>',
  neutral:  '<circle cx="12" cy="12" r="7"/>',
};

/**
 * Paint one task card: its headline value, its status dot, and the tone of
 * the banner beneath it. Presentation is expressed as a semantic tone; the
 * design system resolves the colour.
 */
function setTaskStatus(key, { value, tone, empty = false }) {
  const valueEl = document.getElementById(`route${key}Status`);
  if (valueEl) {
    valueEl.textContent = value;
    if (empty) valueEl.setAttribute('data-empty', 'true');
    else valueEl.removeAttribute('data-empty');
  }
  const dot = document.getElementById(`route${key}Dot`);
  if (dot) {
    dot.setAttribute('data-tone', tone);
    dot.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="${tone === 'positive' ? 3 : 2}" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${STATUS_GLYPH[tone] || STATUS_GLYPH.neutral}</svg>`;
  }
}

/* ---------- Topbar clock -----------------------------------------------------
   Rendered in IST because every deadline in this console is: the broadcast goes
   out at 07:30 India time regardless of where the operator is sitting. */
const _istDate = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata', weekday: 'short', day: 'numeric', month: 'short',
});
const _istTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
});

/** Paint the clock. */
function updateClock() {
  const dateEl = document.getElementById('appClockDate');
  const timeEl = document.getElementById('appClockTime');
  if (!dateEl || !timeEl) return;
  const now = new Date();
  dateEl.textContent = _istDate.format(now);
  timeEl.textContent = `${_istTime.format(now)} IST`;
}

/* Seconds are not shown, so a 20s tick is enough to keep the minute honest
   without waking the page every second. */
function startClock() {
  updateClock();
  setInterval(updateClock, 20000);
}

/**
 * The topbar's one line about what the database holds, told from the point of
 * view of the page you are on. It used to live inside the date picker and only
 * ever counted articles, so on IPL or Images it stated a fact about a different
 * task — or nothing at all.
 *
 * The colour carries the same judgement the workspace cards make, so glancing
 * up from a task page answers "is this date done?" without going back:
 * complete is green, past halfway is amber, anything less is red.
 *
 * @param {string} viewId the view being shown
 */
function updateTopbarStat(viewId) {
  const el = document.getElementById('inlineDateStatus');
  if (!el) return;

  const NEWS = ['dataEntryView', 'translationOptionsView', 'articleTranslateView', 'reviewEditView'];
  let text = '';
  let done = 0;
  let target = 0;

  if (NEWS.includes(viewId)) {
    // Articles in hand can be ahead of the database mid-session; say which.
    const live = appState.newsArticles.length;
    const stored = _dateCheckCache.articleCount || 0;
    const n = live || stored;
    target = APP.MIN_ARTICLES;
    done = n;
    if (live && !appState.hasExistingData) text = `${live} unsaved`;
    else if (n) text = `${n} article${n === 1 ? '' : 's'} in DB`;
    else text = 'No articles yet';
  } else if (viewId === 'giftNiftyView') {
    const gn = _dateCheckCache.giftNifty;
    const set = !!(gn && gn.lastPrice != null);
    target = 1; done = set ? 1 : 0;
    text = set
      ? `${Number(gn.lastPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 })} in DB`
      : 'Nothing stored yet';
  } else if (viewId === 'iplView') {
    const set = !!_dateCheckCache.ipl;
    target = 1; done = set ? 1 : 0;
    text = set ? 'Snapshot in DB' : 'Nothing stored yet';
  } else if (viewId === 'gmImagesView') {
    const gm = _dateCheckCache.gm;
    target = 4; done = gm ? gm.total : 0;
    text = done > 0 ? `${done} of 4 languages in DB` : 'No images stored yet';
  }
  // The workspace is deliberately blank: its four cards already say all of this.

  el.textContent = text;
  el.classList.toggle('hidden', !text);
  if (text) el.setAttribute('data-tone', completionTone(done, target));
  else el.removeAttribute('data-tone');
}

/**
 * Complete, more than halfway, or short of it.
 * Exactly half counts as short — "more than 50%" is the amber threshold, so
 * 2 of 4 languages is not yet progress worth calling progress.
 */
function completionTone(done, target) {
  if (!target) return 'neutral';
  const ratio = done / target;
  if (ratio >= 1) return 'positive';
  if (ratio > 0.5) return 'warning';
  return 'negative';
}

function updateRouteCardStatuses() {
  // Writes land under whatever mask is up; lifting it is the deferred loading
  // handle's job. Clearing here as well made the cards snap back the instant
  // the data arrived while the overlay was still fading — the two halves of
  // the same page finishing at different times is what looked like a flicker.
  updateWorkspaceDate();
  updateWorkspaceHint();

  // News is only "ready" once it can actually proceed. Reporting green at a
  // single article contradicted the 3-article gate.
  const articles = _dateCheckCache.articleCount || 0;
  if (articles >= APP.MIN_ARTICLES) {
    setTaskStatus('Articles', { value: `${articles} articles saved`, tone: 'positive' });
  } else if (articles > 0) {
    setTaskStatus('Articles', { value: `${articles} article${articles === 1 ? '' : 's'} saved`, tone: 'warning' });
  } else {
    setTaskStatus('Articles', { value: 'No articles yet', tone: 'neutral', empty: true });
  }

  const gn = _dateCheckCache.giftNifty;
  if (gn && gn.lastPrice != null) {
    const manual = gn.source === 'manual';
    setTaskStatus('GiftNifty', {
      value: Number(gn.lastPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 }),
      tone: manual ? 'positive' : 'warning',
    });
  } else {
    setTaskStatus('GiftNifty', { value: 'No data yet', tone: 'neutral', empty: true });
  }

  if (_dateCheckCache.ipl) {
    setTaskStatus('Ipl', { value: 'Snapshot saved', tone: 'positive' });
  } else {
    setTaskStatus('Ipl', { value: 'No snapshot yet', tone: 'neutral', empty: true });
  }

  const gm = _dateCheckCache.gm;
  if (gm && gm.total > 0) {
    setTaskStatus('Gm', {
      value: `${gm.total} of 4 languages`,
      tone: gm.total === 4 ? 'positive' : 'warning',
    });
  } else {
    setTaskStatus('Gm', { value: 'No images yet', tone: 'neutral', empty: true });
  }

  updateReadiness();
  syncStepGating();
}

/** Name the selected task so the Continue button is never ambiguous. */
function updateWorkspaceHint() {
  const hint = document.getElementById('workspaceHint');
  if (!hint) return;
  // The cards are the action now, so this is a quiet note rather than a
  // label for a button.
  hint.textContent = appState.selectedDate
    ? `Select a task to open it for ${formatDateForDisplay(appState.selectedDate)}.`
    : 'Pick a publication date to begin.';
}

/** How much of this date is prepared — the operator's first question. */
function updateReadiness() {
  // Only the tasks the operator keeps on the board count. Hiding IPL should
  // move the target from 4 to 3, not leave a total that can never be reached.
  const done = {
    articles: (_dateCheckCache.articleCount || 0) >= APP.MIN_ARTICLES,
    giftNifty: !!_dateCheckCache.giftNifty,
    ipl: !!_dateCheckCache.ipl,
    gm: !!(_dateCheckCache.gm && _dateCheckCache.gm.total === 4),
  };

  const shown = getVisibleTasks();
  const total = shown.length;
  const ready = shown.filter(k => done[k]).length;

  const label = document.getElementById('readinessLabel');
  if (label) {
    label.textContent = ready === total
      ? `All ${total} ready for broadcast`
      : `${ready} of ${total} ready`;
  }
}


/**
 * The workspace headline states the date in words and names it relative to
 * today. Tomorrow is the overwhelmingly common case, so saying so makes the
 * expensive case — some other day — visually obvious.
 */
function updateWorkspaceDate(dateString = appState.selectedDate) {
  const labelEl = document.getElementById('workspaceDateLabel');
  const relEl = document.getElementById('workspaceDateRel');
  if (!labelEl) return;

  if (!dateString) {
    labelEl.textContent = 'Select a date';
    if (relEl) { relEl.textContent = '\u00a0'; relEl.removeAttribute('data-far'); }
    return;
  }

  labelEl.textContent = formatDateForDisplay(dateString);

  if (!relEl) return;
  const today = getISTDateString(0);
  const tomorrow = getISTDateString(1);
  if (dateString === tomorrow) {
    relEl.textContent = 'Tomorrow';
    relEl.removeAttribute('data-far');
  } else if (dateString === today) {
    relEl.textContent = 'Today';
    relEl.removeAttribute('data-far');
  } else {
    relEl.textContent = dateString < today ? 'Past date' : 'Future date';
    relEl.setAttribute('data-far', 'true');
  }
}

/**
 * Which pipeline steps are reachable. A step with nothing to show is not a
 * step the operator should be able to land on, and a submitted date is closed
 * until they choose to reopen it.
 */
function syncStepGating() {
  const count = appState.newsArticles.length;
  const enough = count >= APP.MIN_ARTICLES;
  const hasTranslations = Object.keys(appState.translations || {}).length > 0;
  const submitted = !!appState.submittedForDate && appState.submittedForDate === appState.selectedDate;
  const closed = 'Submitted for this date — reopen it from the workspace to edit';

  setStepAvailability({
    articles: count > 0 && !submitted,
    languages: enough && !submitted,
    review: hasTranslations && !submitted,
    reason: {
      articles: submitted ? closed : 'Add articles first',
      languages: submitted ? closed : `Add at least ${APP.MIN_ARTICLES} articles first`,
      review: submitted ? closed : 'Generate translations first',
    },
    stepperHint: submitted ? 'Submitted'
      : !enough ? `Needs ${APP.MIN_ARTICLES} articles to continue` : '',
  });
}

function setRouteCardStatusesLoading() {
  // Mask the four cards in place. Their boxes keep their size, so nothing
  // shifts when the real values arrive.
  ['Articles', 'GiftNifty', 'Ipl', 'Gm'].forEach(key => {
    mask([
      [`route${key}Status`, 'md'],
      `route${key}Dot`,
    ]);
  });
  mask([
    ['existingDataTitle', 'md'], ['existingDataDetail', 'lg'],
    ['giftNiftyStatusTitle', 'md'], ['giftNiftyStatusDetail', 'lg'],
    ['iplStatusTitle', 'md'], ['iplStatusDetail', 'lg'],
    ['gmStatusTitle', 'md'], ['gmStatusDetail', 'lg'],
    ['readinessLabel', 'sm'],
  ]);
}

/** Clear every workspace skeleton once the date's data is in. */
function clearRouteCardSkeletons() {
  const ids = [];
  ['Articles', 'GiftNifty', 'Ipl', 'Gm'].forEach(key => {
    ids.push(`route${key}Status`, `route${key}Dot`);
  });
  ids.push(
    'existingDataTitle', 'existingDataDetail',
    'giftNiftyStatusTitle', 'giftNiftyStatusDetail',
    'iplStatusTitle', 'iplStatusDetail',
    'gmStatusTitle', 'gmStatusDetail',
    'readinessLabel',
  );
  unmask(ids);
}


// Ignore stale responses when the user changes date quickly
let _dateCheckSeq = 0;

// Check Firestore for existing data on selected date (articles + Gift Nifty + IPL + GM)
async function checkExistingData(dateString, { showOverlay = true } = {}) {
  const continueBtn = document.getElementById('continueBtn');
  const seq = ++_dateCheckSeq;

  const existingDataBanner = document.getElementById('existingDataBanner');
  const giftNiftyStatusBanner = document.getElementById('giftNiftyStatusBanner');
  const iplStatusBanner = document.getElementById('iplStatusBanner');
  const gmStatusBanner = document.getElementById('gmStatusBanner');

  if (existingDataBanner) existingDataBanner.classList.add('hidden');
  if (giftNiftyStatusBanner) giftNiftyStatusBanner.classList.add('hidden');
  if (iplStatusBanner) iplStatusBanner.classList.add('hidden');
  if (gmStatusBanner) gmStatusBanner.classList.add('hidden');

  appState.hasExistingData = false;
  _dateCheckCache = { date: dateString, articleCount: 0, giftNifty: null, ipl: null, gm: null };

  // Both loading states go behind the grace period. Stepping one day usually
  // answers from Firestore's cache in a few milliseconds, and showing — then
  // immediately hiding — a blocking overlay and four skeletons is the flicker.
  const loading = deferredLoading(
    () => {
      setRouteCardStatusesLoading();
      if (showOverlay) {
        _dateCheckOverlaySeq = seq;
        showFetchOverlay('Loading data for this date...', {
          subtext: formatDateForDisplay(dateString),
          spinnerColor: '#DD5D36'
        });
      }
    },
    // Only whoever raised the overlay may take it down. Holding it for its
    // minimum opens a window in which a superseded check could otherwise pull
    // the overlay out from under the check that replaced it; and hiding it by
    // sequence instead would strand it whenever the newer check is quick
    // enough never to raise one of its own.
    () => { if (showOverlay && _dateCheckOverlaySeq === seq) hideFetchOverlay(); }
  );

  if (!db) {
    console.error('[checkExistingData] Firebase not initialized, skipping data check');
    loading.done(() => { if (seq === _dateCheckSeq) clearRouteCardSkeletons(); });
    if (seq !== _dateCheckSeq) return;
    continueBtn.disabled = false;
    continueBtn.innerHTML = `<span>Continue</span><svg class="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;
    updateRouteCardStatuses();
    updateDateStatusBanners();
    return;
  }

  continueBtn.disabled = true;
  continueBtn.innerHTML = '<span class="spinner-dot"></span><span>Checking\u2026</span>';

  try {
    console.log(`[checkExistingData] Checking data for ${dateString}`);
    const [newsSnap, marketSnap, iplSnap, gmSnap] = await Promise.all([
      getDoc(doc(db, 'news_snapshots', dateString)),
      getDoc(doc(db, 'market_snapshots', dateString)),
      getDoc(doc(db, 'ipl_snapshots', dateString)),
      getDoc(doc(db, 'gm_daily_images', dateString))
    ]);

    // A newer date check started — don't apply this result
    if (seq !== _dateCheckSeq) return;

    console.log('[checkExistingData] Documents fetched:', {
      newsSnap: newsSnap.exists(),
      marketSnap: marketSnap.exists(),
      iplSnap: iplSnap.exists(),
      gmSnap: gmSnap.exists()
    });

    if (newsSnap.exists()) {
      const data = newsSnap.data();
      const articleCount = data.news ? data.news.length : 0;
      // Store the true count either way — the card needs it to say
      // "2 articles saved, needs 1 more" rather than "No articles yet".
      _dateCheckCache.articleCount = articleCount;
      if (articleCount >= APP.MIN_ARTICLES) {
        appState.hasExistingData = true;
      }
    }

    if (marketSnap.exists()) {
      const gn = marketSnap.data()?.giftNifty?.data;
      if (gn && gn.lastPrice) {
        _dateCheckCache.giftNifty = {
          lastPrice: gn.lastPrice,
          dayChange: gn.dayChange ?? 0,
          dayChangePerc: gn.dayChangePerc ?? 0,
          source: gn.source || 'API'
        };
      }
    }

    if (iplSnap.exists()) {
      _dateCheckCache.ipl = iplSnap.data();
    }

    if (gmSnap.exists()) {
      const gmData = gmSnap.data();
      const languages = ['en', 'hi', 'mr', 'gu'];
      const uploadedLanguages = languages.filter(lang => gmData[lang]);

      _dateCheckCache.gm = {
        total: uploadedLanguages.length,
        languages: uploadedLanguages,
        data: gmData
      };

      console.log(`[checkExistingData] GM images: ${uploadedLanguages.length}/4 languages`);
    }
  } catch (error) {
    if (seq !== _dateCheckSeq) return;
    console.error('Error checking existing data:', error);
    toast.error('Failed to load status for this date');
  } finally {
    // The loading state belongs to the call that raised it, so it comes down
    // even when a newer check has superseded this one. Guarding it by sequence
    // — as this did — meant a silent refresh starting mid-check (which is
    // exactly what returning from a task does) left the spinner turning with
    // nothing left to take it down.
    // The masks clear either way — the page loads already wearing some, so a
    // fetch too fast to raise a spinner must still undress it. Only the latest
    // request may do it, or a stale answer would reveal cards a newer check has
    // just re-masked.
    loading.done(() => { if (seq === _dateCheckSeq) clearRouteCardSkeletons(); });

    // The UI writes below are a different matter: only the latest request may
    // apply them, or a stale answer would overwrite a fresher one.
    if (seq === _dateCheckSeq) {
      continueBtn.disabled = false;
      continueBtn.innerHTML = `<span>Continue</span><svg class="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;
      updateRouteCardStatuses();
      updateDateStatusBanners();
    }
  }
}

// Date input change handler — refresh all task statuses for the new date
document.getElementById('publicationDate').addEventListener('change', (e) => {
  const rawDate = e.target.value;
  if (!rawDate) return;

  const { date: selectedDate, wasClamped } = clampToSelectableDate(rawDate);
  if (wasClamped) {
    e.target.value = selectedDate;
    toast.error(`You can only go back ${MAX_PAST_REFERENCE_DAYS} days (from ${formatDateForDisplay(getEarliestSelectableDate())}).`);
  }

  appState.selectedDate = selectedDate;
  appState.submittedForDate = null;
  syncDateStepButtons();
  updateWorkspaceDate(selectedDate);
  checkExistingData(selectedDate, { showOverlay: true });
});

// Route option label visual selection (radio → highlight label)
document.querySelectorAll('input[name="routeSelector"]').forEach(radio => {
  radio.addEventListener('change', () => {
    document.querySelectorAll('.route-option-label').forEach(lbl => {
      lbl.classList.remove('border-success', 'bg-success/5', 'border-brand-accent', 'bg-brand-accent/5', 'border-warning', 'bg-warning/5', 'border-purple-500', 'bg-purple-50');
      lbl.classList.add('border-gray-200', 'bg-white/50');
    });
    const selected = document.querySelector('input[name="routeSelector"]:checked');
    if (selected) {
      const lbl = selected.closest('.route-option-label');
      if (selected.value === 'articles') {
        lbl.classList.remove('border-gray-200', 'bg-white/50');
        lbl.classList.add('border-success', 'bg-success/5');
      } else if (selected.value === 'giftNifty') {
        lbl.classList.remove('border-gray-200', 'bg-white/50');
        lbl.classList.add('border-brand-accent', 'bg-brand-accent/5');
      } else if (selected.value === 'ipl') {
        lbl.classList.remove('border-gray-200', 'bg-white/50');
        lbl.classList.add('border-warning', 'bg-warning/5');
      } else if (selected.value === 'gm') {
        lbl.classList.remove('border-gray-200', 'bg-white/50');
        lbl.classList.add('border-purple-500', 'bg-purple-50');
      }
    }
    // Hide route error
    document.getElementById('routeSelectorError').classList.add('hidden');
    // Show the right status banner for the selected route
    updateDateStatusBanners();
  });
})

// ── Task picker ────────────────────────────────────────────────────────────
// Chooses which task cards belong on this operator's workspace.

function renderTaskPickerOptions() {
  const box = document.getElementById('taskPickerOptions');
  if (!box) return;
  const visible = getVisibleTasks();
  box.innerHTML = TASKS.map(t => `
    <label class="picker__option">
      <span class="check"><input type="checkbox" value="${t.key}" ${visible.includes(t.key) ? 'checked' : ''} /></span>
      <span class="picker__option-label">${t.label}</span>
    </label>`).join('');
  document.getElementById('taskPickerError')?.classList.add('hidden');
}

function openTaskPicker() {
  renderTaskPickerOptions();                 // always open showing what is live
  document.getElementById('taskPickerPanel')?.removeAttribute('hidden');
  document.getElementById('taskPickerBtn')?.setAttribute('aria-expanded', 'true');
}

function closeTaskPicker() {
  document.getElementById('taskPickerPanel')?.setAttribute('hidden', '');
  document.getElementById('taskPickerBtn')?.setAttribute('aria-expanded', 'false');
}

document.getElementById('taskPickerBtn')?.addEventListener('click', (e) => {
  e.stopPropagation();
  const open = document.getElementById('taskPickerPanel')?.hasAttribute('hidden');
  open ? openTaskPicker() : closeTaskPicker();
});

document.getElementById('taskPickerCancel')?.addEventListener('click', closeTaskPicker);

// Click-away and Escape close without applying, so an accidental open never
// changes anything.
document.addEventListener('click', (e) => {
  if (!e.target.closest('#taskPicker')) closeTaskPicker();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeTaskPicker();
});

document.getElementById('taskPickerApply')?.addEventListener('click', async () => {
  const chosen = Array.from(
    document.querySelectorAll('#taskPickerOptions input[type="checkbox"]:checked')
  ).map(i => i.value);

  // An empty board would leave the operator with nowhere to go.
  const err = document.getElementById('taskPickerError');
  if (chosen.length === 0) { err?.classList.remove('hidden'); return; }
  err?.classList.add('hidden');

  const btn = document.getElementById('taskPickerApply');
  btn?.setAttribute('data-loading', 'true');

  const uid = auth?.currentUser?.uid;
  const result = await saveTaskPreferences(db, uid, chosen);

  btn?.removeAttribute('data-loading');
  applyTaskVisibility();
  updateReadiness();
  closeTaskPicker();

  const hiddenCount = TASKS.length - chosen.length;
  if (result.saved) {
    toast.success(hiddenCount
      ? `Workspace updated. ${hiddenCount} task${hiddenCount === 1 ? '' : 's'} hidden — bring them back from Tasks.`
      : 'Workspace updated. All tasks showing.');
  } else {
    // The board still changed locally; be honest that it will not follow them.
    toast.warning('Workspace updated on this device, but the preference could not be saved.');
  }
});

// Manual refresh — for when something changed outside this session, or the
// operator simply wants to confirm what is stored right now.
document.getElementById('workspaceRefreshBtn')?.addEventListener('click', () => {
  if (!appState.selectedDate) {
    toast.warning('Pick a publication date first.');
    return;
  }
  refreshWorkspaceData({ silent: false });
});

// Continue button handler
document.getElementById('continueBtn').addEventListener('click', async () => {
  // A step still sitting in the debounce would have this read a date whose
  // statuses were never fetched. Settle it first.
  flushPendingDateStep();

  const dateInput = document.getElementById('publicationDate');
  applyDateInputLimits(dateInput);

  let selectedDate = dateInput.value;
  if (!selectedDate) {
    toast.warning('Please select a publication date');
    return;
  }

  const { date: allowedDate, wasClamped } = clampToSelectableDate(selectedDate);
  if (wasClamped) {
    dateInput.value = allowedDate;
    toast.error(`You can only go back ${MAX_PAST_REFERENCE_DAYS} days (from ${formatDateForDisplay(getEarliestSelectableDate())}).`);
    checkExistingData(allowedDate, { showOverlay: true });
    return;
  }
  selectedDate = allowedDate;

  // Validate route selection
  const selectedRoute = document.querySelector('input[name="routeSelector"]:checked')?.value;
  if (!selectedRoute) {
    document.getElementById('routeSelectorError').classList.remove('hidden');
    toast.warning('Please select what you want to update.');
    return;
  }

  appState.selectedDate = selectedDate;
  appState.submittedForDate = null;
  updateDateBadges(selectedDate);

  if (selectedRoute === 'giftNifty') {
    // Route → Gift Nifty Override View
    syncInlineDatePicker(selectedDate);
    resetGiftNiftyPanel();
    showView('giftNiftyView');
    loadExistingGiftNifty(selectedDate);
    toast.info(`Gift Nifty override for ${formatDateForDisplay(selectedDate)}`);
    return;
  }

  if (selectedRoute === 'ipl') {
    // Route → IPL Manager View
    syncInlineDatePicker(selectedDate);
    resetIplManagerPanel();
    showView('iplView');
    loadExistingIplData(selectedDate);
    toast.info(`IPL Manager for ${formatDateForDisplay(selectedDate)}`);
    return;
  }

  if (selectedRoute === 'gm') {
    // Route → GM Images View
    syncInlineDatePicker(selectedDate);
    showView('gmImagesView');
    // Restore previews from localStorage instead of resetting
    if (gmImagesController) {
      gmImagesController.restorePreviews(selectedDate);
      gmImagesController.loadExistingImages(selectedDate);
    }
    toast.info(`GM Images for ${formatDateForDisplay(selectedDate)}`);
    return;
  }

  // Route → News Articles
  if (selectedRoute === 'articles' && db) {
    const btn = document.getElementById('continueBtn');
    btn.classList.add('loading');
    btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Loading articles...';

    try {
      const docSnap = await getDoc(doc(db, 'news_snapshots', selectedDate));
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.news && data.news.length > 0) {
          appState.newsArticles = data.news.map((item, index) => ({
            title: item.title,
            description: item.description || '',
            source: item.source,
            link: item.url || '',
            id: Date.now() + index,
            _dbData: item
          }));
          markArticlesSaved();
          toast.info(`Loaded ${data.news.length} existing articles for ${formatDateForDisplay(selectedDate)}`);
        } else {
          startFreshArticles(selectedDate);
        }
      } else {
        startFreshArticles(selectedDate);
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
      appState.newsArticles = [];
      appState.translations = {};
      appState.hasExistingData = false;
      toast.error('Failed to load existing data. Starting fresh.');
    }

    btn.classList.remove('loading');
    btn.innerHTML = `<span>Continue</span><svg class="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;
  } else if (selectedRoute === 'articles') {
    toast.info(`Curating news for ${formatDateForDisplay(selectedDate)}`);
  }

  syncInlineDatePicker(selectedDate);
  showView('dataEntryView');
});

/** Enter the news page with nothing carried over from a previous date. */
function startFreshArticles(dateString) {
  appState.newsArticles = [];
  appState.translations = {};
  appState.hasExistingData = false;
  markArticlesSaved();
  toast.info(`No articles found for ${formatDateForDisplay(dateString)}. Starting fresh.`);
}

// --- Sign Out Logic ---

async function signOut() {
  if (appState.newsArticles.length > 0 || Object.keys(appState.translations).length > 0) {
    const confirmed = await showConfirmModal({
      title: 'Sign out?',
      message: 'You have work on this screen that has not been submitted. Signing out discards it. Anything already saved to the database is unaffected.',
      confirmText: 'Sign out',
      cancelText: 'Keep working',
      variant: 'warning'
    });
    if (!confirmed) return;
  }
  
  // Clean up session before signing out
  cleanupSession();
  
  try {
    // Sign out from Firebase Auth
    if (auth) {
      await firebaseSignOut(auth);
      console.log('User signed out from Firebase Auth');
    }
  } catch (error) {
    console.error('Error signing out from Firebase Auth:', error);
  }
  
  resetApp();
  toast.info('Signed out successfully');
}

// Sign out buttons on all views
document.getElementById('signOutDateView').addEventListener('click', signOut);
document.getElementById('signOutEntryView').addEventListener('click', signOut);
document.getElementById('signOutTranslationView').addEventListener('click', signOut);
document.getElementById('signOutReviewView').addEventListener('click', signOut);
document.getElementById('signOutGiftNiftyView').addEventListener('click', signOut);

// --- Back Button Logic ---

// Back from Data Entry → Date Selection
document.getElementById('backToDateFromEntry').addEventListener('click', async () => {
  if (await requestLeave('dataEntryView', showConfirmModal) !== 'go') return;
  showView('dateSelectionView');
});

// Back from Gift Nifty → Date Selection
document.getElementById('backToDateFromGiftNifty').addEventListener('click', () => {
  resetGiftNiftyPanel();
  resetDateSelectionView();
  showView('dateSelectionView');
});


// Back from Translation Options → Data Entry
document.getElementById('backToEntryFromTranslation').addEventListener('click', () => {
  showView('dataEntryView');
});

// Back from Review → smart destination
document.getElementById('backToTranslationFromReview').addEventListener('click', async () => {
  if (appState.skippedTranslationStep) {
    // Came directly from data entry (all had translations) — go back to data entry
    resetSubmitButton();
    showView('dataEntryView');
    syncInlineDatePicker(appState.selectedDate);
    return;
  }

  if (await requestLeave('reviewEditView', showConfirmModal) !== 'go') return;
  showView('translationOptionsView');
});

// Reset date selection view (route selector, banners, button)
function resetDateSelectionView() {
  // Uncheck route radios
  document.querySelectorAll('input[name="routeSelector"]').forEach(r => r.checked = false);
  // Reset all route labels to default style
  document.querySelectorAll('.route-option-label').forEach(lbl => {
    lbl.classList.remove('border-accent1-300', 'bg-accent1-50', 'border-primary-300', 'bg-primary-50', 'border-purple-500', 'bg-purple-50');
    lbl.classList.add('border-gray-200', 'bg-white');
  });
  document.getElementById('routeSelectorError').classList.add('hidden');
  document.getElementById('existingDataBanner').classList.add('hidden');
  document.getElementById('giftNiftyStatusBanner').classList.add('hidden');
  document.getElementById('gmStatusBanner').classList.add('hidden');
  appState.hasExistingData = false;
  _dateCheckCache = { date: null, articleCount: 0, giftNifty: null, ipl: null, gm: null };
  // Re-init date picker for the last selected date (single overlay fetch)
  initDateSelection(appState.selectedDate || getISTDateString(1));
}

// --- Inline Date Change on Data Entry View ---

function syncInlineDatePicker(dateString) {
  const inlinePicker = document.getElementById('inlineDatePicker');
  const gmInlinePicker = document.getElementById('gmInlineDatePicker');
  const { date: allowedDate } = clampToSelectableDate(dateString);

  applyDateInputLimits(inlinePicker);
  applyDateInputLimits(gmInlinePicker);

  if (inlinePicker) inlinePicker.value = allowedDate;
  if (gmInlinePicker) gmInlinePicker.value = allowedDate;
  syncDateStepButtons();

  // Check and show status badge
  checkInlineDateStatus(allowedDate);
}

async function checkInlineDateStatus(dateString) {
  if (!db) return false;

  try {
    const docSnap = await getDoc(doc(db, 'news_snapshots', dateString));
    const count = docSnap.exists() ? (docSnap.data().news?.length || 0) : 0;
    if (_dateCheckCache.date === dateString) _dateCheckCache.articleCount = count;
    updateTopbarStat(ViewManager.getCurrentView());
    return count > 0;
  } catch (error) {
    console.error('Error checking inline date:', error);
    return false;
  }
}

document.getElementById('inlineDatePicker').addEventListener('change', async (e) => {
  let newDate = e.target.value;
  if (!newDate) return;

  const { date: allowedDate, wasClamped } = clampToSelectableDate(newDate);
  if (wasClamped) {
    e.target.value = allowedDate;
    toast.error(`You can only go back ${MAX_PAST_REFERENCE_DAYS} days (from ${formatDateForDisplay(getEarliestSelectableDate())}).`);
    newDate = allowedDate;
  }

  if (newDate === appState.selectedDate) return;

  // Warn if articles exist
  if (appState.newsArticles.length > 0) {
    const confirmed = await showConfirmModal({
      title: 'Change Date?',
      message: `Changing date will clear your current ${appState.newsArticles.length} article${appState.newsArticles.length > 1 ? 's' : ''} and load data for the new date. Continue?`,
      confirmText: 'Change Date',
      variant: 'warning'
    });
    if (!confirmed) {
      // Reset picker to current date
      e.target.value = appState.selectedDate;
      syncDateStepButtons();
      return;
    }
  }

  // Update state
  appState.selectedDate = newDate;
  appState.submittedForDate = null;
  appState.newsArticles = [];
  appState.translations = {};
  updateDateBadges(newDate);
  syncDateStepButtons();
  document.getElementById('articleForm').reset();
  // Reset Gift Nifty panel for the new date
  resetGiftNiftyPanel();

  // Check and load existing data for new date
  const hasData = await checkInlineDateStatus(newDate);
  if (hasData && db) {
    try {
      const docSnap = await getDoc(doc(db, 'news_snapshots', newDate));
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.news && data.news.length > 0) {
          appState.newsArticles = data.news.map((item, index) => ({
            title: item.title,
            description: item.description || '',
            source: item.source,
            link: item.url || '',
            id: Date.now() + index,
            _dbData: item
          }));
          appState.hasExistingData = true;
          toast.info(`Loaded ${data.news.length} existing articles for ${formatDateForDisplay(newDate)}`);
        }
      }
    } catch (error) {
      console.error('Error loading data for new date:', error);
      toast.error('Failed to load existing data for new date.');
    }
  } else {
    appState.hasExistingData = false;
    toast.info(`Switched to ${formatDateForDisplay(newDate)}`);
  }

  // Whatever the outcome, the list now reflects the database for this date.
  markArticlesSaved();
  updateArticlesList();
});

// Article Form Handler
document.getElementById('articleForm').addEventListener('submit', (e) => {
  e.preventDefault();

  // Check if maximum articles limit reached
  if (appState.newsArticles.length >= APP.MAX_ARTICLES) {
    toast.warning(`Maximum ${APP.MAX_ARTICLES} articles allowed. Please remove an article before adding a new one.`);
    return;
  }

  const title = document.getElementById('articleTitle').value.trim();
  const source = document.getElementById('articleSource').value.trim();
  const description = document.getElementById('articleDescription').value.trim();
  const link = document.getElementById('articleLink').value.trim();

  // Only headline and source are required
  if (!title || !source) {
    toast.warning('Headline/Title and Source Name are required.');
    if (!title) document.getElementById('articleTitle').focus();
    else document.getElementById('articleSource').focus();
    return;
  }

  const newArticle = {
    title,
    description: description || '',
    source,
    link: link || '',
    id: Date.now()
  };

  appState.newsArticles.push(newArticle);
  updateArticlesList();
  toast.success('Article added successfully!');

  // Clear form and auto-focus title for next add
  document.getElementById('articleForm').reset();
  document.getElementById('articleTitle').focus();
});

// Clear Article Form Button
document.getElementById('clearArticleFormBtn').addEventListener('click', () => {
  document.getElementById('articleForm').reset();
  document.getElementById('articleTitle').focus();
  toast.info('Form cleared.');
});

// Per-article translation status helpers
const TRANSLATE_LANGS = ['hi', 'mr', 'gu'];

// Hydrate appState.translations[index] from article._dbData.translations so chips
// reflect saved DB state even before the user enters the translate view.
function hydrateTranslationsFromDB() {
  appState.newsArticles.forEach((article, i) => {
    if (!appState.translations[i]) appState.translations[i] = {};
    const dbT = article._dbData && article._dbData.translations;
    if (!dbT) return;
    TRANSLATE_LANGS.forEach(lang => {
      const t = dbT[lang];
      if (t && t.title && t.source && !appState.translations[i][lang]) {
        appState.translations[i][lang] = {
          title: t.title,
          description: t.description || '',
          source: t.source
        };
      }
    });
  });
}

/**
 * Translations held only in this session.
 *
 * hydrateTranslationsFromDB() copies stored translations into
 * appState.translations, so "are there translations?" is true of a date that
 * was merely opened. The question that matters is whether any of them differ
 * from what is stored.
 */
function countUnsavedTranslations() {
  return appState.newsArticles.reduce((n, article, i) => {
    const session = appState.translations[i];
    if (!session) return n;
    const stored = (article._dbData && article._dbData.translations) || {};
    const differs = TRANSLATE_LANGS.some(lang => {
      const mine = session[lang];
      if (!mine) return false;
      const theirs = stored[lang];
      if (!theirs) return true;
      return mine.title !== theirs.title
        || (mine.description || '') !== (theirs.description || '')
        || mine.source !== theirs.source;
    });
    return differs ? n + 1 : n;
  }, 0);
}

// Return per-language translation status: { hi:bool, mr:bool, gu:bool, doneCount, total }
function getArticleTranslationStatus(index) {
  const status = { doneCount: 0, failedCount: 0, total: TRANSLATE_LANGS.length };
  const failures = appState.translationFailures[index] || {};
  TRANSLATE_LANGS.forEach(lang => {
    const t = appState.translations[index] && appState.translations[index][lang];
    const present = !!(t && t.title && t.source);
    // Present but untranslated: the service failed and the English text was
    // kept. It counts as filled, but it is not a translation.
    const fellBack = present && !!failures[lang];
    status[lang] = present;
    status[`${lang}_fellBack`] = fellBack;
    if (present) status.doneCount++;
    if (fellBack) status.failedCount++;
  });
  return status;
}

// Render small per-language status chips (HI / MR / GU)
function renderLanguageChips(index) {
  const status = getArticleTranslationStatus(index);
  return TRANSLATE_LANGS.map(lang => {
    const done = status[lang];
    const fellBack = status[`${lang}_fellBack`];
    const label = lang.toUpperCase();

    // A language that fell back to English is filled but not translated, and
    // must never read as done — that is what let a silent failure reach air.
    let tone, glyph, title;
    if (fellBack) {
      tone = 'warning';
      glyph = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 8v5M12 17h.01"/></svg>';
      title = 'Translation failed — still shows the English text';
    } else if (done) {
      tone = 'positive';
      glyph = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';
      title = 'Translated';
    } else {
      tone = 'neutral';
      glyph = '';
      title = 'Not translated';
    }
    return `<span class="badge" data-variant="outline" data-tone="${tone}" title="${title}">${glyph}${label}</span>`;
  }).join('');
}


/**
 * How many articles exist only in this session. Articles carrying `_dbData`
 * are already persisted, so navigating away never loses them — copy that
 * claimed otherwise made routine navigation look destructive.
 */
function countUnsavedArticles() {
  return appState.newsArticles.filter(a => !a._dbData).length;
}

/* ---------- What the database does not have -----------------------------------
   Order is held in memory until submission (handleMoveArticle syncs on submit),
   so a reordered list of saved articles is genuinely unsaved work even though
   every article in it is stored. Everything else — adding, editing, deleting —
   writes as it happens.

   The baseline is the order of saved articles the last time this list agreed
   with the database. Without it, "is there unsaved work?" was answered with
   `newsArticles.length > 0`, which is also true of a list that was just loaded
   from the database and not touched — so leaving a finished date warned about
   losing work that was never at risk. */
let _savedOrderBaseline = [];

/** Record that the list now matches what is stored. */
function markArticlesSaved() {
  _savedOrderBaseline = appState.newsArticles.filter(a => a._dbData).map(a => a.title);
}

/** @returns {{added: number, reordered: boolean}} */
function unsavedArticleChanges() {
  const added = countUnsavedArticles();
  const order = appState.newsArticles.filter(a => a._dbData).map(a => a.title);
  const reordered = order.join('\u0000') !== _savedOrderBaseline.join('\u0000');
  return { added, reordered };
}

function hasUnsavedArticleChanges() {
  const { added, reordered } = unsavedArticleChanges();
  return added > 0 || reordered;
}

/** Say precisely what would be lost, so the warning is worth reading. */
function describeUnsavedArticles() {
  const { added, reordered } = unsavedArticleChanges();
  const bits = [];
  if (added) bits.push(`${added} article${added === 1 ? '' : 's'} you added`);
  if (reordered) bits.push('the order you set');

  // Singular only when there is one thing and that thing is singular: "the
  // order you set is", "1 article you added is", but "2 articles ... are".
  const singular = bits.length === 1 && (added === 1 || (reordered && !added));
  const what = bits.join(' and ');
  return `${what.charAt(0).toUpperCase()}${what.slice(1)} `
    + `${singular ? 'is' : 'are'} not in the database yet. `
    + 'Everything already saved for this date is unaffected and loads again when you come back.';
}

/** State the real gate in the action bar rather than a fixed sentence. */
function updateEntryActionHint() {
  const hint = document.getElementById('entryHint');
  if (!hint) return;
  const total = appState.newsArticles.length;
  const unsaved = countUnsavedArticles();

  if (total < APP.MIN_ARTICLES) {
    const need = APP.MIN_ARTICLES - total;
    hint.textContent = `Add ${need} more article${need === 1 ? '' : 's'} to continue.`;
  } else if (total > APP.MAX_ARTICLES) {
    const over = total - APP.MAX_ARTICLES;
    hint.textContent = `Remove ${over} article${over === 1 ? '' : 's'} — the maximum is ${APP.MAX_ARTICLES}.`;
  } else if (unsaved > 0) {
    hint.textContent = `${unsaved} article${unsaved === 1 ? '' : 's'} not saved yet — they are stored when you submit.`;
  } else {
    hint.textContent = 'All articles saved.';
  }
}

/**
 * A blank numbered row for an article that is still required.
 *
 * The list used to collapse to a single "No articles yet" line, which said
 * nothing about how many were needed or what a row holds. Keeping the shape
 * visible makes the remaining work countable at a glance.
 */
function articleSlotHtml(n) {
  return `<div class="record record--slot">
      <div class="record__head">
        <span class="record__num">${n}</span>
        <div class="record__body">
          <p class="record__title">Article ${n}</p>
          <p class="record__desc">Headline and source required. Description and link optional.</p>
        </div>
      </div>
    </div>`;
}

// Update Articles List Display
function updateArticlesList() {
  // Keep translation chips in sync with DB-backed articles
  hydrateTranslationsFromDB();

  const container = document.getElementById('articlesContainer');
  const countElement = document.getElementById('articlesCount');
  const nextButton = document.getElementById('nextButtonContainer');

  countElement.textContent = appState.newsArticles.length;
  updateEntryActionHint();
  syncStepGating();

  if (appState.newsArticles.length === 0) {
    // Empty, but not blank: show the slots still to fill.
    container.innerHTML = Array.from(
      { length: APP.MAX_ARTICLES },
      (_, i) => articleSlotHtml(i + 1)
    ).join('');
    nextButton.classList.add('hidden');
    syncStatusBadge();
    return;
  }

  container.innerHTML = '';
  appState.newsArticles.forEach((article, index) => {
    const articleDiv = document.createElement('div');
    articleDiv.className = 'article-card';
    articleDiv.dataset.index = index;

    // Escape HTML for safe rendering
    const safeTitle = escapeHtml(article.title);
    const safeDescription = escapeHtml(article.description || '');
    const safeSource = escapeHtml(article.source);
    const safeLink = escapeHtml(article.link || '');

    articleDiv.innerHTML = `
      <div class="record">

        <div class="record__head">
          <span class="record__num">${index + 1}</span>
          <div class="record__body">
            <p class="record__title">${safeTitle}</p>
            ${safeDescription ? `<p class="record__desc">${safeDescription}</p>` : ''}
          </div>
          <span class="record__tools">
            <button type="button" class="iconbtn" data-action="move-up" data-index="${index}" title="Move up" ${index === 0 ? 'disabled' : ''} aria-label="Move up">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M18 15l-6-6-6 6"/></svg>
            </button>
            <button type="button" class="iconbtn" data-action="move-down" data-index="${index}" title="Move down" ${index === appState.newsArticles.length - 1 ? 'disabled' : ''} aria-label="Move down">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
            </button>

            <span class="menu">
              <button type="button" class="iconbtn" title="More actions" aria-label="More actions">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="12" cy="19" r="1"/></svg>
              </button>
              <span class="menu__panel">
                <button type="button" class="menu__item" data-action="edit" data-index="${index}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M17 3a2.8 2.8 0 014 4L7.5 20.5 2 22l1.5-5.5z"/></svg>Edit
                </button>
                <button type="button" class="menu__item" data-action="copy-title" data-index="${index}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy headline
                </button>
                ${safeDescription ? `<button type="button" class="menu__item" data-action="copy-description" data-index="${index}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy description
                </button>` : ''}
                <button type="button" class="menu__item" data-action="copy-source" data-index="${index}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy source
                </button>
                ${article.link ? `<button type="button" class="menu__item" data-action="copy-link" data-index="${index}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy link
                </button>` : ''}
                <span class="menu__sep"></span>
                <button type="button" class="menu__item" data-tone="negative" data-action="delete" data-index="${index}">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>Delete
                </button>
              </span>
            </span>
          </span>
        </div>

        <div class="record__foot">
          <span class="record__meta" title="${safeSource}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9h4"/></svg>
            <span>${safeSource}</span>
          </span>

          ${article.link ? `<a href="${safeLink}" target="_blank" rel="noopener" class="record__meta" title="Open article" style="color:var(--info-ink)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6"/><path d="M10 14L21 3"/></svg>
            <span>Link</span>
          </a>` : ''}

          <span class="u-spacer"></span>

          <span class="record__chips">
            ${article._dbData
              ? '<span class="badge" data-tone="positive" title="Saved to the database">Saved</span>'
              : '<span class="badge" data-tone="warning" title="Only in this session until you submit">Unsaved</span>'}
            ${renderLanguageChips(index)}
          </span>

          <button type="button" class="btn" data-variant="secondary" data-size="sm" data-action="translate" data-index="${index}">
            <svg class="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M4 5h10M9 3v2c0 4.4-2.7 8-6 8"/><path d="M5 9c0 2.8 3.1 5.6 7 6M12 20l4.5-9 4.5 9M15 17h5"/></svg>
            <span>Translate</span>
          </button>
        </div>

      </div>
    `;
    container.appendChild(articleDiv);
  });

  // One blank slot for every place the day can still hold, so both "1 more to
  // reach the minimum" and "room for 2 more" are visible in the list itself
  // rather than only in the counter above it.
  for (let n = appState.newsArticles.length + 1; n <= APP.MAX_ARTICLES; n++) {
    container.insertAdjacentHTML('beforeend', articleSlotHtml(n));
  }

  // Show/hide next button based on article count
  if (appState.newsArticles.length >= 3 && appState.newsArticles.length <= 5) {
    nextButton.classList.remove('hidden');
  } else {
    nextButton.classList.add('hidden');
  }

  // Sync inline date status badge
  syncStatusBadge();
}

// Sync the topbar stat with whatever the current view reports on.
function syncStatusBadge() {
  updateTopbarStat(ViewManager.getCurrentView());
}

// Escape HTML to prevent XSS and broken attributes
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// Copy Original Content Functions
window.copyOriginalContent = async function (articleIndex, field) {
  const article = appState.newsArticles[articleIndex];
  let content = '';
  let type = '';

  if (field === 'title') {
    content = article.title;
    type = 'Title';
  } else if (field === 'description') {
    content = article.description || '';
    type = 'Description';
  } else if (field === 'source') {
    content = article.source;
    type = 'Source';
  } else if (field === 'link') {
    content = article.link || '';
    type = 'Link';
  }

  if (content) {
    await handleCopyContent(content, type);
  }
};

// Copy Translation Content Functions
window.copyTranslationContent = async function (articleIndex, language, field) {
  const translation = appState.translations[articleIndex][language];
  let content = '';
  let type = '';

  if (field === 'title') {
    content = translation.title;
    type = 'Translated Title';
  } else if (field === 'description') {
    content = translation.description || '';
    type = 'Translated Description';
  } else if (field === 'source') {
    content = translation.source;
    type = 'Translated Source';
  }

  if (content) {
    await handleCopyContent(content, type);
  }
};

// Copy to Clipboard Helper Function
async function handleCopyContent(content, type) {
  try {
    await navigator.clipboard.writeText(content);
    toast.success(`${type} copied to clipboard!`);
  } catch (error) {
    console.error('Failed to copy:', error);
    toast.error(`Failed to copy ${type.toLowerCase()}`);
  }
}

// Event delegation for all article actions
document.getElementById('articlesContainer').addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-action]');
  if (!btn) return;

  const action = btn.dataset.action;
  const index = parseInt(btn.dataset.index, 10);

  if (action === 'delete') {
    await handleDeleteArticle(index);
  } else if (action === 'edit') {
    handleEditArticle(index);
  } else if (action === 'cancel-edit') {
    updateArticlesList();
  } else if (action === 'save-edit') {
    await handleSaveEdit(index);
  } else if (action === 'move-up') {
    handleMoveArticle(index, -1);
  } else if (action === 'move-down') {
    handleMoveArticle(index, 1);
  } else if (action === 'copy-title') {
    await handleCopyContent(appState.newsArticles[index].title, 'Title');
  } else if (action === 'copy-description') {
    await handleCopyContent(appState.newsArticles[index].description || '', 'Description');
  } else if (action === 'copy-source') {
    await handleCopyContent(appState.newsArticles[index].source, 'Source');
  } else if (action === 'copy-link') {
    await handleCopyContent(appState.newsArticles[index].link, 'Link');
  } else if (action === 'translate') {
    openArticleTranslateView(index);
  }
});

// Move Article (reorder locally, DB sync on submission)
function handleMoveArticle(index, direction) {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= appState.newsArticles.length) return;

  // Swap articles in the array
  const temp = appState.newsArticles[index];
  appState.newsArticles[index] = appState.newsArticles[newIndex];
  appState.newsArticles[newIndex] = temp;

  updateArticlesList();
}

// Delete Article
async function handleDeleteArticle(index) {
  const article = appState.newsArticles[index];
  const isFromDB = article._dbData && appState.hasExistingData;

  // Ask for confirmation — destructive for DB articles, warning for unsaved
  const confirmed = await showConfirmModal({
    title: isFromDB ? 'Delete from Database?' : 'Remove Article?',
    message: isFromDB
      ? `Delete "${article.title}" from the database? This cannot be undone.`
      : `Remove "${article.title}" from the list?`,
    confirmText: isFromDB ? 'Delete' : 'Remove',
    variant: isFromDB ? 'destructive' : 'warning'
  });
  if (!confirmed) return;

  appState.newsArticles.splice(index, 1);
  updateArticlesList();

  // Sync to Firestore if this was DB data
  if (isFromDB && db) {
    try {
      if (appState.newsArticles.filter(a => a._dbData).length === 0) {
        // No DB articles left — delete the entire document from both collections
        await deleteDoc(doc(db, 'news_snapshots', appState.selectedDate));
        
        // Also remove news from morning_ready/{date}
        const morningReadyRef = doc(db, 'morning_ready', appState.selectedDate);
        const morningSnap = await getDoc(morningReadyRef);
        if (morningSnap.exists()) {
          await updateDoc(morningReadyRef, {
            'news': []
          });
        }
        
        appState.hasExistingData = false;
        syncStatusBadge();
        // Only the news snapshot is removed here — Gift Nifty, IPL and the
        // good-morning images for this date are untouched. "All data" read as
        // though the whole day had been wiped.
        toast.success(`Deleted "${article.title}". No articles are left for ${formatDateForDisplay(appState.selectedDate)}, so the news snapshot was removed. Gift Nifty, IPL and images for this date are unchanged.`, 7000);
      } else {
        // Update the document with remaining articles, preserving translations
        const newsData = appState.newsArticles.filter(a => a._dbData).map(a => {
          return { ...a._dbData, title: a.title, description: a.description || '', source: a.source, url: a.link };
        });
        
        // Update news_snapshots
        await setDoc(doc(db, 'news_snapshots', appState.selectedDate), {
          articleCount: newsData.length,
          date: appState.selectedDate,
          fetchedAt: serverTimestamp(),
          news: newsData
        });

        // Update morning_ready.news
        const morningReadyRef = doc(db, 'morning_ready', appState.selectedDate);
        const morningSnap = await getDoc(morningReadyRef);
        if (morningSnap.exists()) {
          await updateDoc(morningReadyRef, {
            'news': newsData
          });
        }
        
        markArticlesSaved();
        toast.success(`Deleted "${article.title}" from database`);
      }
    } catch (error) {
      console.error('Error deleting article from Firestore:', error);
      toast.error('Failed to delete from database. Please try again.');
      // Re-add the article since DB delete failed
      appState.newsArticles.splice(index, 0, article);
      updateArticlesList();
      return;
    }
  } else {
    toast.info(`Removed: ${article.title}`);
  }
}

// Edit Article — show inline edit form
function handleEditArticle(index) {
  const article = appState.newsArticles[index];
  const container = document.getElementById('articlesContainer');
  const articleDiv = container.querySelector(`[data-index="${index}"]`);
  if (!articleDiv) return;

  articleDiv.className = 'record record--editing';
  articleDiv.innerHTML = `
    <div class="space-y-3">
      <div class="flex items-center gap-2 mb-1">
        <span class="inline-flex items-center justify-center w-6 h-6 bg-accent1 text-white text-xs font-bold rounded-lg flex-shrink-0">${index + 1}</span>
        <span class="text-sm font-semibold text-accent1">Editing Article</span>
      </div>
      <div>
        <label class="label">Title <span class="text-red-500">*</span></label>
        <textarea data-field="title" class="input resize-none overflow-y-auto" rows="4" required>${escapeHtml(article.title)}</textarea>
      </div>
      <div>
        <label class="label">Description <span class="text-gray-500 font-normal">(optional)</span></label>
        <textarea data-field="description" class="input resize-none overflow-y-auto" rows="3">${escapeHtml(article.description || '')}</textarea>
      </div>
      <div>
        <label class="label">Source <span class="text-red-500">*</span></label>
        <input type="text" data-field="source" class="input" value="${escapeHtml(article.source)}" required>
      </div>
      <div>
        <label class="label">Link <span class="text-gray-500 font-normal">(optional)</span></label>
        <input type="url" data-field="link" class="input" value="${escapeHtml(article.link || '')}" placeholder="https://...">
      </div>
      <div class="flex gap-2 pt-1">
        <button data-action="save-edit" data-index="${index}" class="flex-1 py-2.5 px-4 bg-accent1 hover:bg-accent1-600 text-white font-semibold rounded-xl transition-all text-sm flex items-center justify-center gap-1.5">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
          Save
        </button>
        <button data-action="cancel-edit" data-index="${index}" class="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all text-sm">
          Cancel
        </button>
      </div>
    </div>
  `;

  // Focus the title input
  const titleInput = articleDiv.querySelector('[data-field="title"]');
  if (titleInput) titleInput.focus();
}

// Save Edit Handler
async function handleSaveEdit(index) {
  const container = document.getElementById('articlesContainer');
  const articleDiv = container.querySelector(`[data-index="${index}"]`);
  const titleVal = articleDiv.querySelector('[data-field="title"]').value.trim();
  const descriptionVal = articleDiv.querySelector('[data-field="description"]').value.trim();
  const sourceVal = articleDiv.querySelector('[data-field="source"]').value.trim();
  const linkVal = articleDiv.querySelector('[data-field="link"]').value.trim();

  if (!titleVal || !sourceVal) {
    toast.warning('Headline/Title and Source are required.');
    return;
  }

  const article = appState.newsArticles[index];

  // Check if translatable content (title, description, or source) actually changed
  const titleChanged = titleVal !== article.title;
  const descriptionChanged = descriptionVal !== (article.description || '');
  const sourceChanged = sourceVal !== article.source;
  const translatableContentChanged = titleChanged || descriptionChanged || sourceChanged;
  const hadTranslations = article._dbData && article._dbData.translations;

  // If translatable content changed and article had translations, warn user
  if (translatableContentChanged && hadTranslations) {
    const confirmed = await showConfirmModal({
      title: 'Re-translation needed',
      message: 'You changed the English text, so the saved translations no longer match it. Saving clears them and this article will need translating again.',
      confirmText: 'Save and clear translations',
      cancelText: 'Cancel',
      variant: 'warning'
    });
    if (!confirmed) return;
  }

  // Apply changes
  article.title = titleVal;
  article.description = descriptionVal;
  article.source = sourceVal;
  article.link = linkVal;

  // If translatable content changed, strip stale translations from _dbData
  if (translatableContentChanged && article._dbData) {
    delete article._dbData.translations;
  }

  updateArticlesList();

  // If from DB, sync the edit to Firestore
  if (article._dbData && appState.hasExistingData && db) {
    try {
      const newsData = appState.newsArticles.filter(a => a._dbData).map(a => {
        return { ...a._dbData, title: a.title, description: a.description || '', source: a.source, url: a.link };
      });
      
      // Update news_snapshots
      await setDoc(doc(db, 'news_snapshots', appState.selectedDate), {
        articleCount: newsData.length,
        date: appState.selectedDate,
        fetchedAt: serverTimestamp(),
        news: newsData
      });

      // Update morning_ready.news
      const morningReadyRef = doc(db, 'morning_ready', appState.selectedDate);
      const morningSnap = await getDoc(morningReadyRef);
      if (morningSnap.exists()) {
        await updateDoc(morningReadyRef, {
          'news': newsData
        });
      }

      if (translatableContentChanged && hadTranslations) {
        toast.success('Article updated. Translations removed — please re-translate.');
      } else {
        markArticlesSaved();
        toast.success('Article updated in database');
      }
    } catch (error) {
      console.error('Error updating article in Firestore:', error);
      toast.error('Saved locally but failed to sync to database.');
    }
  } else {
    toast.success('Article updated');
  }
}

// Check if all articles have translations for given languages
function allArticlesHaveTranslations(languages) {
  return appState.newsArticles.every(article => {
    if (!article._dbData || !article._dbData.translations) return false;
    return languages.every(lang => {
      const t = article._dbData.translations[lang];
      // Title + source required; description optional
      return t && t.title && t.source;
    });
  });
}

// Pre-populate appState.translations from _dbData for all articles
function loadTranslationsFromDB(languages) {
  appState.translations = {};
  appState.newsArticles.forEach((article, i) => {
    appState.translations[i] = {};
    languages.forEach(lang => {
      if (article._dbData && article._dbData.translations && article._dbData.translations[lang]) {
        appState.translations[i][lang] = { ...article._dbData.translations[lang] };
      }
    });
  });
}

// Check how many articles need translation
function getTranslationStatus(languages) {
  let needsTranslation = 0;
  let hasTranslation = 0;
  appState.newsArticles.forEach(article => {
    const hasAll = languages.every(lang => {
      const t = article._dbData && article._dbData.translations && article._dbData.translations[lang];
      // Title + source required; description optional
      return t && t.title && t.source;
    });
    if (hasAll) hasTranslation++;
    else needsTranslation++;
  });
  return { needsTranslation, hasTranslation, total: appState.newsArticles.length };
}

// Next to Translate Button — smart routing
document.getElementById('nextToTranslateBtn').addEventListener('click', async () => {
  if (appState.newsArticles.length === 0) {
    toast.warning('Add at least 3 articles before you translate');
    return;
  }

  const defaultLanguages = ['hi', 'mr', 'gu'];
  const status = getTranslationStatus(defaultLanguages);

  if (status.needsTranslation === 0) {
    // All articles have translations — skip translate step entirely
    appState.selectedLanguages = defaultLanguages;
    appState.skippedTranslationStep = true;
    loadTranslationsFromDB(defaultLanguages);
    showReviewEditView();
    toast.success(`All ${status.total} articles already have translations. Ready to review!`);
  } else if (status.hasTranslation > 0) {
    // Some need translation — go to translate step with info
    appState.skippedTranslationStep = false;
    showView('translationOptionsView');
    toast.info(`${status.hasTranslation} of ${status.total} articles already translated. ${status.needsTranslation} need translation.`);
  } else {
    // None have translations
    appState.skippedTranslationStep = false;
    showView('translationOptionsView');
    toast.info('Select languages for translation');
  }
});

// Translation Options
document.getElementById('generateTranslationsBtn').addEventListener('click', async () => {
  const selectedLanguages = [];
  if (document.getElementById('hindiCheck').checked) selectedLanguages.push('hi');
  if (document.getElementById('marathiCheck').checked) selectedLanguages.push('mr');
  if (document.getElementById('gujaratiCheck').checked) selectedLanguages.push('gu');

  if (selectedLanguages.length === 0) {
    toast.warning('Please select at least one language');
    return;
  }

  appState.selectedLanguages = selectedLanguages;

  const btn = document.getElementById('generateTranslationsBtn');
  btn.classList.add('loading');
  btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Generating...';
  toast.info('Generating translations... This may take a moment.');

  const { reusedCount, translatedCount, failedCount } = await generateTranslations(selectedLanguages);

  btn.classList.remove('loading');
  btn.textContent = 'Generate Translations';

  // A partial failure must not read as success. When the service could not
  // translate, that language now carries the English text unchanged — usable,
  // but the operator has to know before it goes out.
  if (failedCount > 0) {
    const ok = reusedCount + translatedCount;
    toast.error(
      `${failedCount} translation${failedCount === 1 ? '' : 's'} failed and kept the English text. ` +
      `Review them before submitting.${ok > 0 ? ` ${ok} succeeded.` : ''}`,
      10000
    );
  } else if (reusedCount > 0 && translatedCount > 0) {
    toast.success(`${translatedCount} new translations generated, ${reusedCount} reused from the database.`);
  } else if (reusedCount > 0 && translatedCount === 0) {
    toast.success(`All ${reusedCount} translations reused from the database.`);
  } else {
    toast.success(`${translatedCount} translations generated.`);
  }

  showReviewEditView();
});

// Cloud Function Translation — delegates to Vertex AI (ADC) backend
async function translateArticle(title, description, source, targetLanguage) {
  try {
    const result = await cloudFunctionsClient.post('manualAdminTranslate', {
      title,
      description,
      source,
      language: targetLanguage
    });

    if (!result.success || !result.translation) {
      throw new Error(result.error || 'Translation failed');
    }

    return {
      title: result.translation.title,
      description: result.translation.description || '',
      source: result.translation.source,
      ok: true
    };
  } catch (error) {
    console.error('Cloud translation error:', error);
    // Last resort: hand back the English text unchanged rather than a
    // `[HI] …` placeholder. English on air is recoverable; a fake
    // translation that looks real is not. `ok:false` is stripped before
    // the document is written, so the stored shape is unchanged — it
    // exists only so the operator is told which languages fell back.
    return {
      title,
      description: description || '',
      source,
      ok: false,
      error: error && error.message ? error.message : 'Translation failed'
    };
  }
}

// Generate Translations for All Articles (smart: reuses existing DB translations)
async function generateTranslations(languages) {
  appState.translations = {};
  appState.translationFailures = {};
  let reusedCount = 0;
  let translatedCount = 0;
  let failedCount = 0;

  for (let i = 0; i < appState.newsArticles.length; i++) {
    const article = appState.newsArticles[i];
    appState.translations[i] = {};

    for (const lang of languages) {
      // Check if article has existing DB translations for this language
      const dbTranslations = article._dbData && article._dbData.translations;
      const existingTranslation = dbTranslations && dbTranslations[lang];

      if (existingTranslation && existingTranslation.title && existingTranslation.source) {
        // Reuse existing translation — article content hasn't changed (description optional)
        appState.translations[i][lang] = {
          title: existingTranslation.title,
          description: existingTranslation.description || '',
          source: existingTranslation.source
        };
        reusedCount++;
      } else {
        // New article or edited article — needs fresh translation
        const translated = await translateArticle(article.title, article.description || '', article.source, lang);
        // Store only the three fields the document carries; `ok`/`error`
        // stay out of Firestore.
        appState.translations[i][lang] = {
          title: translated.title,
          description: translated.description || '',
          source: translated.source
        };
        if (translated.ok === false) {
          if (!appState.translationFailures[i]) appState.translationFailures[i] = {};
          appState.translationFailures[i][lang] = translated.error;
          failedCount++;
        } else {
          translatedCount++;
        }
      }
    }
  }

  return { reusedCount, translatedCount, failedCount };
}

// Helper function to calculate text similarity
function calculateSimilarity(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

// Helper function to calculate Levenshtein distance
function levenshteinDistance(str1, str2) {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Fetch news via cloud function (Vertex AI ADC backend).
 * Delegates to manualAdminNewsFetch which handles 10-article fetch + committee eval + translation.
 * @returns {Promise<Array>} Array of articles with hi/mr/gu translations
 */
async function fetchEnglishNewsOnDemand() {
  console.log('[News Fetch] Calling cloud function for admin news fetch...');
  const result = await cloudFunctionsClient.post('manualAdminNewsFetch', {}, { date: appState.selectedDate || getISTDateString(1) });

  if (!result.success || !result.articles) {
    throw new Error(result.error || 'Failed to fetch news from cloud function');
  }

  console.log(`[News Fetch] Got ${result.articles.length} articles from cloud function`);
  return result.articles;
}

// Show Review & Edit View
function showReviewEditView() {
  const container = document.getElementById('translationsContainer');
  container.innerHTML = '';

  // Where "back" leads depends on how the operator arrived. The visible
  // control now lives in the shell, which proxies to this element, so only
  // its accessible label needs to change.
  const backBtn = document.getElementById('backToTranslationFromReview');
  const backLabel = appState.skippedTranslationStep ? 'Back to articles' : 'Back to languages';
  backBtn.setAttribute('aria-label', backLabel);
  const shellBack = document.getElementById('shellBack');
  if (shellBack) { shellBack.title = backLabel; shellBack.setAttribute('aria-label', backLabel); }

  // Reset submit button in case we're coming back from a previous submission
  resetSubmitButton();

  const languageNames = LANGUAGE_NAMES;

  appState.newsArticles.forEach((article, articleIndex) => {
    const articleSection = document.createElement('div');
    articleSection.className = 'panel';

    // ── Article panel header ─────────────────────────────────────────────
    const articleHeader = document.createElement('div');
    articleHeader.className = 'panel__head';
    articleHeader.innerHTML = `
      <div>
        <div class="panel__title">Article ${articleIndex + 1}</div>
        <div class="panel__sub">Original and translations</div>
      </div>
      <button type="button" class="btn" data-variant="ghost" data-size="sm"
        onclick="refreshSingleArticle(${articleIndex})" title="Fetch a fresh article to replace this one">
        <svg class="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M21 12a9 9 0 11-2.6-6.4L21 8"/><path d="M21 3v5h-5"/></svg>
        <span>Replace</span>
      </button>
    `;
    articleSection.appendChild(articleHeader);

    // ── Original (English) ───────────────────────────────────────────────
    const field = (label, value, action, mono) => `
      <div class="reviewfield">
        <div class="reviewfield__head">
          <span class="reviewfield__label">${label}</span>
          ${action ? `<button type="button" class="reviewfield__copy" onclick="${action}" title="Copy ${label.toLowerCase()}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy
          </button>` : ''}
        </div>
        <p class="reviewfield__value${mono ? ' u-mono' : ''}">${value}</p>
      </div>`;

    const originalCard = document.createElement('div');
    originalCard.className = 'panel__body';
    originalCard.innerHTML = `
      <div class="reviewblock">
        <div class="reviewblock__head">
          <span class="badge" data-tone="neutral">English</span>
          <span class="reviewblock__name">Original</span>
        </div>
        <div class="u-col u-col--sm">
          ${field('Title', escapeHtml(article.title), `window.copyOriginalContent(${articleIndex}, 'title')`)}
          ${article.description ? field('Description', escapeHtml(article.description), `window.copyOriginalContent(${articleIndex}, 'description')`) : ''}
          ${field('Source', escapeHtml(article.source), `window.copyOriginalContent(${articleIndex}, 'source')`)}
          ${article.link
            ? field('Link', `<a href="${escapeHtml(article.link)}" target="_blank" rel="noopener" class="u-wrap">${escapeHtml(article.link)}</a>`, `window.copyOriginalContent(${articleIndex}, 'link')`)
            : field('Link', '<span class="u-muted">No link provided</span>', null)}
        </div>
      </div>
    `;
    articleSection.appendChild(originalCard);

    const translationsGrid = document.createElement('div');
    translationsGrid.className = 'panel__body reviewgrid';

    appState.selectedLanguages.forEach(lang => {
      const t = appState.translations[articleIndex][lang];
      const fellBack = !!(appState.translationFailures[articleIndex] || {})[lang];

      const translationCard = document.createElement('div');
      translationCard.className = 'reviewblock';
      translationCard.innerHTML = `
        <div class="reviewblock__head">
          <span class="badge" data-tone="${fellBack ? 'warning' : 'neutral'}">${lang.toUpperCase()}</span>
          <span class="reviewblock__name">${languageNames[lang]}</span>
          <span class="u-spacer"></span>
          <button type="button" class="iconbtn" onclick="window.regenerateTranslation(${articleIndex}, '${lang}')" title="Regenerate this translation" aria-label="Regenerate ${languageNames[lang]}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M21 12a9 9 0 11-2.6-6.4L21 8"/><path d="M21 3v5h-5"/></svg>
          </button>
          <button type="button" class="iconbtn" onclick="window.toggleEditMode(${articleIndex}, '${lang}')" title="Edit this translation" aria-label="Edit ${languageNames[lang]}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M17 3a2.8 2.8 0 014 4L7.5 20.5 2 22l1.5-5.5z"/></svg>
          </button>
        </div>

        ${fellBack ? `<div class="banner" data-tone="warning" style="margin-bottom:var(--sp-8)">
          <svg class="banner__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg>
          <span class="banner__content"><span class="banner__title">Still in English</span><span class="banner__detail">Translation failed. Regenerate, or edit it by hand.</span></span>
        </div>` : ''}

        <div id="preview-${articleIndex}-${lang}" class="u-col u-col--sm">
          ${field('Title', escapeHtml(t.title), `window.copyTranslationContent(${articleIndex}, '${lang}', 'title')`)}
          ${t.description ? field('Description', escapeHtml(t.description), `window.copyTranslationContent(${articleIndex}, '${lang}', 'description')`) : ''}
          ${field('Source', escapeHtml(t.source), `window.copyTranslationContent(${articleIndex}, '${lang}', 'source')`)}
        </div>

        <div id="edit-${articleIndex}-${lang}" class="u-col u-col--sm hidden">
          <div class="field">
            <label class="label" data-required="true">Title</label>
            <input type="text" id="translation-${articleIndex}-${lang}-title" class="input" value="${escapeHtml(t.title)}" required>
          </div>
          <div class="field">
            <label class="label">Description</label>
            <textarea id="translation-${articleIndex}-${lang}-description" class="textarea" rows="3">${escapeHtml(t.description || '')}</textarea>
          </div>
          <div class="field">
            <label class="label" data-required="true">Source</label>
            <input type="text" id="translation-${articleIndex}-${lang}-source" class="input" value="${escapeHtml(t.source)}" required>
          </div>
          <button type="button" class="btn" data-variant="primary" data-size="sm" data-block="true" onclick="window.saveTranslation(${articleIndex}, '${lang}')">
            <svg class="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>
            <span>Save changes</span>
          </button>
        </div>
      `;
      translationsGrid.appendChild(translationCard);
    });

    articleSection.appendChild(translationsGrid);
    container.appendChild(articleSection);
  });

  showView('reviewEditView');
}

// Regenerate Translation via Cloud Function (Vertex AI ADC)
window.regenerateTranslation = async function (articleIndex, language) {
  const article = appState.newsArticles[articleIndex];

  // Show loading state
  const previewDiv = document.getElementById(`preview-${articleIndex}-${language}`);
  const originalContent = previewDiv.innerHTML;
  previewDiv.innerHTML = `
    <div class="flex items-center justify-center py-8">
      <div class="spinner inline-block mr-2"></div>
      <span class="text-gray-600">Regenerating translation...</span>
    </div>
  `;

  try {
    const result = await cloudFunctionsClient.post('manualAdminTranslate', {
      title: article.title,
      description: article.description || '',
      source: article.source,
      language
    });

    if (!result.success || !result.translation) {
      throw new Error(result.error || 'Translation failed');
    }

    const translated = result.translation;

    // Update state
    appState.translations[articleIndex][language] = {
      title: translated.title,
      description: translated.description || '',
      source: translated.source
    };

    // Update preview display
    previewDiv.innerHTML = `
      <div class="bg-white rounded-xl p-4">
        <p class="text-xs font-semibold text-gray-600 mb-1.5">Title</p>
        <p class="text-gray-800 font-semibold">${translated.title}</p>
      </div>
      ${translated.description ? `<div class="bg-white rounded-xl p-4">
        <p class="text-xs font-semibold text-gray-600 mb-1.5">Description</p>
        <p class="text-gray-700 text-sm leading-snug">${escapeHtml(translated.description)}</p>
      </div>` : ''}
      <div class="bg-white rounded-xl p-4">
        <p class="text-xs font-semibold text-gray-600 mb-1.5">Source</p>
        <p class="text-gray-800 font-medium">${translated.source}</p>
      </div>
    `;

    toast.success('Translation regenerated successfully!');

    // Re-enable submit button so user can resubmit with new translation
    resetSubmitButton();

    // Update edit inputs if in edit mode
    const titleInput = document.getElementById(`translation-${articleIndex}-${language}-title`);
    const descriptionInput = document.getElementById(`translation-${articleIndex}-${language}-description`);
    const sourceInput = document.getElementById(`translation-${articleIndex}-${language}-source`);
    if (titleInput) titleInput.value = translated.title;
    if (descriptionInput) descriptionInput.value = translated.description || '';
    if (sourceInput) sourceInput.value = translated.source;

  } catch (error) {
    console.error('Regeneration error:', error);
    // Restore original content on error
    previewDiv.innerHTML = originalContent;
    toast.error(`Failed to regenerate translation. Please try again or edit manually.`);
  }
};

// Toggle Edit Mode
window.toggleEditMode = function (articleIndex, language) {
  const previewDiv = document.getElementById(`preview-${articleIndex}-${language}`);
  const editDiv = document.getElementById(`edit-${articleIndex}-${language}`);

  if (previewDiv.classList.contains('hidden')) {
    previewDiv.classList.remove('hidden');
    editDiv.classList.add('hidden');
  } else {
    previewDiv.classList.add('hidden');
    editDiv.classList.remove('hidden');

    const titleInput = document.getElementById(`translation-${articleIndex}-${language}-title`);
    if (titleInput) titleInput.focus();
  }
};

// Save Translation
window.saveTranslation = function (articleIndex, language) {
  const titleInput = document.getElementById(`translation-${articleIndex}-${language}-title`);
  const descriptionInput = document.getElementById(`translation-${articleIndex}-${language}-description`);
  const sourceInput = document.getElementById(`translation-${articleIndex}-${language}-source`);

  const newTitle = (titleInput ? titleInput.value : appState.translations[articleIndex][language].title || '').trim();
  const newDescription = (descriptionInput ? descriptionInput.value : (appState.translations[articleIndex][language].description || '')).trim();
  const newSource = (sourceInput ? sourceInput.value : appState.translations[articleIndex][language].source || '').trim();

  if (!newTitle || !newSource) {
    toast.warning('Title and Source are required.');
    return;
  }

  appState.translations[articleIndex][language].title = newTitle;
  appState.translations[articleIndex][language].description = newDescription || '';
  appState.translations[articleIndex][language].source = newSource;

  const previewDiv = document.getElementById(`preview-${articleIndex}-${language}`);
  previewDiv.innerHTML = `
    <div class="bg-white rounded-xl p-4">
      <p class="text-xs font-semibold text-gray-600 mb-1.5">Title</p>
      <p class="text-gray-800 font-semibold">${escapeHtml(newTitle)}</p>
    </div>
    ${newDescription ? `<div class="bg-white rounded-xl p-4">
      <p class="text-xs font-semibold text-gray-600 mb-1.5">Description</p>
      <p class="text-gray-700 text-sm leading-snug">${escapeHtml(newDescription)}</p>
    </div>` : ''}
    <div class="bg-white rounded-xl p-4">
      <p class="text-xs font-semibold text-gray-600 mb-1.5">Source</p>
      <p class="text-gray-800 font-medium">${escapeHtml(newSource)}</p>
    </div>
  `;

  window.toggleEditMode(articleIndex, language);

  // Re-enable submit button so user can resubmit with edited translation
  resetSubmitButton();

  toast.success('Translation saved successfully!');
};

// Confirm & Submit
document.getElementById('confirmSubmitBtn').addEventListener('click', async () => {
  // Collect any in-progress edits
  appState.newsArticles.forEach((article, articleIndex) => {
    appState.selectedLanguages.forEach(lang => {
      const titleInput = document.getElementById(`translation-${articleIndex}-${lang}-title`);
      const descriptionInput = document.getElementById(`translation-${articleIndex}-${lang}-description`);
      const sourceInput = document.getElementById(`translation-${articleIndex}-${lang}-source`);
      if (titleInput) appState.translations[articleIndex][lang].title = titleInput.value;
      if (descriptionInput) appState.translations[articleIndex][lang].description = descriptionInput.value;
      if (sourceInput) appState.translations[articleIndex][lang].source = sourceInput.value;
    });
  });

  // Last gate before this reaches air: if any language fell back to the
  // English text, say so plainly and make the operator choose.
  const fallbackList = [];
  Object.keys(appState.translationFailures || {}).forEach(idx => {
    const langs = Object.keys(appState.translationFailures[idx] || {});
    if (!langs.length) return;
    const article = appState.newsArticles[idx];
    if (!article) return;
    fallbackList.push(`Article ${Number(idx) + 1} — ${langs.map(l => LANGUAGE_NAMES[l] || l.toUpperCase()).join(', ')}`);
  });

  if (fallbackList.length > 0) {
    const confirmed = await showConfirmModal({
      title: 'Some translations are still in English',
      message:
        `${fallbackList.length} article${fallbackList.length === 1 ? '' : 's'} could not be translated and kept the English text:\n\n` +
        `${fallbackList.join('\n')}\n\n` +
        'Submitting publishes them in English for those languages.',
      confirmText: 'Publish anyway',
      cancelText: 'Go back and retry',
      variant: 'warning'
    });
    if (!confirmed) return;
  }

  // Overwrite confirmation if data already existed for this date
  if (appState.hasExistingData) {
    const confirmed = await showConfirmModal({
      title: 'Replace published articles?',
      message: `${formatDateForDisplay(appState.selectedDate)} already has published articles. Submitting replaces them with the ${appState.newsArticles.length} on this screen.`,
      confirmText: `Publish for ${formatDateForDisplay(appState.selectedDate)}`,
      cancelText: 'Cancel',
      variant: 'warning'
    });
    if (!confirmed) return;
  }

  const btn = document.getElementById('confirmSubmitBtn');
  btn.classList.add('loading');
  btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Submitting...';

  // Merge over whatever is already stored rather than replacing it. The
  // language picker chooses what to *generate*; a language left unchecked
  // was previously dropped from the document on submit, silently deleting
  // that translation for every article.
  const newsData = appState.newsArticles.map((article, index) => {
    const stored = (article._dbData && article._dbData.translations) || {};
    const generated = appState.translations[index] || {};
    return {
      source: article.source,
      title: article.title,
      description: article.description || '',
      url: article.link,
      translations: { ...stored, ...generated }
    };
  });

  // Use the user-selected date
  const dateString = appState.selectedDate;

  const submissionData = {
    articleCount: newsData.length,
    date: dateString,
    fetchedAt: serverTimestamp(),
    news: newsData
  };

  try {
    if (db) {
      // ── 1. Write to news_snapshots/{date} ────────────────────────────────
      await setDoc(doc(db, 'news_snapshots', dateString), submissionData);

      // ── 2. Write to morning_ready/{date}.news ───────────────────────────────
      // Uses dot-notation via updateDoc so only this sub-field is patched.
      // Falls back to setDoc with merge if the doc doesn't exist yet.
      const morningReadyRef = doc(db, 'morning_ready', dateString);
      const morningSnap = await getDoc(morningReadyRef);

      if (morningSnap.exists()) {
        await updateDoc(morningReadyRef, {
          'news': newsData
        });
      } else {
        // morning_ready doc doesn't exist yet — create with minimal structure
        await setDoc(morningReadyRef, {
          date: dateString,
          news: newsData
        }, { merge: true });
      }

      toast.success(`🎉 ${newsData.length} articles submitted for ${formatDateForDisplay(dateString)}! Both news_snapshots & morning_ready updated.`);
    } else {
      console.log('Mock submission data:', submissionData);
      toast.success(`✅ ${newsData.length} articles submitted for ${formatDateForDisplay(dateString)} (mock mode)`);
    }

    // Mark data as existing now (it's been saved)
    appState.hasExistingData = true;
    // Update _dbData on each article to reflect what's now in DB
    appState.newsArticles.forEach((article, i) => {
      article._dbData = { ...newsData[i] };
    });

    // Show success state on the button with options
    btn.classList.remove('loading');
    appState.submittedForDate = dateString;
    markArticlesSaved();
    syncStepGating();

    // Painting the confirmation is not part of saving. Anything that goes
    // wrong here is a UI fault and must not be reported as data loss — the
    // articles are already in Firestore by this point.
    try {
      showSubmissionSummary({
        task: 'articles',
        date: dateString,
        summary: `${newsData.length} article${newsData.length === 1 ? '' : 's'}, in broadcast order.`,
        rows: newsData.map(a => ({ primary: a.title, secondary: a.source })),
      });
    } catch (uiError) {
      console.error('Submitted, but could not render the confirmation page:', uiError);
    }

  } catch (error) {
    console.error('Error submitting to Firestore:', error);
    toast.error('Error submitting data. Please check console for details.');
    btn.classList.remove('loading');
    btn.innerHTML = `<span class="flex items-center justify-center gap-2">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>
      </svg>
      Confirm & Submit
    </span>`;
  }
});

/* ---------- Navigation guards -------------------------------------------------
   Each task page declares whether it is holding work the database does not have
   and what to do about it. The shell asks once and jumps straight to where the
   operator clicked, instead of walking up one Back button at a time — which
   asked twice on the way out of Review and never reached the Workspace at all
   from a nested page.

   Where a page has a primary save, that save is offered: choosing it finishes
   the task and lands on its confirmation page. Where a page has no way to
   persist what it holds (a half-built article list cannot be saved without
   translating and submitting it), only leaving and staying are offered — a
   Save button that cannot run is worse than none. */

/** Does the IPL form hold a result or a fixture with something in it? */
function iplFormHasContent() {
  const filled = (sel) => Array.from(document.querySelectorAll(sel))
    .some(el => (el.value || '').trim() !== '');
  return filled('#iplResultsContainer input, #iplResultsContainer select, #iplResultsContainer textarea')
    || filled('#iplFixturesContainer input, #iplFixturesContainer select, #iplFixturesContainer textarea');
}

/** Run a page's own submit, then wait for it to report itself clean. */
async function runPrimarySave(buttonId, isStillDirty) {
  const btn = document.getElementById(buttonId);
  if (!btn) return false;
  btn.click();
  // The handler is async and owns all the validation; watch its effect rather
  // than duplicating any of it here.
  for (let waited = 0; waited < 60000; waited += 80) {
    await new Promise(r => setTimeout(r, 80));
    if (!isStillDirty()) return true;
  }
  return false;
}

function leaveDataEntry() {
  appState.newsArticles = [];
  appState.translations = {};
  appState.hasExistingData = false;
  markArticlesSaved();
  updateArticlesList();
  document.getElementById('articleForm')?.reset();
  resetDateSelectionView();
}

function leaveReviewEdit() {
  appState.translations = {};
  resetSubmitButton();
}

function giftNiftyFormValues() {
  return ['gnLastPrice', 'gnDayChange', 'gnDayChangePerc']
    .map(id => (document.getElementById(id)?.value || '').trim());
}

function registerNavGuards() {
  registerGuard('dataEntryView', {
    isDirty: hasUnsavedArticleChanges,
    prompt: () => ({
      title: 'Leave without saving your changes?',
      message: describeUnsavedArticles(),
      discardText: 'Discard changes and leave',
    }),
    leave: leaveDataEntry,
  });

  registerGuard('reviewEditView', {
    // Not "are there translations?" — a date that was merely opened has those,
    // hydrated from the database. Only ones the database does not have.
    isDirty: () => appState.submittedForDate !== appState.selectedDate
      && (countUnsavedTranslations() > 0 || hasUnsavedArticleChanges()),
    prompt: () => {
      const n = countUnsavedTranslations();
      return {
        title: 'Leave without submitting?',
        message: (n
          ? `Translations for ${n} article${n === 1 ? '' : 's'} were generated in this session and are not saved. `
          : 'Changes made in this session are not saved. ')
          + 'Leaving discards them. Nothing is removed from the database, and this date stays unpublished.',
        discardText: 'Discard and leave',
      };
    },
    leave: leaveReviewEdit,
  });

  registerGuard('articleTranslateView', {
    // Each translation is saved explicitly on this screen, so there is nothing
    // held here that leaving would lose.
    isDirty: () => false,
    prompt: () => ({ title: '', message: '', discardText: '' }),
    leave: () => { _atvCurrentIndex = null; updateArticlesList(); },
  });

  registerGuard('giftNiftyView', {
    isDirty: () => giftNiftyFormValues().some(Boolean),
    prompt: () => ({
      title: 'Save the Gift Nifty override first?',
      message: 'The values typed here are not in the database yet. Saving stores them for this date; '
        + 'leaving clears the form and keeps whatever is already stored.',
      saveText: 'Save and finish',
      discardText: 'Leave without saving',
    }),
    save: () => runPrimarySave('giftNiftySubmitBtn', () => giftNiftyFormValues().some(Boolean)),
    leave: () => { resetGiftNiftyPanel(); resetDateSelectionView(); },
  });

  registerGuard('iplView', {
    isDirty: () => iplFormHasContent(),
    prompt: () => ({
      title: 'Save the IPL snapshot first?',
      message: 'The results and fixtures on this screen are not in the database yet. Saving stores them '
        + 'for this date; leaving keeps whatever snapshot is already stored.',
      saveText: 'Save and finish',
      discardText: 'Leave without saving',
    }),
    save: () => runPrimarySave('iplSubmitBtn', () => iplFormHasContent()),
  });

  registerGuard('gmImagesView', {
    isDirty: () => LANGUAGES_GM.some(lang => gmImagesState.hasPendingUpload(lang)),
    prompt: () => ({
      title: 'Upload the chosen images first?',
      message: 'Images picked on this screen have not been uploaded. Uploading stores them for this date; '
        + 'leaving discards the selection and keeps whatever is already stored.',
      saveText: 'Upload and finish',
      discardText: 'Leave without uploading',
    }),
    save: () => runPrimarySave('updateGmBtn',
      () => LANGUAGES_GM.some(lang => gmImagesState.hasPendingUpload(lang))),
  });
}

/* ---------- Submission confirmation ------------------------------------------
   All four feeders end the same way: state what reached the database, then
   offer only what follows from that. Previously each one left the operator on
   its working page with a greened-out Submit button and a Sign out beneath it —
   controls for a task that was already over. */

/** What the last completed submission was, so "Edit this date" knows where to go. */
let _lastSubmission = null;

const SUBMIT_META = {
  articles:  { title: 'News published',     panel: 'Articles published' },
  giftNifty: { title: 'Gift Nifty saved',   panel: 'Values stored' },
  ipl:       { title: 'IPL snapshot saved', panel: 'Snapshot contents' },
  gm:        { title: 'Images uploaded',    panel: 'Images stored' },
};

/**
 * Show the confirmation page.
 *
 * @param {object} opts
 * @param {'articles'|'giftNifty'|'ipl'|'gm'} opts.task which feeder finished
 * @param {string} opts.date the publication date it was saved against
 * @param {string} opts.summary one line naming what happened
 * @param {Array<{primary: string, secondary?: string, tone?: string}>} opts.rows
 *        the items that were saved, in the order they were saved
 */
function showSubmissionSummary({ task, date, summary, rows = [] }) {
  const meta = SUBMIT_META[task] || { title: 'Saved', panel: 'What was saved' };
  _lastSubmission = { task, date };

  const set = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };
  set('submittedTitle', meta.title);
  set('submittedDesc', `${formatDateForDisplay(date)} — this is now what will go out at 07:30 IST.`);
  set('submittedPanelTitle', meta.panel);
  set('submittedPanelSub', summary || '');

  const list = document.getElementById('submittedList');
  if (list) {
    list.innerHTML = rows.map(({ primary, secondary, tone }) => `
      <li class="done__item"${tone ? ` data-tone="${tone}"` : ''}>
        <span class="done__item-main">${escapeHtml(primary)}</span>
        ${secondary ? `<span class="done__item-sub">${escapeHtml(secondary)}</span>` : ''}
      </li>`).join('');
    // A numbered list only reads as one where order carries meaning.
    list.classList.toggle('done__list--ordered', task === 'articles');
  }

  showView('submittedView');
}

document.getElementById('submittedHomeBtn')?.addEventListener('click', () => {
  resetSubmitButton();
  initDateSelection(appState.selectedDate || getISTDateString(1));
  showView('dateSelectionView');
});

document.getElementById('submittedEditBtn')?.addEventListener('click', () => {
  if (!_lastSubmission) { showView('dateSelectionView'); return; }
  const back = {
    articles:  'dataEntryView',
    giftNifty: 'giftNiftyView',
    ipl:       'iplView',
    gm:        'gmImagesView',
  }[_lastSubmission.task];
  if (!back) { showView('dateSelectionView'); return; }

  // The button says edit, so it has to actually reopen: a submitted date locks
  // every pipeline step, and without clearing that flag this would land on the
  // articles page with Languages and Review greyed out and the stepper still
  // reading "Submitted".
  if (_lastSubmission.task === 'articles' && appState.submittedForDate === _lastSubmission.date) {
    appState.submittedForDate = null;
  }
  resetSubmitButton();
  showView(back);
  syncStepGating();
});

// Reset submit button to default state
function resetSubmitButton() {
  const btn = document.getElementById('confirmSubmitBtn');
  if (!btn) return;
  btn.disabled = false;
  btn.classList.remove('bg-green-700', 'border-green-800');
  btn.innerHTML = `<span class="flex items-center justify-center gap-2">
    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>
    </svg>
    Confirm & Submit
  </span>`;
}

// ============================================================================
// GIFT NIFTY OVERRIDE
// ============================================================================

// --- Load existing Gift Nifty from market_snapshots ---

/** No stored values for this date — clear the masks and hide the card. */
function hideGiftNiftyStoredCard() {
  unmask(['giftNiftyCurrentPrice', 'giftNiftyCurrentChange', 'giftNiftyCurrentPercent', 'giftNiftyCurrentSource']);
  document.getElementById('giftNiftyCurrentCard')?.classList.add('hidden');
}

async function loadExistingGiftNifty(dateString) {
  const currentCard = document.getElementById('giftNiftyCurrentCard');
  const loadingEl = document.getElementById('giftNiftyLoading');
  const priceEl = document.getElementById('giftNiftyCurrentPrice');
  const changeEl = document.getElementById('giftNiftyCurrentChange');
  const percentEl = document.getElementById('giftNiftyCurrentPercent');
  const sourceEl = document.getElementById('giftNiftyCurrentSource');

  // Show the card straight away with its figures masked, so the layout is
  // settled before the values arrive rather than shifting when they do.
  currentCard.classList.remove('hidden');
  mask([
    ['giftNiftyCurrentPrice', 'sm'],
    ['giftNiftyCurrentChange', 'sm'],
    ['giftNiftyCurrentPercent', 'sm'],
    ['giftNiftyCurrentSource', 'xs'],
  ]);
  if (loadingEl) loadingEl.classList.add('hidden');

  if (!db || !dateString) {
    if (loadingEl) loadingEl.classList.add('hidden');
    return;
  }

  try {
    const snap = await getDoc(doc(db, 'market_snapshots', dateString));
    if (snap.exists()) {
      const data = snap.data();
      const gn = data?.giftNifty?.data;
      if (gn && gn.lastPrice) {
        const isManual = gn.source === 'manual';
        priceEl.textContent = Number(gn.lastPrice).toLocaleString('en-IN', { maximumFractionDigits: 2 });

        // These three set a tone rather than a class string. Overwriting
        // className stripped `.stat__value` and `.badge`, which left the
        // stats at three different sizes and the source pill unstyled.
        const changeSign = gn.dayChange >= 0 ? '+' : '';
        changeEl.textContent = `${changeSign}${Number(gn.dayChange || 0).toFixed(2)}`;
        changeEl.closest('.stat').setAttribute('data-tone', gn.dayChange >= 0 ? 'positive' : 'negative');

        const percSign = gn.dayChangePerc >= 0 ? '+' : '';
        percentEl.textContent = `${percSign}${Number(gn.dayChangePerc || 0).toFixed(2)}%`;
        percentEl.closest('.stat').setAttribute('data-tone', gn.dayChangePerc >= 0 ? 'positive' : 'negative');

        sourceEl.textContent = isManual ? 'Manual override' : (gn.source || 'API');
        sourceEl.setAttribute('data-tone', isManual ? 'positive' : 'warning');

        unmask(['giftNiftyCurrentPrice', 'giftNiftyCurrentChange', 'giftNiftyCurrentPercent', 'giftNiftyCurrentSource']);
        currentCard.classList.remove('hidden');

        // Pre-fill form with existing values for easy editing
        document.getElementById('gnLastPrice').value = gn.lastPrice;
        document.getElementById('gnDayChange').value = gn.dayChange || 0;
        document.getElementById('gnDayChangePerc').value = gn.dayChangePerc || 0;
      } else {
        hideGiftNiftyStoredCard();
      }
    } else {
      hideGiftNiftyStoredCard();
    }
  } catch (err) {
    console.error('[Gift Nifty] Error loading existing data:', err);
    hideGiftNiftyStoredCard();
    toast.warning('Could not load existing Gift Nifty values from database.');
  } finally {
    if (loadingEl) loadingEl.classList.add('hidden');
  }
}

// --- Gift Nifty Form Submit ---

document.getElementById('giftNiftyForm').addEventListener('submit', async (e) => {
  e.preventDefault();

  const errorDiv = document.getElementById('giftNiftyFormError');
  const errorText = errorDiv.querySelector('.banner__title') || errorDiv;
  errorDiv.classList.add('hidden');

  if (!appState.selectedDate) {
    errorText.textContent = 'No date selected. Please go back and select a date first.';
    errorDiv.classList.remove('hidden');
    toast.warning('Please select a date first.');
    return;
  }

  const lastPrice = parseFloat(document.getElementById('gnLastPrice').value);
  const dayChange = parseFloat(document.getElementById('gnDayChange').value);
  const dayChangePerc = parseFloat(document.getElementById('gnDayChangePerc').value);

  // Validation
  if (isNaN(lastPrice) || lastPrice <= 0) {
    errorText.textContent = 'Last Price must be a positive number.';
    errorDiv.classList.remove('hidden');
    document.getElementById('gnLastPrice').focus();
    return;
  }
  if (isNaN(dayChange)) {
    errorText.textContent = 'Day Change value is required (can be negative).';
    errorDiv.classList.remove('hidden');
    document.getElementById('gnDayChange').focus();
    return;
  }
  if (isNaN(dayChangePerc)) {
    errorText.textContent = 'Day Change % is required (can be negative).';
    errorDiv.classList.remove('hidden');
    document.getElementById('gnDayChangePerc').focus();
    return;
  }

  const changeSign = dayChange >= 0 ? '+' : '';
  const percSign = dayChangePerc >= 0 ? '+' : '';
  const dayChangeText = `${changeSign}${dayChange.toFixed(2)} (${percSign}${dayChangePerc.toFixed(2)}%)`;

  const btn = document.getElementById('giftNiftySubmitBtn');
  btn.disabled = true;
  btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Saving...';

  try {
    if (db) {
      // ── 1. Write to market_snapshots/{date}.giftNifty.data ──────────────
      const marketSnapshotData = {
        giftNifty: {
          data: {
            symbol: 'GIFTNIFTY',
            name: 'Gift Nifty',
            lastPrice,
            dayChange,
            dayChangePerc,
            dayChangeText,
            source: 'manual',
            lastUpdated: new Date().toISOString(),
          },
          fetchedAt: serverTimestamp()
        }
      };
      await setDoc(
        doc(db, 'market_snapshots', appState.selectedDate),
        marketSnapshotData,
        { merge: true }
      );

      // ── 2. Write to morning_ready/{date}.market.giftNifty ───────────────
      // Uses dot-notation via updateDoc so only this sub-field is patched.
      // Falls back to setDoc with merge if the doc doesn't exist yet.
      const morningReadyRef = doc(db, 'morning_ready', appState.selectedDate);
      const morningSnap = await getDoc(morningReadyRef);

      const normalizedGiftNifty = {
        price: lastPrice,
        change: dayChange,
        percent: dayChangePerc,
        symbol: 'GIFTNIFTY',
        source: 'manual'
      };

      if (morningSnap.exists()) {
        await updateDoc(morningReadyRef, {
          'market.giftNifty': normalizedGiftNifty
        });
      } else {
        // morning_ready doc doesn't exist yet — create with minimal structure
        await setDoc(morningReadyRef, {
          date: appState.selectedDate,
          market: { giftNifty: normalizedGiftNifty }
        }, { merge: true });
      }

      toast.success(`Gift Nifty override saved for ${formatDateForDisplay(appState.selectedDate)}! Both market_snapshots & morning_ready updated.`);
    } else {
      // Mock mode
      console.log('[Gift Nifty Override] Mock save:', { lastPrice, dayChange, dayChangePerc });
      toast.success(`Gift Nifty override saved (mock mode) for ${formatDateForDisplay(appState.selectedDate)}`);
    }

    // Refresh the current-value display card
    await loadExistingGiftNifty(appState.selectedDate);
    btn.disabled = false;

    const fmt = (n) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
    const signed = (n) => `${Number(n) >= 0 ? '+' : ''}${fmt(n)}`;
    showSubmissionSummary({
      task: 'giftNifty',
      date: appState.selectedDate,
      summary: 'Manual override — this replaces the fetched value.',
      rows: [
        { primary: fmt(lastPrice),                  secondary: 'Last price' },
        { primary: signed(dayChange),               secondary: 'Day change' },
        { primary: `${signed(dayChangePerc)}%`,     secondary: 'Day change %' },
      ],
    });

  } catch (err) {
    console.error('[Gift Nifty Override] Error:', err);
    toast.error('Failed to save Gift Nifty override. Please try again.');
    btn.disabled = false;
    btn.innerHTML = `<span class="flex items-center justify-center gap-2">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>
      </svg>
      Save override
    </span>`;
    errorText.textContent = `Save failed: ${err.message}`;
    errorDiv.classList.remove('hidden');
  }
});

// --- Clear Gift Nifty Form ---

document.getElementById('giftNiftyClearBtn').addEventListener('click', async () => {
  const confirmed = await showConfirmModal({
    title: 'Clear the form?',
    message: 'Empties the fields on this screen. Values already saved to the database stay as they are.',
    confirmText: 'Clear form',
    cancelText: 'Cancel',
    variant: 'warning'
  });
  if (!confirmed) return;

  document.getElementById('giftNiftyForm').reset();
  document.getElementById('gnSource').value = 'manual';
  document.getElementById('giftNiftyFormError').classList.add('hidden');
  toast.info('Form cleared.');
});

// --- Fetch Gift Nifty Live Data from Cloud Function ---

document.getElementById('fetchGiftNiftyBtn').addEventListener('click', async () => {
  showFetchOverlay('Fetching Gift Nifty data...', {
    subtext: 'Scraping live values from MoneyControl / Groww',
    cancellable: true,
    spinnerColor: '#2563eb'
  });
  const signal = _fetchAbortController?.signal;

  try {
    const result = await callCloudFunction('manualDailyGiftNiftyFetch', {}, signal);

    if (result.success && result.giftNifty && result.giftNifty.success) {
      updateFetchOverlayMessage('Loading values into form...');
      const dateStr = result.date || appState.selectedDate;
      const snap = await getDoc(doc(db, 'market_snapshots', dateStr));
      if (snap.exists()) {
        const gn = snap.data()?.giftNifty?.data;
        if (gn && gn.lastPrice) {
          document.getElementById('gnLastPrice').value = gn.lastPrice;
          document.getElementById('gnDayChange').value = gn.dayChange || 0;
          document.getElementById('gnDayChangePerc').value = gn.dayChangePerc || 0;
          await loadExistingGiftNifty(dateStr);
          hideFetchOverlay();
          toast.success(`Gift Nifty fetched: ${Number(gn.lastPrice).toLocaleString('en-IN')} (${gn.source || 'API'})`);
        } else {
          hideFetchOverlay();
          toast.warning('Data fetched but Gift Nifty values not found in snapshot.');
        }
      } else {
        hideFetchOverlay();
        toast.warning('Cloud function succeeded but snapshot not found. Check date.');
      }
    } else {
      hideFetchOverlay();
      const errMsg = result.giftNifty?.error || result.error || 'Unknown error';
      toast.error(`Gift Nifty fetch failed: ${errMsg}`);
    }
  } catch (err) {
    hideFetchOverlay();
    if (err.name === 'AbortError') return; // User cancelled
    console.error('[Gift Nifty Fetch] Error:', err);
    // The override form below does not depend on the fetch, so point at it
    // rather than leaving the operator with a dead end.
    toast.error(`${err.message} You can still enter the values manually below.`, 8000);
  }
});

// --- Reset Gift Nifty panel ---

function resetGiftNiftyPanel() {
  const currentCard = document.getElementById('giftNiftyCurrentCard');
  const loadingEl = document.getElementById('giftNiftyLoading');
  if (currentCard) currentCard.classList.add('hidden');
  if (loadingEl) loadingEl.classList.add('hidden');
  document.getElementById('giftNiftyForm').reset();
  document.getElementById('gnSource').value = 'manual';
  document.getElementById('giftNiftyFormError').classList.add('hidden');
  const btn = document.getElementById('giftNiftySubmitBtn');
  btn.disabled = false;
  btn.classList.remove('bg-green-700', 'border-green-800');
  btn.innerHTML = `<span class="flex items-center justify-center gap-2">
    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>
    </svg>
    Save override
  </span>`;
}

// ============================================================================
// IPL MANAGER 
// ============================================================================

// --- Initialize Team Selects ---
function populateTeamDropdowns() {
  const selects = document.querySelectorAll('.ipl-team-select');
  selects.forEach(select => {
    // Keep initial "Select Team" option
    const firstOption = select.options[0];
    select.innerHTML = '';
    select.appendChild(firstOption);

    IPL_TEAMS.forEach(team => {
      const opt = document.createElement('option');
      opt.value = team.code;
      opt.textContent = `${team.name} (${team.code})`;
      select.appendChild(opt);
    });
  });
}

// --- Result Management ---
window.updateMomStatsVisibility = function (resultId) {
  const div = document.getElementById(`result-${resultId}`);
  if (!div) return;
  const role = div.querySelector('.ipl-res-momType').value;
  const battingStats = div.querySelector('.stats-batting');
  const bowlingStats = div.querySelector('.stats-bowling');

  if (role === 'Batter') {
    battingStats.classList.remove('hidden');
    bowlingStats.classList.add('hidden');
    // Clear bowling fields
    div.querySelector('.ipl-res-momWickets').value = '';
    div.querySelector('.ipl-res-momRunsConceded').value = '';
  } else if (role === 'Bowler') {
    battingStats.classList.add('hidden');
    bowlingStats.classList.remove('hidden');
    // Clear batting fields
    div.querySelector('.ipl-res-momRuns').value = '';
    div.querySelector('.ipl-res-momBalls').value = '';
  } else {
    battingStats.classList.remove('hidden');
    bowlingStats.classList.remove('hidden');
  }
};

window.addIplMatchResult = function (data = null) {
  const container = document.getElementById('iplResultsContainer');
  const resultId = Date.now() + Math.random().toString(36).substr(2, 5);
  const resultIndex = container.children.length + 1;

  const div = document.createElement('div');
  div.id = `result-${resultId}`;
  div.className = 'iplcard';
  div.innerHTML = resultCardHtml({ resultId, resultIndex, data, teams: IPL_TEAMS });
  container.appendChild(div);
  updateMomStatsVisibility(resultId);
  updateResultFieldsVisibility(resultId);
};

window.removeIplMatchResult = function (id) {
  const el = document.getElementById(`result-${id}`);
  if (el) el.remove();
};

// The fixture cards' delete button has always called this, but it was never
// assigned — clicking it threw ReferenceError, so fixtures could not be
// removed. Mirrors removeIplMatchResult against the `fixture-${id}` wrapper.
window.removeIplFixture = function (id) {
  const el = document.getElementById(`fixture-${id}`);
  if (el) el.remove();
};

// Function to handle visibility of result fields based on result type
function updateResultFieldsVisibility(resultId) {
  const resultTypeSelect = document.querySelector(`#result-${resultId} .ipl-res-resultType`);
  const resultType = resultTypeSelect.value;
  
  // Hide all result-specific fields
  const normalFields = document.getElementById(`normal-result-fields-${resultId}`);
  const noResultFields = document.getElementById(`no-result-fields-${resultId}`);
  const tieFields = document.getElementById(`tie-result-fields-${resultId}`);
  const superOverFields = document.getElementById(`super-over-result-fields-${resultId}`);
  const abandonedFields = document.getElementById(`abandoned-result-fields-${resultId}`);
  const teamSelectionFields = document.getElementById(`team-selection-fields-${resultId}`);
  const momSection = document.getElementById(`mom-section-${resultId}`);
  
  // Hide all fields first
  if (normalFields) normalFields.classList.add('hidden');
  if (noResultFields) noResultFields.classList.add('hidden');
  if (tieFields) tieFields.classList.add('hidden');
  if (superOverFields) superOverFields.classList.add('hidden');
  if (abandonedFields) abandonedFields.classList.add('hidden');
  if (teamSelectionFields) teamSelectionFields.classList.add('hidden');
  
  // Show relevant fields based on result type
  switch (resultType) {
    case 'normal':
      if (normalFields) normalFields.classList.remove('hidden');
      if (momSection) momSection.classList.remove('hidden');
      // Don't show team selection - winner/loser already covers it
      break;
    case 'no-result':
      if (noResultFields) noResultFields.classList.remove('hidden');
      if (teamSelectionFields) teamSelectionFields.classList.remove('hidden');
      if (momSection) momSection.classList.add('hidden'); // Hide MOM for no-result
      break;
    case 'tie':
      if (tieFields) tieFields.classList.remove('hidden');
      if (teamSelectionFields) teamSelectionFields.classList.remove('hidden');
      if (momSection) momSection.classList.remove('hidden');
      break;
    case 'super-over':
      if (superOverFields) superOverFields.classList.remove('hidden');
      if (momSection) momSection.classList.remove('hidden');
      // Don't show team selection - super over winner/loser already covers it
      break;
    case 'abandoned':
    case 'forfeit':
      if (abandonedFields) abandonedFields.classList.remove('hidden');
      if (teamSelectionFields) teamSelectionFields.classList.remove('hidden');
      if (momSection) momSection.classList.add('hidden'); // Hide MOM for abandoned/forfeit
      break;
    default:
      if (normalFields) normalFields.classList.remove('hidden');
      if (momSection) momSection.classList.remove('hidden');
  }
  
  // Update team labels dynamically
  updateTeamLabels(resultId);
};

// Function to update team labels based on selected teams
function updateTeamLabels(resultId) {
  const team1Select = document.querySelector(`#result-${resultId} .ipl-res-team1`);
  const team2Select = document.querySelector(`#result-${resultId} .ipl-res-team2`);
  
  const team1Name = team1Select.options[team1Select.selectedIndex]?.text || 'Team 1';
  const team2Name = team2Select.options[team2Select.selectedIndex]?.text || 'Team 2';
  
  // Update labels in different sections
  const team1Label = document.getElementById(`team1-label-${resultId}`);
  const team2Label = document.getElementById(`team2-label-${resultId}`);
  const tieTeam1Label = document.getElementById(`tie-team1-label-${resultId}`);
  const tieTeam2Label = document.getElementById(`tie-team2-label-${resultId}`);
  const superTeam1Label = document.getElementById(`super-team1-label-${resultId}`);
  
  if (team1Label) team1Label.textContent = `👕 ${team1Name}`;
  if (team2Label) team2Label.textContent = `👕 ${team2Name}`;
  if (tieTeam1Label) tieTeam1Label.textContent = team1Name;
  if (tieTeam2Label) tieTeam2Label.textContent = team2Name;
  if (superTeam1Label) superTeam1Label.textContent = team1Name;
};

// Make functions globally accessible for onclick handlers
window.updateResultFieldsVisibility = updateResultFieldsVisibility;
window.updateTeamLabels = updateTeamLabels;

// --- Fixture Management ---
window.addIplFixture = function (data = null) {
  const container = document.getElementById('iplFixturesContainer');
  const fixtureId = Date.now() + Math.random().toString(36).substr(2, 5);
  const fixtureIndex = container.children.length + 1;

  const div = document.createElement('div');
  div.id = `fixture-${fixtureId}`;
  div.className = 'iplcard iplcard--fixture';
  div.innerHTML = fixtureCardHtml({ fixtureId, fixtureIndex, data, teams: IPL_TEAMS });
  container.appendChild(div);
};

// --- Helper Functions to Fetch IPL Data from Firestore ---

async function fetchIplFixturesFromFirestore(dateString) {
  try {
    // Query fixtures collection for the specific date
    const fixturesQuery = query(
      collection(db, 'ipl_fixtures'),
      where('date', '==', dateString),
      orderBy('matchNumber', 'asc')
    );
    
    const querySnapshot = await getDocs(fixturesQuery);
    const fixtures = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      fixtures.push({
        matchLegend: data.matchLegend || `Match ${fixtures.length + 1}`,
        team1: data.team1 || '',
        team2: data.team2 || '',
        startTime: data.startTime || '7:30 PM IST',
        venue: data.venue || '',
        matchNumber: data.matchNumber || fixtures.length + 1
      });
    });
    
    console.log(`[Firestore] Fetched ${fixtures.length} fixtures for ${dateString}`);
    return fixtures;
  } catch (error) {
    console.error('[Firestore] Error fetching fixtures:', error);
    // An empty array is also what a date with no matches returns, so a query
    // that could not run at all used to be indistinguishable from "no games
    // today". Say which it was.
    toast.warning(`Could not read the fixture list: ${error.message}`, 7000);
    return [];
  }
}

async function fetchIplResultsFromFirestore(dateString) {
  try {
    // Get yesterday's date
    const yesterday = new Date(dateString);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toISOString().split('T')[0];
    
    // Query results collection for yesterday's date
    const resultsQuery = query(
      collection(db, 'ipl_results'),
      where('date', '==', yesterdayString),
      orderBy('matchNumber', 'asc')
    );
    
    const querySnapshot = await getDocs(resultsQuery);
    const results = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      results.push({
        matchLegend: data.matchLegend || `Match ${results.length + 1}`,
        venue: data.venue || '',
        resultType: data.resultType || 'normal',
        team1: data.team1 || '',
        team2: data.team2 || '',
        winner: data.winner || '',
        loser: data.loser || '',
        marginType: data.marginType || '',
        marginValue: data.marginValue || '',
        team1Score: data.team1Score || '',
        team2Score: data.team2Score || '',
        superOverWinner: data.superOverWinner || '',
        superOverLoser: data.superOverLoser || '',
        superOverMargin: data.superOverMargin || '',
        team1SuperOverScore: data.team1SuperOverScore || '',
        noResultReason: data.noResultReason || '',
        noResultNotes: data.noResultNotes || '',
        abandonReason: data.abandonReason || '',
        manOfTheMatch: data.manOfTheMatch || '',
        momType: data.momType || 'All-rounder',
        momStats: data.momStats || {
          runs: '',
          balls: '',
          wickets: '',
          runsConceded: ''
        }
      });
    });
    
    console.log(`[Firestore] Fetched ${results.length} results for ${yesterdayString}`);
    return results;
  } catch (error) {
    console.error('[Firestore] Error fetching results:', error);
    toast.warning(`Could not read yesterday's results: ${error.message}`, 7000);
    return [];
  }
}

// --- Load existing IPL from ipl_snapshots ---

async function loadExistingIplData(dateString) {
  if (!db || !dateString) return;

  try {
    const snap = await getDoc(doc(db, 'ipl_snapshots', dateString));
    if (snap.exists()) {
      const data = snap.data();
      appState.iplData = data;

      // Fill Yesterday's Results
      const resultsContainer = document.getElementById('iplResultsContainer');
      
      // Only clear if we're loading different data (check if current data is different)
      const currentResults = Array.from(resultsContainer.querySelectorAll('.ipl-res-matchLegend')).map(el => el.value).filter(v => v);
      const newResults = data.yesterdayResults?.map(r => r.matchLegend).filter(v => v) || [];
      const shouldClearResults = currentResults.length === 0 || 
                                JSON.stringify(currentResults.sort()) !== JSON.stringify(newResults.sort());
      
      if (shouldClearResults) {
        resultsContainer.innerHTML = '';
      }
      
      if (data.yesterdayResults && data.yesterdayResults.length > 0) {
        data.yesterdayResults.forEach(res => addIplMatchResult(res));
      } else if (data.yesterdayResult) {
        // Backwards compatibility for single result
        const oldRes = data.yesterdayResult;
        addIplMatchResult({
          ...oldRes,
          manOfTheMatch: oldRes.manOfTheMatch || oldRes.topBatter?.name || oldRes.topBowler?.name || '',
          momType: oldRes.momType || (oldRes.topBatter?.runs ? 'Batter' : 'Bowler'),
          momStats: oldRes.momStats || {
            runs: oldRes.topBatter?.runs || '',
            balls: oldRes.topBatter?.balls || '',
            wickets: oldRes.topBowler?.wickets || '',
            runsConceded: oldRes.topBowler?.runsConceded || ''
          }
        });
      } else if (shouldClearResults) {
        addIplMatchResult();
      }

      // Fill Today's Fixtures
      const fixturesContainer = document.getElementById('iplFixturesContainer');
      
      // Only clear if we're loading different data
      const currentFixtures = Array.from(fixturesContainer.querySelectorAll('.fixture-matchLegend')).map(el => el.value).filter(v => v);
      const newFixtures = data.todayFixtures?.map(f => f.matchLegend).filter(v => v) || [];
      const shouldClearFixtures = currentFixtures.length === 0 || 
                                JSON.stringify(currentFixtures.sort()) !== JSON.stringify(newFixtures.sort());
      
      if (shouldClearFixtures) {
        fixturesContainer.innerHTML = '';
      }
      
      if (data.todayFixtures && data.todayFixtures.length > 0) {
        data.todayFixtures.forEach(fix => addIplFixture(fix));
      } else {
        addIplFixture();
      }
    } else {
      // No existing data
      resetIplManagerPanel();
      
      // Show loading state and disable form
      showIplLoadingState();
      
      // Fetch IPL data directly from Firestore and prefill form
      try {
        console.log('[IPL Manager] No existing data found, fetching from Firestore...');
        
        if (!db) {
          throw new Error('Firestore not initialized');
        }
        
        // Fetch today's fixtures from Firestore
        const todayFixtures = await fetchIplFixturesFromFirestore(dateString);
        const yesterdayResults = await fetchIplResultsFromFirestore(dateString);
        
        if (todayFixtures.length > 0 || yesterdayResults.length > 0) {
          console.log(`[IPL Manager] Found ${todayFixtures.length} fixtures and ${yesterdayResults.length} results for ${dateString}`);
          
          // Only clear containers if they have default empty data, not if they already have user data
          const fixturesContainer = document.getElementById('iplFixturesContainer');
          const resultsContainer = document.getElementById('iplResultsContainer');
          
          // Check if containers have only default empty items
          const hasDefaultFixture = fixturesContainer.children.length === 1 && 
                                   !fixturesContainer.querySelector('.fixture-matchLegend')?.value;
          const hasDefaultResult = resultsContainer.children.length === 1 && 
                                 !resultsContainer.querySelector('.ipl-res-matchLegend')?.value;
          
          if (hasDefaultFixture) {
            fixturesContainer.innerHTML = '';
          }
          if (hasDefaultResult) {
            resultsContainer.innerHTML = '';
          }
          
          // Add fetched fixtures to form (only if not already present)
          todayFixtures.forEach(fixture => {
            addIplFixture(fixture);
          });
          
          // Add fetched results to form (only if not already present)
          yesterdayResults.forEach(result => {
            addIplMatchResult(result);
          });
          
          // Auto-save to Firestore so it's persisted immediately
          const iplSnapshotData = {
            date: dateString,
            yesterdayResults: yesterdayResults,
            todayFixtures: todayFixtures,
            status: 'Firestore-Fetched',
            updatedAt: serverTimestamp()
          };
          
          await setDoc(doc(db, 'ipl_snapshots', dateString), iplSnapshotData);
          console.log(`[IPL Manager] Auto-saved fetched data for ${dateString}`);

          toast.success(`Fetched ${todayFixtures.length} fixtures and ${yesterdayResults.length} results from Firestore for ${formatDateForDisplay(dateString)}!`);
          
        } else {
          console.log('[IPL Manager] No IPL data found in Firestore for selected date');
          toast.info(`Nothing stored for ${formatDateForDisplay(dateString)}. Add the matches by hand, or fetch again.`);
        }
        
      } catch (fetchErr) {
        console.error('[IPL Manager] Error fetching from Firestore:', fetchErr);
        // The warning was written twice here, which is why a failed IPL fetch
        // raised two identical toasts.
        toast.warning(fetchErr.message.includes('timeout')
          ? 'The fixture fetch timed out. You can enter the matches by hand.'
          : `Could not fetch IPL fixtures: ${fetchErr.message}`);
      } finally {
        // Whatever happened above — fixtures, none, or a thrown error — the
        // page must come out of its loading state. This was previously done
        // separately in each branch, and the branches did not agree.
        enableIplFormEditing();
      }
    }
  } catch (err) {
    console.error('[IPL Manager] Error loading existing data:', err);
    toast.warning('Could not load existing IPL values from database.');
  } finally {
    // Belt and braces: a throw before the inner try (a missing container, a
    // bad snapshot) must not leave the form disabled and shimmering.
    hideIplLoadingState();
  }
}

// --- IPL Loading State Management ---

function showIplLoadingState() {
  // Disable all form inputs
  const form = document.getElementById('iplManagerForm');
  if (form) {
    const inputs = form.querySelectorAll('input, select, button, textarea');
    inputs.forEach(input => {
      if (input.id !== 'backToDateFromIpl') { // Keep back button enabled
        input.disabled = true;
      }
    });
  }

  const resultsContainer = document.getElementById('iplResultsContainer');
  if (resultsContainer) resultsContainer.innerHTML = listSkeleton(1);

  const fixturesContainer = document.getElementById('iplFixturesContainer');
  if (fixturesContainer) {
    // Placeholder rows keep the section's height while the feed is read, so
    // the page does not collapse and then jump when fixtures arrive. These
    // are not `.iplcard`, so the positional validator ignores them.
    fixturesContainer.innerHTML = listSkeleton(2);
  }
}

/**
 * Take the page out of its loading state.
 *
 * Re-enabling the inputs is not enough: showIplLoadingState() also fills both
 * containers with skeleton rows, and a skeleton nobody removes is a page that
 * loads forever. Clearing them here means every exit from the load — data, no
 * data, or an error — leaves the page usable.
 */
function hideIplLoadingState() {
  const form = document.getElementById('iplManagerForm');
  if (form) {
    form.querySelectorAll('input, select, button, textarea')
      .forEach(input => { input.disabled = false; });
  }
  clearIplSkeletons();
}

/** Remove any skeleton rows left in the results and fixtures containers. */
function clearIplSkeletons() {
  ['iplResultsContainer', 'iplFixturesContainer'].forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.querySelectorAll('.skel-rows').forEach(node => node.remove());
  });
}

function enableIplFormEditing() {
  hideIplLoadingState();

  // This used to look for a `.spinner` before showing the empty state. The
  // loading state has been skeleton rows since the loaders were reworked, and
  // listSkeleton() never emits `.spinner` — so the condition was never true,
  // the placeholder rows were never replaced, and the page shimmered forever
  // on exactly the dates with no IPL data, which is most of the year.
  // showIplLoadingState() replaces both containers wholesale, so after the
  // skeletons come out they are empty. Leave each one with something the
  // operator can actually use: results are typed by hand, so a blank row;
  // fixtures come from a feed, so say the feed had nothing and offer both ways
  // forward.
  const resultsContainer = document.getElementById('iplResultsContainer');
  if (resultsContainer && resultsContainer.children.length === 0) {
    addIplMatchResult();
  }

  const fixturesContainer = document.getElementById('iplFixturesContainer');
  if (fixturesContainer && fixturesContainer.children.length === 0) {
    fixturesContainer.innerHTML = `
      <div class="state" data-tone="neutral" data-compact="true">
        <span class="state__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M6 4h12v5a6 6 0 01-12 0V4z"/><path d="M9 20h6M12 15v5"/></svg>
        </span>
        <p class="state__title">No fixtures found for this date</p>
        <p class="state__desc">The feed returned nothing. Add them by hand, or fetch again.</p>
        <span class="state__actions">
          <button type="button" class="btn" data-variant="primary" data-size="sm" onclick="enterIplFixturesManually()">Enter manually</button>
          <button type="button" class="btn" data-variant="secondary" data-size="sm" onclick="retryIplFetch()">Fetch again</button>
        </span>
      </div>
    `;
  }
}

function retryIplFetch() {
  if (!appState.selectedDate) return;
  
  // Show loading state again
  showIplLoadingState();
  
  // Retry the fetch
  loadExistingIplData(appState.selectedDate);
}

// Make function globally accessible for onclick handlers
window.retryIplFetch = retryIplFetch;

// Function to display inline validation errors
function displayInlineValidationErrors(errors) {
  // Clear all previous error states
  document.querySelectorAll('.validation-error-message').forEach(el => el.remove());
  // The banner is the container for those messages; leaving it behind showed
  // an empty "fix these" box on cards that were already correct.
  document.querySelectorAll('.validation-error-banner').forEach(el => el.remove());
  document.querySelectorAll('.border-red-500').forEach(el => {
    el.classList.remove('border-red-500', 'border-2');
  });
  
  let firstErrorElement = null;
  
  errors.forEach(error => {
    // Parse error message to extract index and field
    const resultMatch = error.match(/^Result (\d+):/);
    const fixtureMatch = error.match(/^Fixture (\d+):/);
    
    if (resultMatch) {
      const index = parseInt(resultMatch[1]) - 1;
      const resultEl = document.querySelectorAll('#iplResultsContainer > div.iplcard')[index];
      
      if (resultEl) {
        // Add error message to the result card
        addErrorToCard(resultEl, error, 'Result ' + (index + 1));
        
        // Highlight specific field if possible
        highlightErrorField(resultEl, error);
        
        if (!firstErrorElement) {
          firstErrorElement = resultEl;
        }
      }
    } else if (fixtureMatch) {
      const index = parseInt(fixtureMatch[1]) - 1;
      const fixtureEl = document.querySelectorAll('#iplFixturesContainer > div.iplcard')[index];
      
      if (fixtureEl) {
        // Add error message to the fixture card
        addErrorToCard(fixtureEl, error, 'Fixture ' + (index + 1));
        
        // Highlight specific field if possible
        highlightErrorField(fixtureEl, error);
        
        if (!firstErrorElement) {
          firstErrorElement = fixtureEl;
        }
      }
    }
  });
  
  // Scroll to first error
  if (firstErrorElement) {
    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// Add error message to a card
function addErrorToCard(cardElement, errorMessage, cardTitle) {
  // Check if error banner already exists
  let errorBanner = cardElement.querySelector('.validation-error-banner');
  
  if (!errorBanner) {
    errorBanner = document.createElement('div');
    errorBanner.className = 'validation-error-banner';
    errorBanner.innerHTML = `
      <svg class="banner__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>
      <div class="banner__content">
        <p class="banner__title">Fix these before saving &mdash; ${cardTitle}</p>
        <ul class="validation-error-list"></ul>
      </div>
    `;
    cardElement.insertBefore(errorBanner, cardElement.firstChild);
  }
  
  // Add error to the list
  const errorList = errorBanner.querySelector('.validation-error-list');
  const errorItem = document.createElement('li');
  errorItem.className = 'validation-error-message';
  // Remove the "Result X:" or "Fixture X:" prefix from the message
  const cleanMessage = errorMessage.replace(/^(Result|Fixture) \d+:\s*/, '');
  errorItem.textContent = '• ' + cleanMessage;
  errorList.appendChild(errorItem);
}

// Highlight specific error field
function highlightErrorField(cardElement, errorMessage) {
  const fieldMap = {
    'Match Legend': ['.ipl-res-matchLegend', '.fixture-matchLegend'],
    'Venue': ['.ipl-res-venue', '.fixture-venue'],
    'Team 1': ['.ipl-res-team1', '.fixture-team1'],
    'Team 2': ['.ipl-res-team2', '.fixture-team2'],
    'Winning Team': ['.ipl-res-winner'],
    'Losing Team': ['.ipl-res-loser'],
    'Margin Type': ['.ipl-res-marginType'],
    'Margin Value': ['.ipl-res-marginValue'],
    'Team 1 Score': ['.ipl-res-team1Score'],
    'Team 2 Score': ['.ipl-res-team2Score'],
    'Super Over Winner': ['.ipl-res-superOverWinner'],
    'Super Over Loser': ['.ipl-res-superOverLoser'],
    'Super Over Margin': ['.ipl-res-superOverMargin'],
    'No Result Reason': ['.ipl-res-noResultReason'],
    'Reason': ['.ipl-res-abandonReason'],
    'Start Time': ['.fixture-time'],
    'Player type': ['.ipl-res-momType'],
    'MOM Runs': ['.ipl-res-momRuns'],
    'MOM Balls': ['.ipl-res-momBalls'],
    'MOM Wickets': ['.ipl-res-momWickets'],
    'MOM Runs Conceded': ['.ipl-res-momRunsConceded']
  };
  
  // Find which field the error is about
  for (const [fieldName, selectors] of Object.entries(fieldMap)) {
    if (errorMessage.includes(fieldName)) {
      for (const selector of selectors) {
        const field = cardElement.querySelector(selector);
        if (field) {
          field.classList.add('border-red-500', 'border-2');
          break;
        }
      }
      break;
    }
  }
}

function enterIplFixturesManually() {
  hideIplLoadingState();
  
  // Clear the error message and show normal fixture form
  const fixturesContainer = document.getElementById('iplFixturesContainer');
  if (fixturesContainer) {
    fixturesContainer.innerHTML = '';
    addIplFixture();
  }
  
  // Add empty result if results container is empty
  const resultsContainer = document.getElementById('iplResultsContainer');
  if (resultsContainer && resultsContainer.children.length === 0) {
    addIplMatchResult();
  }
  
  toast.info('You can now enter IPL fixtures manually');
}

// Make function globally accessible for onclick handlers
window.enterIplFixturesManually = enterIplFixturesManually;


// --- IPL Form Submit ---

document.getElementById('iplManagerForm').addEventListener('submit', async (e) => {
  e.preventDefault();

  if (!appState.selectedDate) {
    toast.warning('Please select a date first.');
    return;
  }

  const btn = document.getElementById('iplSubmitBtn');
  btn.disabled = true;
  btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Validating...';

  // === VALIDATION SECTION ===
  const validationErrors = [];
  
  // Validate yesterday results
  const resultEls = document.querySelectorAll('#iplResultsContainer > div.iplcard');
  resultEls.forEach((el, index) => {
    const matchLegend = el.querySelector('.ipl-res-matchLegend')?.value || '';
    const venue = el.querySelector('.ipl-res-venue')?.value || '';
    const resultType = el.querySelector('.ipl-res-resultType')?.value || '';
    
    // Basic required field validation
    if (!matchLegend.trim()) {
      validationErrors.push(`Result ${index + 1}: Match Legend is required`);
    }
    if (!venue.trim()) {
      validationErrors.push(`Result ${index + 1}: Venue is required`);
    }
    if (!resultType) {
      validationErrors.push(`Result ${index + 1}: Result Type is required`);
    }
    
    // Team validation for result types that need teams
    if (['no-result', 'tie', 'abandoned', 'forfeit'].includes(resultType)) {
      const team1 = el.querySelector('.ipl-res-team1')?.value || '';
      const team2 = el.querySelector('.ipl-res-team2')?.value || '';
      
      if (!team1) {
        validationErrors.push(`Result ${index + 1}: Team 1 is required for ${resultType}`);
      }
      if (!team2) {
        validationErrors.push(`Result ${index + 1}: Team 2 is required for ${resultType}`);
      }
      if (team1 && team2 && team1 === team2) {
        validationErrors.push(`Result ${index + 1}: Team 1 and Team 2 cannot be the same`);
      }
    }
    
    // Normal result validation
    if (resultType === 'normal') {
      const winner = el.querySelector('.ipl-res-winner')?.value || '';
      const loser = el.querySelector('.ipl-res-loser')?.value || '';
      const marginType = el.querySelector('.ipl-res-marginType')?.value || '';
      const marginValue = el.querySelector('.ipl-res-marginValue')?.value || '';
      
      if (!winner) {
        validationErrors.push(`Result ${index + 1}: Winning Team is required for normal result`);
      }
      if (!loser) {
        validationErrors.push(`Result ${index + 1}: Losing Team is required for normal result`);
      }
      if (winner && loser && winner === loser) {
        validationErrors.push(`Result ${index + 1}: Winner and Loser cannot be the same team`);
      }
      if (!marginType) {
        validationErrors.push(`Result ${index + 1}: Margin Type is required for normal result`);
      }
      if (!marginValue.trim()) {
        validationErrors.push(`Result ${index + 1}: Margin Value is required for normal result`);
      }
      if (marginValue && !/^\d+$/.test(marginValue.trim())) {
        validationErrors.push(`Result ${index + 1}: Margin Value must be a positive number`);
      }
    }
    
    // Tie result validation
    if (resultType === 'tie') {
      const team1Score = el.querySelector('.ipl-res-team1Score')?.value || '';
      const team2Score = el.querySelector('.ipl-res-team2Score')?.value || '';
      
      if (!team1Score.trim()) {
        validationErrors.push(`Result ${index + 1}: Team 1 Score is required for tie`);
      }
      if (!team2Score.trim()) {
        validationErrors.push(`Result ${index + 1}: Team 2 Score is required for tie`);
      }
      // Validate score format (e.g., "180/8" or "180")
      if (team1Score && !/^\d+(\/\d+)?$/.test(team1Score.trim())) {
        validationErrors.push(`Result ${index + 1}: Team 1 Score format is invalid (use format like "180/8" or "180")`);
      }
      if (team2Score && !/^\d+(\/\d+)?$/.test(team2Score.trim())) {
        validationErrors.push(`Result ${index + 1}: Team 2 Score format is invalid (use format like "180/8" or "180")`);
      }
    }
    
    // Super over validation
    if (resultType === 'super-over') {
      const superOverWinner = el.querySelector('.ipl-res-superOverWinner')?.value || '';
      const superOverLoser = el.querySelector('.ipl-res-superOverLoser')?.value || '';
      const superOverMargin = el.querySelector('.ipl-res-superOverMargin')?.value || '';
      const team1SuperOverScore = el.querySelector('.ipl-res-team1SuperOverScore')?.value || '';
      
      if (!superOverWinner) {
        validationErrors.push(`Result ${index + 1}: Super Over Winner is required`);
      }
      if (!superOverLoser) {
        validationErrors.push(`Result ${index + 1}: Super Over Loser is required`);
      }
      if (superOverWinner && superOverLoser && superOverWinner === superOverLoser) {
        validationErrors.push(`Result ${index + 1}: Super Over Winner and Loser cannot be the same team`);
      }
      if (!superOverMargin.trim()) {
        validationErrors.push(`Result ${index + 1}: Super Over Margin is required`);
      }
      if (team1SuperOverScore && !/^\d+(\/\d+)?$/.test(team1SuperOverScore.trim())) {
        validationErrors.push(`Result ${index + 1}: Super Over Score format is invalid (use format like "15/1" or "15")`);
      }
    }
    
    // No result validation
    if (resultType === 'no-result') {
      const noResultReason = el.querySelector('.ipl-res-noResultReason')?.value || '';
      
      if (!noResultReason) {
        validationErrors.push(`Result ${index + 1}: No Result Reason is required`);
      }
    }
    
    // Abandoned/Forfeit validation
    if (['abandoned', 'forfeit'].includes(resultType)) {
      const abandonReason = el.querySelector('.ipl-res-abandonReason')?.value || '';
      
      if (!abandonReason.trim()) {
        validationErrors.push(`Result ${index + 1}: Reason is required for ${resultType}`);
      }
      if (abandonReason && abandonReason.trim().length < 10) {
        validationErrors.push(`Result ${index + 1}: Please provide a detailed reason (minimum 10 characters)`);
      }
    }
    
    // Man of the Match validation (skip for no-result, abandoned, forfeit)
    if (!['no-result', 'abandoned', 'forfeit'].includes(resultType)) {
      const manOfTheMatch = el.querySelector('.ipl-res-mom')?.value || '';
      const momType = el.querySelector('.ipl-res-momType')?.value || '';
      const momRuns = el.querySelector('.ipl-res-momRuns')?.value || '';
      const momBalls = el.querySelector('.ipl-res-momBalls')?.value || '';
      const momWickets = el.querySelector('.ipl-res-momWickets')?.value || '';
      const momRunsConceded = el.querySelector('.ipl-res-momRunsConceded')?.value || '';
      
      if (manOfTheMatch && !momType) {
        validationErrors.push(`Result ${index + 1}: Player type is required when Man of the Match is provided`);
      }
      
      // Validate numeric fields for MOM stats
      if (momRuns && !/^\d+\*?$/.test(momRuns)) {
        validationErrors.push(`Result ${index + 1}: MOM Runs must be a number (optionally followed by * for not out)`);
      }
      if (momBalls && !/^\d+$/.test(momBalls)) {
        validationErrors.push(`Result ${index + 1}: MOM Balls must be a number`);
      }
      if (momWickets && !/^\d+$/.test(momWickets)) {
        validationErrors.push(`Result ${index + 1}: MOM Wickets must be a number`);
      }
      if (momRunsConceded && !/^\d+$/.test(momRunsConceded)) {
        validationErrors.push(`Result ${index + 1}: MOM Runs Conceded must be a number`);
      }
    }
  });
  
  // Validate today's fixtures
  const fixtureEls = document.querySelectorAll('#iplFixturesContainer > div.iplcard');
  fixtureEls.forEach((el, index) => {
    const matchLegend = el.querySelector('.fixture-matchLegend')?.value || '';
    const team1 = el.querySelector('.fixture-team1')?.value || '';
    const team2 = el.querySelector('.fixture-team2')?.value || '';
    const startTime = el.querySelector('.fixture-time')?.value || '';
    const venue = el.querySelector('.fixture-venue')?.value || '';
    
    // Basic required field validation
    if (!matchLegend.trim()) {
      validationErrors.push(`Fixture ${index + 1}: Match Legend is required`);
    }
    if (!team1) {
      validationErrors.push(`Fixture ${index + 1}: Team 1 is required`);
    }
    if (!team2) {
      validationErrors.push(`Fixture ${index + 1}: Team 2 is required`);
    }
    if (team1 && team2 && team1 === team2) {
      validationErrors.push(`Fixture ${index + 1}: Team 1 and Team 2 cannot be the same`);
    }
    if (!startTime.trim()) {
      validationErrors.push(`Fixture ${index + 1}: Start Time is required`);
    }
    if (!venue.trim()) {
      validationErrors.push(`Fixture ${index + 1}: Venue is required`);
    }
  });
  
  // Check if there are any validation errors
  if (validationErrors.length > 0) {
    btn.disabled = false;
    btn.innerHTML = `<span class="flex items-center justify-center gap-2">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
      </svg>
      Save IPL snapshot
    </span>`;
    
    // Show validation errors
    console.error('[IPL Manager] Validation Errors:', validationErrors);
    
    // Display inline validation errors on fields
    displayInlineValidationErrors(validationErrors);
    
    // Show summary toast
    // toast.error's second argument is the duration in ms. Passing an object
    // made `duration > 0` false, so this toast never dismissed itself.
    toast.error(
      `${validationErrors.length} field${validationErrors.length > 1 ? 's need' : ' needs'} attention. The highlighted fields are marked below.`,
      6000
    );
    
    return;
  }
  
  // Update button text after validation passes
  btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Saving Snapshot...';

  // Collect Data
  const yesterdayResults = [];
  resultEls.forEach(el => {
    const matchLegend = el.querySelector('.ipl-res-matchLegend').value;
    const venue = el.querySelector('.ipl-res-venue').value;
    const resultType = el.querySelector('.ipl-res-resultType').value;
    const team1Select = el.querySelector('.ipl-res-team1');
    const team2Select = el.querySelector('.ipl-res-team2');
    const team1 = team1Select ? team1Select.value : '';
    const team2 = team2Select ? team2Select.value : '';
    const manOfTheMatch = el.querySelector('.ipl-res-mom').value;
    const momType = el.querySelector('.ipl-res-momType').value;
    const momStats = {
      runs: el.querySelector('.ipl-res-momRuns').value,
      balls: el.querySelector('.ipl-res-momBalls').value,
      wickets: el.querySelector('.ipl-res-momWickets').value,
      runsConceded: el.querySelector('.ipl-res-momRunsConceded').value
    };

    // Base result data
    const resultData = {
      matchLegend,
      venue,
      resultType,
      manOfTheMatch,
      momType,
      momStats
    };

    // Add team data only if available
    if (team1) resultData.team1 = team1;
    if (team2) resultData.team2 = team2;

    // Collect data based on result type
    switch (resultType) {
      case 'normal':
        const winner = el.querySelector('.ipl-res-winner')?.value || '';
        const loser = el.querySelector('.ipl-res-loser')?.value || '';
        const marginType = el.querySelector('.ipl-res-marginType')?.value || '';
        const marginValue = el.querySelector('.ipl-res-marginValue')?.value || '';
        resultData.winner = winner;
        resultData.loser = loser;
        resultData.marginType = marginType;
        resultData.marginValue = marginValue;
        break;

      case 'no-result':
        const noResultReason = el.querySelector('.ipl-res-noResultReason')?.value || '';
        const noResultNotes = el.querySelector('.ipl-res-noResultNotes')?.value || '';
        resultData.noResultReason = noResultReason;
        resultData.noResultNotes = noResultNotes;
        break;

      case 'tie':
        const team1Score = el.querySelector('.ipl-res-team1Score')?.value || '';
        const team2Score = el.querySelector('.ipl-res-team2Score')?.value || '';
        resultData.team1Score = team1Score;
        resultData.team2Score = team2Score;
        break;

      case 'super-over':
        const superOverWinner = el.querySelector('.ipl-res-superOverWinner')?.value || '';
        const superOverLoser = el.querySelector('.ipl-res-superOverLoser')?.value || '';
        const superOverMargin = el.querySelector('.ipl-res-superOverMargin')?.value || '';
        const team1SuperOverScore = el.querySelector('.ipl-res-team1SuperOverScore')?.value || '';
        resultData.superOverWinner = superOverWinner;
        resultData.superOverLoser = superOverLoser;
        resultData.superOverMargin = superOverMargin;
        resultData.team1SuperOverScore = team1SuperOverScore;
        break;

      case 'abandoned':
      case 'forfeit':
        const abandonReason = el.querySelector('.ipl-res-abandonReason')?.value || '';
        resultData.abandonReason = abandonReason;
        break;

      default:
        // Fallback to normal result handling
        const fallbackWinner = el.querySelector('.ipl-res-winner')?.value || '';
        const fallbackLoser = el.querySelector('.ipl-res-loser')?.value || '';
        const fallbackMarginType = el.querySelector('.ipl-res-marginType')?.value || '';
        const fallbackMarginValue = el.querySelector('.ipl-res-marginValue')?.value || '';
        resultData.winner = fallbackWinner;
        resultData.loser = fallbackLoser;
        resultData.marginType = fallbackMarginType;
        resultData.marginValue = fallbackMarginValue;
    }

    // Only include results that have meaningful data
    const hasRequiredFields = 
      (resultType === 'normal' && (resultData.winner || resultData.loser)) ||
      (resultType === 'no-result' && (resultData.team1 || resultData.team2 || resultData.noResultReason)) ||
      (resultType === 'tie' && (resultData.team1 || resultData.team2 || resultData.team1Score || resultData.team2Score)) ||
      (resultType === 'super-over' && (resultData.superOverWinner || resultData.superOverLoser)) ||
      ((resultType === 'abandoned' || resultType === 'forfeit') && (resultData.team1 || resultData.team2 || resultData.abandonReason));

    if (matchLegend || venue || resultType || manOfTheMatch || hasRequiredFields) {
      yesterdayResults.push(resultData);
    }
  });

  const todayFixtures = [];
  fixtureEls.forEach(el => {
    const matchLegend = el.querySelector('.fixture-matchLegend').value;
    const team1 = el.querySelector('.fixture-team1').value;
    const team2 = el.querySelector('.fixture-team2').value;
    const startTime = el.querySelector('.fixture-time').value;
    const venue = el.querySelector('.fixture-venue').value;

    if (team1 || team2 || matchLegend) {
      todayFixtures.push({ matchLegend, team1, team2, startTime, venue });
    }
  });

  const iplSnapshotData = {
    date: appState.selectedDate,
    yesterdayResults,
    todayFixtures,
    status: 'Manually Edited',
    updatedAt: serverTimestamp()
  };

  try {
    if (db) {
      // 1. Save to ipl_snapshots
      await setDoc(doc(db, 'ipl_snapshots', appState.selectedDate), iplSnapshotData);

      // 2. Trigger morning compile (mock/implied)
      // Here we might update a 'morning_ready' flag or similar if needed.
      // Based on UX plan: "Trigger morningDataCompile for ipl field"

      toast.success(`IPL Snapshot saved for ${formatDateForDisplay(appState.selectedDate)}!`);
    } else {
      console.log('[IPL Manager] Mock save:', iplSnapshotData);
      toast.success(`IPL Snapshot saved (mock mode) for ${formatDateForDisplay(appState.selectedDate)}`);
    }

    btn.disabled = false;

    const versus = (m) => [m.team1, m.team2].filter(Boolean).join(' v ') || 'Match';
    showSubmissionSummary({
      task: 'ipl',
      date: appState.selectedDate,
      summary: `${yesterdayResults.length} result${yesterdayResults.length === 1 ? '' : 's'}`
        + ` and ${todayFixtures.length} fixture${todayFixtures.length === 1 ? '' : 's'}.`,
      rows: [
        ...yesterdayResults.map(r => ({
          primary: versus(r),
          secondary: [r.matchLegend, 'Result'].filter(Boolean).join(' · '),
        })),
        ...todayFixtures.map(f => ({
          primary: versus(f),
          secondary: [f.matchLegend, f.startTime, f.venue].filter(Boolean).join(' · ') || 'Fixture',
        })),
      ],
    });

  } catch (err) {
    console.error('[IPL Manager] Save error:', err);
    toast.error('Failed to save IPL snapshot.');
    btn.disabled = false;
    btn.innerHTML = 'Save IPL snapshot';
  }
});

// --- Clear IPL Form ---

document.getElementById('iplClearBtn').addEventListener('click', async () => {
  const confirmed = await showConfirmModal({
    title: 'Clear all results and fixtures?',
    message: 'Everything on this screen is removed and replaced with one empty result and one empty fixture. The IPL snapshot already saved for this date stays in the database until you save again.',
    confirmText: 'Clear form',
    cancelText: 'Cancel',
    variant: 'warning'
  });
  if (!confirmed) return;

  document.getElementById('iplManagerForm').reset();
  document.getElementById('iplResultsContainer').innerHTML = '';
  document.getElementById('iplFixturesContainer').innerHTML = '';
  addIplMatchResult();
  addIplFixture();
  toast.info('Form cleared.');
});

// --- Reset IPL Manager panel ---

function resetIplManagerPanel() {
  document.getElementById('iplManagerForm').reset();
  document.getElementById('iplResultsContainer').innerHTML = '';
  document.getElementById('iplFixturesContainer').innerHTML = '';
  addIplMatchResult();
  addIplFixture();
  populateTeamDropdowns();

  const btn = document.getElementById('iplSubmitBtn');
  btn.disabled = false;
  btn.classList.remove('bg-green-700', 'border-green-800');
  btn.innerHTML = `<span class="flex items-center justify-center gap-2">
    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
    </svg>
    Save IPL snapshot
  </span>`;
}

// Back to Date from IPL
document.getElementById('backToDateFromIpl').addEventListener('click', () => {
  // showView refreshes the workspace on the way in; this was the only handler
  // that refetched, which is why the other three went stale.
  showView('dateSelectionView');
});

// Sign out from IPL
// Was calling resetApp() only, which clears local state but leaves the
// Firebase session active — the button said Sign Out and did not sign out.
document.getElementById('signOutIplView').addEventListener('click', signOut);

// Add result button
document.getElementById('addIplResultBtn').addEventListener('click', () => {
  addIplMatchResult();
});

// Add fixture button
document.getElementById('addIplFixtureBtn').addEventListener('click', () => {
  addIplFixture();
});

// --- Fetch IPL Data from Cloud Function ---

document.getElementById('fetchIplDataBtn').addEventListener('click', async () => {
  const dateStr = appState.selectedDate;

  if (!dateStr) {
    toast.warning('Please select a date first.');
    return;
  }

  showFetchOverlay('Fetching IPL data...', {
    subtext: 'Downloading results & fixtures from IPL S3 feed',
    cancellable: true,
    spinnerColor: '#ea580c'
  });
  const signal = _fetchAbortController?.signal;

  try {
    // Call both results (yesterday) and fixtures (today) in parallel
    const [resultsData, fixturesData] = await Promise.allSettled([
      callCloudFunction('manualIPLResults', { date: dateStr }, signal),
      callCloudFunction('manualIPLFixtures', { date: dateStr }, signal)
    ]);

    const resultsSummary = resultsData.status === 'fulfilled'
      ? `${resultsData.value.resultsCount || 0} results`
      : 'results failed';
    const fixturesSummary = fixturesData.status === 'fulfilled'
      ? `${fixturesData.value.fixturesCount || 0} fixtures`
      : 'fixtures failed';

    console.log(`[IPL Fetch] Results: ${resultsSummary}, Fixtures: ${fixturesSummary}`);

    updateFetchOverlayMessage('Loading into form...', `${resultsSummary}, ${fixturesSummary}`);

    // Reload the form from Firestore (the cloud functions stored the data)
    resetIplManagerPanel();
    await loadExistingIplData(dateStr);

    hideFetchOverlay();

    const anySuccess = resultsData.status === 'fulfilled' || fixturesData.status === 'fulfilled';
    if (anySuccess) {
      toast.success(`IPL data fetched: ${resultsSummary}, ${fixturesSummary}`);
    } else {
      toast.error('Both IPL result and fixture fetches failed. Check console for details.');
    }
  } catch (err) {
    hideFetchOverlay();
    if (err.name === 'AbortError') return; // User cancelled
    console.error('[IPL Fetch] Error:', err);
    toast.error(`IPL fetch failed: ${err.message}`);
  }
});

// --- Reset App
function resetApp() {
  appState = {
    isAuthenticated: false,
    selectedDate: '',
    hasExistingData: false,
    newsArticles: [],
    currentArticle: {
      title: '',
      source: '',
      link: ''
    },
    translations: {},
    submittedForDate: null,
    selectedLanguages: [LANGUAGES.HINDI, LANGUAGES.MARATHI, LANGUAGES.GUJARATI],
    skippedTranslationStep: false,
    iplData: {
      yesterdayResults: [],
      todayFixtures: [],
      status: 'Fetched'
    }
  };

  // Clear login credentials from DOM
  document.getElementById('username').value = '';
  document.getElementById('password').value = '';
  document.getElementById('loginForm').reset();

  document.getElementById('articleForm').reset();
  updateArticlesList();

  document.getElementById('hindiCheck').checked = true;
  document.getElementById('marathiCheck').checked = true;
  document.getElementById('gujaratiCheck').checked = true;

  resetGiftNiftyPanel();
  resetIplManagerPanel();

  // Reset date badges
  document.querySelectorAll('.dateBadgeText').forEach(el => {
    el.textContent = '';
  });

  showView('loginView');
}

// ============================================================================
// NEWS PREVIEW MODAL FUNCTIONS
// ============================================================================

// Global state for news modal
let fetchedNewsArticles = [];
let selectedNewsArticles = new Set();

// Show news preview modal
function showNewsPreviewModal() {
  const modal = document.getElementById('newsPreviewModal');
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';
  
  // Reset state
  fetchedNewsArticles = [];
  selectedNewsArticles.clear();
  updateSelectedCount();
  
  // Show loading state
  showNewsLoadingState();
  
  // Start fetching news
  fetchAndDisplayNews();
}

// Hide news preview modal
function hideNewsPreviewModal() {
  const modal = document.getElementById('newsPreviewModal');
  modal.classList.add('hidden');
  document.body.style.overflow = 'auto';
  
  // Reset state
  fetchedNewsArticles = [];
  selectedNewsArticles.clear();
}

// Show loading state
function showNewsLoadingState() {
  document.getElementById('newsLoadingState').classList.remove('hidden');
  document.getElementById('newsArticlesList').classList.add('hidden');
  document.getElementById('newsErrorState').classList.add('hidden');
}

// Show error state
function showNewsErrorState(errorMessage) {
  document.getElementById('newsLoadingState').classList.add('hidden');
  document.getElementById('newsArticlesList').classList.add('hidden');
  document.getElementById('newsErrorState').classList.remove('hidden');
  
  // Enhanced error message with time window info
  const enhancedMessage = errorMessage.includes('No articles found') 
    ? `Couldn't find enough recent news. Tried: 6h → 7h → 8h → 12h windows. Please try again later or add articles manually.`
    : errorMessage;
    
  document.getElementById('newsErrorMessage').textContent = enhancedMessage;
}

// Show articles list
function showArticlesList() {
  document.getElementById('newsLoadingState').classList.add('hidden');
  document.getElementById('newsErrorState').classList.add('hidden');
  document.getElementById('newsArticlesList').classList.remove('hidden');
}

// Fetch and display news
async function fetchAndDisplayNews() {
  try {
    console.log('[News Modal] Starting news fetch...');
    const articles = await fetchEnglishNewsOnDemand();
    
    if (!articles || articles.length === 0) {
      throw new Error('No articles found. Please try again.');
    }
    
    fetchedNewsArticles = articles;
    displayNewsArticles(articles);
    showArticlesList();
    
    console.log(`[News Modal] Successfully fetched ${articles.length} articles`);
    
  } catch (error) {
    console.error('[News Modal] Error fetching news:', error);
    showNewsErrorState(error.message || 'Failed to fetch news. Please try again.');
  }
}

// Display news articles in the modal
function displayNewsArticles(articles) {
  // Own the backing array here. Selection resolves picks via
  // fetchedNewsArticles[index]; the refresh path used to re-render without
  // reassigning it, so choosing "article 3" from a refreshed list added the
  // previous article 3. Rendering and the array can no longer disagree.
  fetchedNewsArticles = articles;

  const container = document.getElementById('newsArticlesList');
  container.innerHTML = '';

  articles.forEach((article, index) => {
    const articleElement = createNewsArticleElement(article, index);
    container.appendChild(articleElement);
  });
}

// Create article element for the modal
function createNewsArticleElement(article, index) {
  const articleDiv = document.createElement('div');
  articleDiv.className = 'news-article-card record';

  const description = article.description
    ? (article.description.length > 200 ? article.description.slice(0, 200) + '…' : article.description)
    : '';

  const sourceDisplay = article.source || 'Unknown source';

  // Relevance is a judgement the fetch returns; express it as a tone rather
  // than a bespoke colour pair per band.
  const score = article.relevance_score || 5;
  const relevance = score >= 8 ? { tone: 'positive', label: 'High' }
                  : score >= 6 ? { tone: 'info',     label: 'Good' }
                  : score >= 4 ? { tone: 'warning',  label: 'Medium' }
                               : { tone: 'neutral',  label: 'Low' };

  articleDiv.innerHTML = `
    <div class="record__head">
      <label class="check" style="margin-top:1px" title="Select this article">
        <input type="checkbox" id="article-${index}" class="news-article-checkbox" data-article-index="${index}">
      </label>
      <span class="record__num">${index + 1}</span>
      <div class="record__body">
        <p class="record__title">${escapeHtml(article.title || 'No title available')}</p>
        ${description ? `<p class="record__desc">${escapeHtml(description)}</p>` : ''}
      </div>
    </div>

    <div class="record__foot">
      <span class="record__meta" title="${escapeHtml(sourceDisplay)}">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M4 22h16a2 2 0 002-2V4a2 2 0 00-2-2H8a2 2 0 00-2 2v16a2 2 0 01-2 2zm0 0a2 2 0 01-2-2v-9h4"/></svg>
        <span>${escapeHtml(sourceDisplay)}</span>
      </span>

      ${article.published_time ? `<span class="record__meta">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
        <span>${escapeHtml(article.published_time)}</span>
      </span>` : ''}

      <span class="u-spacer"></span>

      <span class="badge" data-tone="${relevance.tone}" title="Relevance score from the fetch">
        ${relevance.label} · ${score}/10
      </span>

      ${article.url ? `<a href="${escapeHtml(article.url)}" target="_blank" rel="noopener" class="record__meta" style="color:var(--info-ink)" title="Open the full article">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><path d="M15 3h6v6"/><path d="M10 14L21 3"/></svg>
        <span>Read</span>
      </a>` : ''}
    </div>
  `;

  const checkbox = articleDiv.querySelector('.news-article-checkbox');
  checkbox.addEventListener('change', (e) => handleArticleSelection(index, e.target.checked));

  return articleDiv;
}

function handleArticleSelection(index, isSelected) {
  if (isSelected) {
    selectedNewsArticles.add(index);
  } else {
    selectedNewsArticles.delete(index);
  }
  
  updateSelectedCount();
  updateAddButtonState();
}

// Update selected count display
function updateSelectedCount() {
  const countElement = document.getElementById('selectedCount');
  countElement.textContent = selectedNewsArticles.size;
}

// Update add button state
function updateAddButtonState() {
  const addButton = document.getElementById('addSelectedArticlesBtn');
  const count = selectedNewsArticles.size;
  
  if (count >= 1 && count <= 5) {
    addButton.disabled = false;
  } else {
    addButton.disabled = true;
  }
}

// Add selected articles to the main article list
function addSelectedArticlesToMain() {
  const selectedArticles = Array.from(selectedNewsArticles).map(index => {
    const article = fetchedNewsArticles[index];
    return {
      title: article.title,
      description: article.description,
      source: article.source,
      link: article.url,
      isFetched: true
    };
  });
  
  // Add each selected article to the main articles array
  selectedArticles.forEach(article => {
    if (appState.newsArticles.length < APP.MAX_ARTICLES) {
      appState.newsArticles.push(article);
    }
  });
  
  // Update the articles list display
  updateArticlesList();
  
  // Show success message
  toast.success(`Added ${selectedArticles.length} articles successfully!`);
  
  // Close modal
  hideNewsPreviewModal();
}

// ============================================================================
// NEWS MODAL EVENT LISTENERS
// ============================================================================

// Fetch news button click
document.getElementById('fetchNewsBtn').addEventListener('click', () => {
  showNewsPreviewModal();
});

// Close modal buttons
document.getElementById('closeNewsPreviewBtn').addEventListener('click', hideNewsPreviewModal);
document.getElementById('cancelNewsBtn').addEventListener('click', hideNewsPreviewModal);

// Refresh modal button
document.getElementById('refreshNewsModalBtn').addEventListener('click', async () => {
  toast.info('Refreshing news articles...');
  
  try {
    // Fetch fresh news
    const freshArticles = await fetchEnglishNewsOnDemand();
    
    if (freshArticles && freshArticles.length > 0) {
      // Update the modal with fresh articles
      displayNewsArticles(freshArticles);
      
      // Reset selection
      selectedNewsArticles.clear();
      updateSelectedCount();
      
      toast.success(`Refreshed! Found ${freshArticles.length} fresh articles`);
    } else {
      toast.warning('No fresh articles found');
    }
  } catch (error) {
    console.error('Error refreshing modal articles:', error);
    toast.error('Failed to refresh articles');
  }
});

// Retry fetch button
document.getElementById('retryFetchBtn').addEventListener('click', () => {
  showNewsLoadingState();
  fetchAndDisplayNews();
});

// Add selected articles button
document.getElementById('addSelectedArticlesBtn').addEventListener('click', addSelectedArticlesToMain);

// Close modal on escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    const modal = document.getElementById('newsPreviewModal');
    if (!modal.classList.contains('hidden')) {
      hideNewsPreviewModal();
    }
  }
});

// Close modal on backdrop click
document.getElementById('newsPreviewModal').addEventListener('click', (e) => {
  if (e.target.id === 'newsPreviewModal') {
    hideNewsPreviewModal();
  }
});

// Refresh all articles button
document.getElementById('refreshAllArticlesBtn').addEventListener('click', async () => {
  if (!appState.newsArticles || appState.newsArticles.length === 0) {
    toast.warning('No articles to refresh');
    return;
  }

  // This discards every curated article and every translation in one click,
  // and the control sits in the page header. It needs to say so first.
  const count = appState.newsArticles.length;
  const translated = Object.keys(appState.translations || {}).length;
  const confirmed = await showConfirmModal({
    title: 'Replace all articles?',
    message:
      `This fetches a fresh set of articles and discards the ${count} currently on this screen` +
      `${translated > 0 ? `, along with the translations generated for ${translated} of them` : ''}. ` +
      'Anything already submitted for this date stays in the database until you submit again.',
    confirmText: 'Replace all',
    cancelText: 'Keep what I have',
    variant: 'destructive'
  });
  if (!confirmed) return;

  toast.info('Refreshing all articles...');
  
  try {
    // Fetch fresh news
    const freshArticles = await fetchEnglishNewsOnDemand();
    
    if (freshArticles && freshArticles.length > 0) {
      // Replace current articles with fresh ones (map backend url → frontend link)
      appState.newsArticles = freshArticles.map(a => ({
        title: a.title,
        description: a.description || '',
        source: a.source,
        link: a.url || '',
        isFetched: true
      }));
      appState.translations = {}; // clear old translations

      // Update the UI - re-render the entire review view
      showReviewEditView();
      
      toast.success(`Refreshed! Found ${freshArticles.length} fresh articles`);
    } else {
      toast.warning('No fresh articles found');
    }
  } catch (error) {
    console.error('Error refreshing articles:', error);
    toast.error('Failed to refresh articles');
  }
});

// Refresh single article function
window.refreshSingleArticle = async function(index) {
  if (!appState.newsArticles || index >= appState.newsArticles.length) {
    toast.error('Invalid article index');
    return;
  }
  
  const article = appState.newsArticles[index];
  console.log(`[Individual Refresh] Attempting to refresh article ${index}:`, article.title.substring(0, 50));
  toast.info(`Refreshing article: ${article.title.substring(0, 30)}...`);
  
  try {
    // Fetch fresh news
    const freshArticles = await fetchEnglishNewsOnDemand();
    
    console.log(`[Individual Refresh] Found ${freshArticles?.length || 0} fresh articles`);
    
    if (freshArticles && freshArticles.length > 0) {
      // Find a replacement article that's different from the current one
      // Use multiple criteria to find a good replacement
      const replacementArticle = freshArticles.find(fresh => {
        const titleDifferent = fresh.title !== article.title;
        const urlDifferent = fresh.url !== (article.link || article.url);
        const descriptionDifferent = fresh.description !== article.description;

        return titleDifferent && urlDifferent && descriptionDifferent;
      });

      console.log(`[Individual Refresh] Replacement found:`, replacementArticle ? replacementArticle.title.substring(0, 50) : 'None');

      if (replacementArticle) {
        // Replace the specific article (normalize url → link)
        appState.newsArticles[index] = {
          title: replacementArticle.title,
          description: replacementArticle.description || '',
          source: replacementArticle.source,
          link: replacementArticle.url || '',
          isFetched: true
        };
        // Clear translations for this article
        if (appState.translations[index]) delete appState.translations[index];

        // Update the UI - re-render the entire review view
        showReviewEditView();

        toast.success('Article refreshed successfully!');
        console.log(`[Individual Refresh] Successfully replaced article at index ${index}`);
      } else {
        // If no exact different article found, try to find any article with different title
        const fallbackReplacement = freshArticles.find(fresh => fresh.title !== article.title);

        if (fallbackReplacement) {
          appState.newsArticles[index] = {
            title: fallbackReplacement.title,
            description: fallbackReplacement.description || '',
            source: fallbackReplacement.source,
            link: fallbackReplacement.url || '',
            isFetched: true
          };
          if (appState.translations[index]) delete appState.translations[index];
          showReviewEditView();
          toast.success('Article refreshed with alternative!');
          console.log(`[Individual Refresh] Used fallback replacement for article at index ${index}`);
        } else {
          toast.warning('No different article found for replacement');
          console.log('[Individual Refresh] No suitable replacement found');
        }
      }
    } else {
      toast.warning('No fresh articles found');
      console.log('[Individual Refresh] No fresh articles returned from fetch');
    }
  } catch (error) {
    console.error('[Individual Refresh] Error refreshing single article:', error);
    toast.error('Failed to refresh article');
  }
};

// Handle GM Images form submission (original Update GM Images logic + success/failure modal)
document.getElementById('gmImagesForm')?.addEventListener('submit', async (e) => {
  e.preventDefault();

  if (gmImagesController) {
    await gmImagesController.uploadAllImages();
  }
});

// --- Re-generate GM Images via Cloud Function ---

document.getElementById('reoverlayGmImagesBtn')?.addEventListener('click', async () => {
  const dateStr = appState.selectedDate;

  if (!dateStr) {
    toast.warning('Please select a date first.');
    return;
  }

  const confirmed = await showConfirmModal({
    title: 'Re-generate AI Images?',
    message: 'Generates a new AI image and logo overlay for every language on this date. This replaces what is stored for the date, including images you uploaded by hand.',
    confirmText: 'Re-generate',
    cancelText: 'Cancel',
    variant: 'warning'
  });
  if (!confirmed) return;

  showFetchOverlay('Generating GM images...', {
    subtext: 'AI generation + logo overlay — this may take a minute',
    cancellable: true,
    spinnerColor: '#d97706'
  });
  const signal = _fetchAbortController?.signal;

  try {
    const result = await callCloudFunction('manualGmImageGeneration', { date: dateStr, force: true }, signal);

    if (result.success !== false) {
      updateFetchOverlayMessage('Reloading previews...');
      if (gmImagesController) {
        await gmImagesController.loadExistingImages(dateStr);
      }
      hideFetchOverlay();
      toast.success('GM images re-generated successfully!');
    } else {
      hideFetchOverlay();
      toast.error(`Re-generation failed: ${result.error || 'Unknown error'}`);
    }
  } catch (err) {
    hideFetchOverlay();
    if (err.name === 'AbortError') return; // User cancelled
    console.error('[GM Re-generate] Error:', err);
    toast.error(`Re-generation failed: ${err.message}`);
  }
});

// --- Personalize GM Images via Cloud Function ---

document.getElementById('personalizeGmImagesBtn')?.addEventListener('click', async () => {
  const dateStr = appState.selectedDate;

  if (!dateStr) {
    toast.warning('Please select a date first.');
    return;
  }

  const confirmed = await showConfirmModal({
    title: 'Personalize Images?',
    message: 'This will generate personalized images with user names for all languages. Images will be stored in the database for each user.',
    confirmText: 'Personalize',
    variant: 'warning'
  });
  if (!confirmed) return;

  showFetchOverlay('Personalizing GM images...', {
    subtext: 'Generating personalized images with user names — this may take a few minutes',
    cancellable: true,
    spinnerColor: '#db2777'
  });
  const signal = _fetchAbortController?.signal;

  try {
    const result = await callCloudFunction('manualPersonalizedImagePreGen', { date: dateStr, force: true }, signal);

    hideFetchOverlay();

    if (result.success !== false) {
      // Populate results panel
      document.getElementById('pgTotalUsers').textContent = result.total || 0;
      document.getElementById('pgGenerated').textContent = result.generated || 0;
      document.getElementById('pgSkipped').textContent = result.skipped || 0;
      document.getElementById('pgErrors').textContent = result.errors || 0;
      document.getElementById('pgLangList').textContent = result.availableBaseImages?.map(l => l.toUpperCase()).join(', ') || '-';

      // Show/hide error details
      const errorDetailsEl = document.getElementById('pgErrorDetails');
      const errorListEl = document.getElementById('pgErrorList');
      if (result.errorDetails && result.errorDetails.length > 0) {
        errorDetailsEl.classList.remove('hidden');
        errorListEl.innerHTML = result.errorDetails.slice(0, 5).map(e =>
          `<li>${e.phone}: ${e.error}</li>`
        ).join('');
        if (result.errorDetails.length > 5) {
          errorListEl.innerHTML += `<li>...and ${result.errorDetails.length - 5} more errors</li>`;
        }
      } else {
        errorDetailsEl.classList.add('hidden');
        errorListEl.innerHTML = '';
      }

      // Show the results panel and open the advanced tools section
      document.getElementById('personalizeResultsPanel').classList.remove('hidden');
      const advanced = document.getElementById('gmAdvancedTools');
      if (advanced) advanced.open = true;

      toast.success('GM images personalized successfully!');
    } else {
      toast.error(`Personalization failed: ${result.error || 'Unknown error'}`);
    }
  } catch (err) {
    hideFetchOverlay();
    if (err.name === 'AbortError') return; // User cancelled
    console.error('[GM Personalize] Error:', err);
    toast.error(`Personalization failed: ${err.message}`);
  }
});

// --- Apply Logo Overlay via Cloud Function ---

document.getElementById('applyLogoOverlayBtn')?.addEventListener('click', async () => {
  const dateStr = appState.selectedDate;

  if (!dateStr) {
    toast.warning('Please select a date first.');
    return;
  }

  const confirmed = await showConfirmModal({
    title: 'Apply Logo Overlay?',
    message: 'This will apply the Ghoshna logo overlay to all existing images for this date. Images will be re-uploaded with the logo applied.',
    confirmText: 'Apply Logo',
    variant: 'warning'
  });
  if (!confirmed) return;

  showFetchOverlay('Applying logo overlay...', {
    subtext: 'Adding logo to all existing images — this may take a minute',
    cancellable: true,
    spinnerColor: '#4f46e5'
  });
  const signal = _fetchAbortController?.signal;

  try {
    const result = await callCloudFunction('manualGmLogoOverlay', { date: dateStr }, signal);

    hideFetchOverlay();

    if (result.success !== false) {
      toast.success(`Logo overlay applied! Processed ${result.processed}/${result.total} images.`);
      // Reload images to show updated versions
      if (gmImagesController) {
        await gmImagesController.loadExistingImages(dateStr);
      }
    } else {
      toast.error(`Logo overlay failed: ${result.error || 'Unknown error'}`);
    }
  } catch (err) {
    hideFetchOverlay();
    if (err.name === 'AbortError') return; // User cancelled
    console.error('[GM Logo Overlay] Error:', err);
    toast.error(`Logo overlay failed: ${err.message}`);
  }
});

// Setup back button handlers for GM view
document.getElementById('backToDateFromGm')?.addEventListener('click', () => {
  showView('dateSelectionView');
});

document.getElementById('signOutGmView')?.addEventListener('click', signOut);

// Setup inline date picker for GM view
document.getElementById('gmInlineDatePicker')?.addEventListener('change', async (e) => {
  let newDate = e.target.value;
  if (!newDate) return;

  const { date: allowedDate, wasClamped } = clampToSelectableDate(newDate);
  if (wasClamped) {
    e.target.value = allowedDate;
    toast.error(`You can only go back ${MAX_PAST_REFERENCE_DAYS} days (from ${formatDateForDisplay(getEarliestSelectableDate())}).`);
    newDate = allowedDate;
  }

  if (newDate === appState.selectedDate) return;

  // Switching dates reloads every slot from the database, which discards
  // images picked but not yet uploaded. The news date picker already guards
  // this; without the same guard here the images vanish with no warning.
  const staged = gmImagesState.getStagedLanguages();
  if (staged.length > 0) {
    const names = staged.map(l => l.toUpperCase()).join(', ');
    const confirmed = await showConfirmModal({
      title: 'Discard the images you selected?',
      message:
        `You have images selected for ${names} that have not been uploaded yet. ` +
        `Switching to ${formatDateForDisplay(newDate)} discards them. ` +
        'Images already saved to the database are not affected.',
      confirmText: 'Switch date',
      cancelText: 'Stay on this date',
      variant: 'warning'
    });
    if (!confirmed) {
      e.target.value = appState.selectedDate;
      syncDateStepButtons();
      return;
    }
  }

  appState.selectedDate = newDate;
  appState.submittedForDate = null;
  updateDateBadges(newDate);
  syncDateStepButtons();
  if (gmImagesController) {
    gmImagesController.loadExistingImages(newDate);
  }
});

// Clear individual GM image
window.clearGmImage = async function(language) {
  event.stopPropagation();
  const lang = language.toLowerCase();
  if (gmImagesController) {
    await gmImagesController.clearImage(lang);
  }
};

// Expand GM image in modal
window.expandGmImage = function(language) {
  const lang = language.toLowerCase();
  if (gmImagesController) {
    gmImagesController.ui.expandImage(lang);
  }
};

// Close image modal
window.closeImageModal = function() {
  if (gmImagesController) {
    gmImagesController.ui.closeImageModal();
  }
};

// Close modal on backdrop click
document.getElementById('imagePreviewModal')?.addEventListener('click', (e) => {
  if (e.target.id === 'imagePreviewModal') {
    closeImageModal();
  }
});

// Close modal on Escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    closeImageModal();
  }
});

// ============================================================================
// PER-ARTICLE TRANSLATE VIEW (Step-wise translation per article)
// ============================================================================

let _atvCurrentIndex = null;
let _atvStep = 0; // 0 = Hindi, 1 = Marathi, 2 = Gujarati
const _atvGeneratingLangs = new Set(); // languages currently being generated

function openArticleTranslateView(index) {
  const article = appState.newsArticles[index];
  if (!article) {
    toast.error('Invalid article');
    return;
  }
  _atvCurrentIndex = index;
  _atvStep = 0;
  if (!appState.translations[index]) appState.translations[index] = {};
  renderArticleTranslateView();
  showView('articleTranslateView');
  // Make sure date badge text is set
  if (appState.selectedDate) updateDateBadges(appState.selectedDate);
}

function renderAtvStepper() {
  const el = document.getElementById('atvStepper');
  if (!el) return;
  const index = _atvCurrentIndex;
  const labels = TRANSLATE_LANGS.map(l => LANGUAGE_NAMES[l]);
  el.innerHTML = `
    <div class="flex items-center justify-between gap-2">
      ${TRANSLATE_LANGS.map((lang, i) => {
        const t = (appState.translations[index] || {})[lang];
        const done = !!(t && t.title && t.source);
        const isCurrent = i === _atvStep;
        const circleCls = isCurrent
          ? 'bg-primary text-white border-primary'
          : (done ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-500 border-gray-300');
        const labelCls = isCurrent ? 'text-primary font-bold' : (done ? 'text-green-600 font-semibold' : 'text-gray-500 font-medium');
        const inner = done && !isCurrent
          ? '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>'
          : `${i + 1}`;
        const connector = i < TRANSLATE_LANGS.length - 1
          ? `<div class="flex-1 h-0.5 ${i < _atvStep || done ? 'bg-green-400' : 'bg-gray-200'}"></div>`
          : '';
        return `
          <button type="button" onclick="window.atvGoToStep(${i})" class="flex items-center gap-2 focus:outline-none">
            <div class="w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm ${circleCls}">${inner}</div>
            <span class="text-sm ${labelCls} u-hide-sm">${labels[i]}</span>
          </button>
          ${connector}
        `;
      }).join('')}
    </div>
  `;
}

function updateAtvNavButtons() {
  const prevBtn = document.getElementById('atvPrevStepBtn');
  const nextBtn = document.getElementById('atvNextStepBtn');
  const saveBtn = document.getElementById('atvSaveArticleBtn');
  const nextLabel = document.getElementById('atvNextStepLabel');
  if (!prevBtn || !nextBtn || !saveBtn) return;

  prevBtn.disabled = _atvStep === 0;

  const isLast = _atvStep === TRANSLATE_LANGS.length - 1;
  if (isLast) {
    nextBtn.classList.add('hidden');
    saveBtn.classList.remove('hidden');
  } else {
    nextBtn.classList.remove('hidden');
    saveBtn.classList.add('hidden');
    if (nextLabel) {
      const nextLang = LANGUAGE_NAMES[TRANSLATE_LANGS[_atvStep + 1]];
      nextLabel.textContent = `Next: ${nextLang}`;
    }
  }
}

window.atvGoToStep = function(stepIndex) {
  if (stepIndex < 0 || stepIndex >= TRANSLATE_LANGS.length) return;
  _atvStep = stepIndex;
  renderArticleTranslateView();
};

function renderArticleTranslateView() {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const article = appState.newsArticles[index];
  if (!article) return;

  // Update badge
  document.getElementById('atvArticleBadge').textContent = `#${index + 1}`;

  const container = document.getElementById('atvContent');
  const languageNames = LANGUAGE_NAMES;

  const safe = (s) => escapeHtml(s || '');

  // Original card
  const originalHtml = `
    <div class="card scale-in">
      <div class="flex items-center gap-2 mb-4">
        <div class="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path>
          </svg>
        </div>
        <h3 class="font-bold text-gray-800 text-lg">Original (English)</h3>
      </div>
      <div class="space-y-3">
        <div class="bg-gray-50 rounded-xl p-4">
          <div class="flex items-center justify-between mb-1.5">
            <p class="text-xs font-semibold text-gray-600">Title</p>
            <button onclick="window.atvCopyOriginal('title')" class="text-xs text-gray-500 hover:text-accent1 flex items-center gap-1">Copy</button>
          </div>
          <p class="text-gray-800 font-semibold">${safe(article.title)}</p>
        </div>
        ${article.description ? `<div class="bg-gray-50 rounded-xl p-4">
          <div class="flex items-center justify-between mb-1.5">
            <p class="text-xs font-semibold text-gray-600">Description</p>
            <button onclick="window.atvCopyOriginal('description')" class="text-xs text-gray-500 hover:text-accent1 flex items-center gap-1">Copy</button>
          </div>
          <p class="text-gray-700 text-sm leading-snug">${safe(article.description)}</p>
        </div>` : ''}
        <div class="bg-gray-50 rounded-xl p-4">
          <div class="flex items-center justify-between mb-1.5">
            <p class="text-xs font-semibold text-gray-600">Source</p>
            <button onclick="window.atvCopyOriginal('source')" class="text-xs text-gray-500 hover:text-accent1 flex items-center gap-1">Copy</button>
          </div>
          <p class="text-gray-800 font-medium">${safe(article.source)}</p>
        </div>
        ${article.link ? `<div class="bg-gray-50 rounded-xl p-4">
          <p class="text-xs font-semibold text-gray-600 mb-1.5">Link</p>
          <a href="${safe(article.link)}" target="_blank" class="text-accent1 hover:underline break-all text-sm">${safe(article.link)}</a>
        </div>` : ''}
      </div>
    </div>`;

  // Per-language cards (only current step's language is rendered)
  const currentLang = TRANSLATE_LANGS[_atvStep];
  const langHtml = [currentLang].map(lang => {
    const t = appState.translations[index][lang];
    const done = !!(t && t.title && t.source);
    const isEditing = !!(t && t._editing);
    const isGenerating = _atvGeneratingLangs.has(lang);

    let bodyHtml;
    if (isGenerating) {
      bodyHtml = `
        <div class="bg-white rounded-xl p-6 border-2 border-dashed border-accent1-200 text-center">
          <div class="flex items-center justify-center gap-2 text-accent1 font-semibold">
            <div class="spinner inline-block"></div>
            <span>Generating ${languageNames[lang]} translation…</span>
          </div>
          <p class="text-gray-500 text-xs mt-2">This usually takes a few seconds</p>
        </div>`;
    } else if (!done) {
      bodyHtml = `
        <div class="bg-white rounded-xl p-6 border-2 border-dashed border-gray-200 text-center">
          <p class="text-gray-500 text-sm mb-3">Not translated yet</p>
          <button onclick="window.atvGenerateLang('${lang}')"
            class="inline-flex items-center gap-2 bg-accent1 hover:bg-accent1-700 text-white font-semibold px-4 py-2 rounded-lg transition-all">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
            Generate ${languageNames[lang]}
          </button>
        </div>`;
    } else if (isEditing) {
      bodyHtml = `
        <div class="space-y-3">
          <div>
            <label class="text-xs font-semibold text-gray-600 mb-1 block">Title <span class="text-red-500">*</span></label>
            <textarea id="atv-${lang}-title" class="input" rows="2" required>${safe(t.title)}</textarea>
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-600 mb-1 block">Description <span class="text-gray-500 font-normal">(optional)</span></label>
            <textarea id="atv-${lang}-description" class="input" rows="3">${safe(t.description || '')}</textarea>
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-600 mb-1 block">Source <span class="text-red-500">*</span></label>
            <input type="text" id="atv-${lang}-source" class="input" value="${safe(t.source)}" required />
          </div>
          <div class="flex gap-2">
            <button onclick="window.atvSaveEdit('${lang}')" class="btn-accent flex-1 text-sm">
              <span class="flex items-center justify-center gap-1.5">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Save
              </span>
            </button>
            <button onclick="window.atvCancelEdit('${lang}')" class="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-sm">Cancel</button>
          </div>
        </div>`;
    } else {
      bodyHtml = `
        <div class="space-y-2">
          <div class="bg-white rounded-xl p-3 border border-gray-100">
            <div class="flex items-center justify-between mb-1">
              <p class="text-xs font-semibold text-gray-600">Title</p>
              <button onclick="window.atvCopyTranslation('${lang}', 'title')" class="text-xs text-gray-500 hover:text-accent1">Copy</button>
            </div>
            <p class="text-gray-800 font-semibold text-sm">${safe(t.title)}</p>
          </div>
          ${t.description ? `<div class="bg-white rounded-xl p-3 border border-gray-100">
            <div class="flex items-center justify-between mb-1">
              <p class="text-xs font-semibold text-gray-600">Description</p>
              <button onclick="window.atvCopyTranslation('${lang}', 'description')" class="text-xs text-gray-500 hover:text-accent1">Copy</button>
            </div>
            <p class="text-gray-700 text-sm leading-snug">${safe(t.description)}</p>
          </div>` : ''}
          <div class="bg-white rounded-xl p-3 border border-gray-100">
            <div class="flex items-center justify-between mb-1">
              <p class="text-xs font-semibold text-gray-600">Source</p>
              <button onclick="window.atvCopyTranslation('${lang}', 'source')" class="text-xs text-gray-500 hover:text-accent1">Copy</button>
            </div>
            <p class="text-gray-800 font-medium text-sm">${safe(t.source)}</p>
          </div>
        </div>`;
    }

    return `
      <div class="card scale-in" id="atv-card-${lang}">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-accent1 rounded-lg flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path>
              </svg>
            </div>
            <h3 class="font-bold text-gray-800">${languageNames[lang]}</h3>
            ${done && !isEditing ? '<span class="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md border bg-green-100 text-green-700 border-green-200"><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path></svg>Translated</span>' : ''}
          </div>
          ${done && !isEditing && !isGenerating ? `<div class="flex items-center gap-1">
            <button onclick="window.atvGenerateLang('${lang}')" class="text-accent1 hover:text-accent1-700 p-2 rounded-lg hover:bg-accent1-100 transition-all" title="Regenerate translation">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </button>
            <button onclick="window.atvToggleEdit('${lang}')" class="text-primary hover:text-primary-700 p-2 rounded-lg hover:bg-primary-100 transition-all" title="Edit translation">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
          </div>` : ''}
        </div>
        ${bodyHtml}
      </div>`;
  }).join('');

  container.innerHTML = originalHtml + langHtml;

  renderAtvStepper();
  updateAtvNavButtons();
}

// Generate single-language translation for current article
window.atvGenerateLang = async function(lang) {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const article = appState.newsArticles[index];
  if (!article) return;
  if (_atvGeneratingLangs.has(lang)) return; // already in progress

  _atvGeneratingLangs.add(lang);
  renderArticleTranslateView();
  toast.info(`Generating ${LANGUAGE_NAMES[lang]} translation...`);
  try {
    const result = await translateArticle(article.title, article.description || '', article.source, lang);
    if (!appState.translations[index]) appState.translations[index] = {};
    appState.translations[index][lang] = {
      title: result.title,
      description: result.description,
      source: result.source
    };

    // translateArticle resolves even on failure, so success has to be read
    // from the result rather than assumed from reaching this line.
    if (!appState.translationFailures[index]) appState.translationFailures[index] = {};
    if (result.ok === false) {
      appState.translationFailures[index][lang] = result.error;
      toast.error(`${LANGUAGE_NAMES[lang]} translation failed — kept the English text. Review it before submitting.`, 8000);
    } else {
      delete appState.translationFailures[index][lang];
      toast.success(`${LANGUAGE_NAMES[lang]} translation generated`);
    }
  } catch (err) {
    console.error('[ATV] Generate lang error:', err);
    toast.error(`Could not translate to ${LANGUAGE_NAMES[lang]}. Please try again.`);
  } finally {
    _atvGeneratingLangs.delete(lang);
    renderArticleTranslateView();
  }
};

// Generate all 3 languages
document.getElementById('atvGenerateAllBtn')?.addEventListener('click', async () => {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const article = appState.newsArticles[index];
  if (!article) return;

  const btn = document.getElementById('atvGenerateAllBtn');
  btn.disabled = true;
  const originalHtml = btn.innerHTML;
  btn.innerHTML = '<div class="spinner inline-block mr-2"></div>Generating...';
  toast.info('Generating all translations...');

  try {
    const results = await Promise.all(
      TRANSLATE_LANGS.map(lang =>
        translateArticle(article.title, article.description || '', article.source, lang)
          .then(r => ({ lang, r }))
          .catch(e => { console.error(`[ATV] ${lang} failed:`, e); return null; })
      )
    );
    if (!appState.translations[index]) appState.translations[index] = {};
    if (!appState.translationFailures[index]) appState.translationFailures[index] = {};
    let success = 0;
    let fellBack = 0;
    results.forEach(item => {
      if (!item) return;
      appState.translations[index][item.lang] = {
        title: item.r.title,
        description: item.r.description,
        source: item.r.source
      };
      // A fallback fills the field but is not a translation.
      if (item.r.ok === false) {
        appState.translationFailures[index][item.lang] = item.r.error;
        fellBack++;
      } else {
        delete appState.translationFailures[index][item.lang];
        success++;
      }
    });
    renderArticleTranslateView();
    if (fellBack > 0) {
      toast.error(
        `${fellBack} of ${TRANSLATE_LANGS.length} translations failed and kept the English text. Review them before submitting.`,
        8000
      );
    } else {
      toast.success(`Generated ${success} of ${TRANSLATE_LANGS.length} translations`);
    }
  } catch (err) {
    console.error('[ATV] Generate all error:', err);
    toast.error('Failed to generate translations');
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalHtml;
  }
});

// Toggle edit mode for a language
window.atvToggleEdit = function(lang) {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const t = appState.translations[index] && appState.translations[index][lang];
  if (!t) return;
  t._editing = true;
  renderArticleTranslateView();
};

window.atvCancelEdit = function(lang) {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const t = appState.translations[index] && appState.translations[index][lang];
  if (!t) return;
  delete t._editing;
  renderArticleTranslateView();
};

window.atvSaveEdit = function(lang) {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const titleEl = document.getElementById(`atv-${lang}-title`);
  const descEl = document.getElementById(`atv-${lang}-description`);
  const sourceEl = document.getElementById(`atv-${lang}-source`);
  if (!titleEl || !sourceEl) return;
  const newTitle = titleEl.value.trim();
  const newSource = sourceEl.value.trim();
  if (!newTitle || !newSource) {
    toast.warning('Title and source cannot be empty');
    return;
  }
  appState.translations[index][lang] = {
    title: newTitle,
    description: descEl ? descEl.value.trim() : '',
    source: newSource
  };
  renderArticleTranslateView();
  toast.success(`${LANGUAGE_NAMES[lang]} translation saved`);
};

window.atvCopyOriginal = async function(field) {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const article = appState.newsArticles[index];
  const value = article[field === 'link' ? 'link' : field] || '';
  await handleCopyContent(value, field.charAt(0).toUpperCase() + field.slice(1));
};

window.atvCopyTranslation = async function(lang, field) {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const t = appState.translations[index] && appState.translations[index][lang];
  if (!t) return;
  await handleCopyContent(t[field] || '', `${LANGUAGE_NAMES[lang]} ${field}`);
};

// Save the current article (with its translations) to Firestore
document.getElementById('atvSaveArticleBtn')?.addEventListener('click', async () => {
  const index = _atvCurrentIndex;
  if (index === null) return;
  const article = appState.newsArticles[index];
  if (!article) return;
  if (!db) {
    toast.error('Database not available');
    return;
  }

  const status = getArticleTranslationStatus(index);
  if (status.doneCount < TRANSLATE_LANGS.length) {
    const confirmed = await showConfirmModal({
      title: 'Save with incomplete translations?',
      message: `Only ${status.doneCount}/${TRANSLATE_LANGS.length} languages translated. Save anyway?`,
      confirmText: 'Save Anyway',
      variant: 'warning'
    });
    if (!confirmed) return;
  }

  const btn = document.getElementById('atvSaveArticleBtn');
  btn.disabled = true;
  const originalHtml = btn.innerHTML;
  btn.innerHTML = '<span class="flex items-center justify-center gap-2"><div class="spinner inline-block"></div>Saving...</span>';

  try {
    const dateString = appState.selectedDate;
    // Build news array from all articles in appState (preserving DB state for untouched ones)
    const newsData = appState.newsArticles.map((a, i) => {
      const base = a._dbData ? { ...a._dbData } : {};
      const translations = { ...(base.translations || {}) };
      // Overlay any in-memory translations
      if (appState.translations[i]) {
        TRANSLATE_LANGS.forEach(lang => {
          const t = appState.translations[i][lang];
          if (t && t.title && t.source) {
            translations[lang] = {
              title: t.title,
              description: t.description || '',
              source: t.source
            };
          }
        });
      }
      return {
        ...base,
        title: a.title,
        description: a.description || '',
        source: a.source,
        url: a.link || '',
        translations
      };
    });

    const submissionData = {
      articleCount: newsData.length,
      date: dateString,
      fetchedAt: serverTimestamp(),
      news: newsData
    };

    await setDoc(doc(db, 'news_snapshots', dateString), submissionData);

    // Mirror to morning_ready/{date}.news
    const morningReadyRef = doc(db, 'morning_ready', dateString);
    const morningSnap = await getDoc(morningReadyRef);
    if (morningSnap.exists()) {
      await updateDoc(morningReadyRef, { news: newsData });
    } else {
      await setDoc(morningReadyRef, { date: dateString, news: newsData }, { merge: true });
    }

    // Update local _dbData to reflect saved state
    appState.newsArticles.forEach((a, i) => {
      a._dbData = { ...newsData[i] };
    });
    appState.hasExistingData = true;

    markArticlesSaved();
    toast.success(`Article #${index + 1} saved to database`);
  } catch (err) {
    console.error('[ATV] Save error:', err);
    toast.error('Failed to save article to database');
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalHtml;
  }
});

// Back to articles list
document.getElementById('backToArticlesList')?.addEventListener('click', () => {
  _atvCurrentIndex = null;
  updateArticlesList();
  showView('dataEntryView');
});

// Step navigation: Previous / Next
document.getElementById('atvPrevStepBtn')?.addEventListener('click', () => {
  if (_atvStep > 0) {
    _atvStep -= 1;
    renderArticleTranslateView();
  }
});

document.getElementById('atvNextStepBtn')?.addEventListener('click', () => {
  if (_atvStep < TRANSLATE_LANGS.length - 1) {
    _atvStep += 1;
    renderArticleTranslateView();
  }
});

// Sign out from article translate view
document.getElementById('signOutArticleTranslateView')?.addEventListener('click', signOut);
